import { useState, useEffect, useRef } from "react";
import MapPin from "./MapPin";
import TrailMap from "./TrailMap";
import useLiveTracking from "./useLiveTracking";
import cleanupOldTracks from "./trackCleanup";
import { initializeApp, getApps } from "firebase/app";
import { getAuth, signInWithEmailAndPassword, signInWithPopup, GoogleAuthProvider, signOut, onAuthStateChanged, sendPasswordResetEmail, createUserWithEmailAndPassword } from "firebase/auth";
import { getFirestore, collection, getDocs, getDoc, query, where, doc, setDoc, updateDoc, addDoc, deleteDoc, orderBy, Timestamp } from "firebase/firestore";
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { fetchAppointments } from "./appointments";
import { fetchMembership, membershipFor, fetchTips } from "./square";
import { GUIDE_INTRO, DECISION_INTERIOR, DECISION_EXTERIOR, CLEAN_VS_DRESS, ORDER_INTERIOR, ORDER_EXTERIOR, PRODUCTS, FAQS, CHEAT_SHEET } from "./productGuide";
import { areasForService } from "./checklistTemplate";


// ─── FIREBASE CONFIG ──────────────────────────────────────────────────────────
const firebaseConfig = {
  apiKey: "AIzaSyBO0-fJbtS8Thxm-rvkxBWbOG6kyhCx04A",
  authDomain: "mint-time.firebaseapp.com",
  projectId: "mint-time",
  storageBucket: "mint-time.firebasestorage.app",
  messagingSenderId: "327689736011",
  appId: "1:327689736011:web:3b99151ee26532a4fc62ce",
  measurementId: "G-RDKP940W3X"
};

const firebaseApp = initializeApp(firebaseConfig);
const auth = getAuth(firebaseApp);
const db = getFirestore(firebaseApp);
const storage = getStorage(firebaseApp);
const googleProvider = new GoogleAuthProvider();

// Secondary Firebase app used only for admin-creates-user flows. This keeps
// the admin's session intact — createUserWithEmailAndPassword on the primary
// auth would auto-sign-in the new user and bump the admin.
const getSecondaryAuth = () => {
  const existing = getApps().find(a => a.name === "secondary");
  const secondaryApp = existing || initializeApp(firebaseConfig, "secondary");
  return getAuth(secondaryApp);
};


const MINT_LOGO = "data:image/svg+xml;base64,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";

// ─── GOOGLE MAPS CONFIG ──────────────────────────────────────────────────────
const GOOGLE_MAPS_API_KEY = "AIzaSyBIBvZPPT7MjD-UCGGUJdgPGNOTdAZmatQ";

const googleGeocode = async (lat, lng) => {
  try {
    const res = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_MAPS_API_KEY}`
    );
    const data = await res.json();
    if (data.status === "OK" && data.results.length > 0) {
      // Prefer street_address or premise result type for most specific address
      const best = data.results.find(r =>
        r.types.includes("street_address") ||
        r.types.includes("premise") ||
        r.types.includes("subpremise")
      ) || data.results[0];
      return best.formatted_address;
    }
  } catch (e) { console.error("Google geocode failed:", e); }
  return `GPS ${lat.toFixed(5)}, ${lng.toFixed(5)}`;
};

// ─── MOCK DATA ────────────────────────────────────────────────────────────────
const MOCK_USERS = [
  { id: "1", name: "Chris Keller", email: "chris@mintmobile.com", role: "admin", pin: "1234", qbEmployeeId: "" },
  { id: "2", name: "Levi Green", email: "levi@mintmobile.com", role: "manager", pin: "5678", qbEmployeeId: "" },
  { id: "3", name: "Jake Torres", email: "jake@mintmobile.com", role: "employee", pin: "1111", qbEmployeeId: "QB-EMP-001" },
  { id: "4", name: "Marcus Webb", email: "marcus@mintmobile.com", role: "employee", pin: "2222", qbEmployeeId: "QB-EMP-002" },
  { id: "5", name: "Tyler Ross", email: "tyler@mintmobile.com", role: "employee", pin: "3333", qbEmployeeId: "QB-EMP-003" },
];

const MOCK_ENTRIES = [];

// ─── HELPERS ─────────────────────────────────────────────────────────────────
const fmt = (d) => d ? new Date(d).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—";
const fmtDate = (d) => d ? new Date(d).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" }) : "—";
const fmtDateISO = (d) => d ? new Date(d).toISOString().split("T")[0] : "";
const fmtUSD = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const hoursLabel = (entry) => {
  if (!entry.clockOut) {
    const mins = Math.floor((Date.now() - new Date(entry.clockIn)) / 60000);
    return `${Math.floor(mins/60)}h ${mins%60}m (active)`;
  }
  return `${entry.hours?.toFixed(2)}h`;
};
const getInitials = (name) => name.split(" ").map(n => n[0]).join("").toUpperCase();
const statusColor = { pending: "#F59E0B", approved: "#10B981", active: "#3B82F6", rejected: "#EF4444", synced: "#8B5CF6" };
// Shared "mint pop" chrome — gradient headers with glassy buttons, reused everywhere.
const PAGE_BG = "linear-gradient(180deg, #EAF7F1 0%, #F5F8F7 220px)";
// Canonical customer-facing domain — used for every share link we generate so
// the URL sent to the customer / GHL is always mint-time.mintdetail.com, never
// the raw Vercel URL, regardless of which domain staff opened the app on.
const APP_BASE_URL = "https://mint-time.mintdetail.com";
const headerBar = { background: "#FFFFFF", padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "2px solid #64C5A6", boxShadow: "0 2px 12px rgba(15,23,42,0.06)" };
const headerBarSticky = { ...headerBar, position: "sticky", top: 0, zIndex: 10 };
const headerTitle = { fontFamily: "'Space Grotesk', sans-serif", fontSize: 18, fontWeight: 700, color: "#0F172A", letterSpacing: -0.3 };
const glassBtn = { background: "#F1F5F9", border: "1px solid #CBD5E1", color: "#334155", borderRadius: 9, padding: "6px 12px", cursor: "pointer", fontSize: 12, fontWeight: 700, fontFamily: "inherit" };
// Open turn-by-turn directions in the device's default maps app. Prefers exact
// coordinates when the appointment has them, else falls back to the address text.
const mapsUrl = (a) => {
  const dest = a && a.lat != null && a.lng != null ? `${a.lat},${a.lng}` : (a?.address || a?.customer || "");
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dest)}`;
};
const dialNumber = (phone) => (phone || "").replace(/[^\d+]/g, "");
// Local calendar date (YYYY-MM-DD) — NOT UTC. Using toISOString() would roll the
// day over early each evening in Central time and fetch the wrong day's schedule.
const localDateISO = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
// "Wed, Jul 1" from a YYYY-MM-DD string (parsed as local, not UTC).
const fmtApptDate = (dateISO) => dateISO ? new Date(dateISO + "T00:00:00").toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" }) : "";

// Upload a checklist photo to Firebase Storage; returns { url, path }.
// The frame is already downscaled at capture time (see CameraCapture.snap), so
// no second canvas/bitmap pass here — that double-processing was OOM-crashing
// lower-memory phones.
async function uploadChecklistPhoto(appointmentId, areaKey, phase, blob) {
  const path = `checklists/${appointmentId}/${areaKey}/${phase}/${Date.now()}_${Math.random().toString(36).slice(2, 7)}.jpg`;
  const r = storageRef(storage, path);
  await uploadBytes(r, blob, { contentType: "image/jpeg" });
  const url = await getDownloadURL(r);
  return { url, path };
}

// ─── PHOTO DIAGNOSTICS ────────────────────────────────────────────────────────
// Fire-and-forget logging for the checklist photo lifecycle. One doc per event
// in `photoDebugLogs` so we can see exactly where a photo dies for a given
// user/device (weak signal, low memory, left mid-upload, accidental remove).
// Never blocks the UI and never throws. Flip PHOTO_DEBUG off once diagnosed.
// To read: Firestore console → photoDebugLogs, filter user == "<name>",
// sort by `at`, and follow one appointmentId's timeline.
const PHOTO_DEBUG = true;

function deviceCtx() {
  try {
    const nav = typeof navigator !== "undefined" ? navigator : {};
    const conn = nav.connection || nav.mozConnection || nav.webkitConnection || null;
    let standalone = null;
    try {
      standalone = (typeof window !== "undefined" && window.matchMedia
        ? window.matchMedia("(display-mode: standalone)").matches : false) || !!nav.standalone;
    } catch {}
    return {
      online: nav.onLine ?? null,
      netType: conn?.effectiveType ?? null,   // '4g' | '3g' | 'slow-2g' …
      downlinkMbps: conn?.downlink ?? null,
      deviceMemoryGb: nav.deviceMemory ?? null,
      standalone,                             // opened from a home-screen icon?
      ua: nav.userAgent || "",
    };
  } catch { return {}; }
}

async function logPhoto(user, appt, event, extra = {}) {
  if (!PHOTO_DEBUG) return;
  try {
    await addDoc(collection(db, "photoDebugLogs"), {
      at: Timestamp.now(),
      event,
      user: user?.name || user?.email || user?.id || "unknown",
      role: user?.role || "",
      appointmentId: appt?.id || "",
      customer: appt?.customer || "",
      ...deviceCtx(),
      ...extra,
    });
  } catch { /* diagnostics must never break the photo flow */ }
}

// ─── CHECKLIST APPROVAL → SHARE + GHL ─────────────────────────────────────────
// Roles allowed to approve a vehicle's photos as OK-to-send-to-customer.
const APPROVER_ROLES = ["lead", "manager", "admin"];

// Build the public before/after gallery doc for a completed checklist and return
// its shareable URL (the same /?share=<id> page the admin "Copy link" uses).
async function createChecklistShare({ appointmentId, service, customer, date, areasMap }) {
  const areas = areasForService(service).map(area => ({
    label: area.label,
    before: (areasMap?.[area.key]?.before || []).map(p => p.url),
    after: (areasMap?.[area.key]?.after || []).map(p => p.url),
  })).filter(a => a.before.length || a.after.length);
  const sid = `${appointmentId}-${Math.random().toString(36).slice(2, 8)}`;
  await setDoc(doc(db, "shares", sid), {
    vehicle: customer || "Your vehicle", service: service || "", date: date || "",
    areas, createdAt: Timestamp.now(),
  });
  return { sid, url: `${APP_BASE_URL}/?share=${sid}` };
}

// Push an approved checklist's photo link into GoHighLevel via the serverless
// proxy: a permanent note (history across repeat visits) + the latest-link
// custom field. Throws on failure so the caller can show a Retry.
async function sendChecklistToGhl(ghl, { name, email, phone, address, service, date, shareUrl, crew }) {
  if (!ghl?.enabled) return { skipped: true };
  if (!ghl?.token || !ghl?.locationId || !ghl?.fieldId) throw new Error("GHL isn't fully configured — check Settings.");
  const res = await fetch("/api/ghl-attach", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      token: ghl.token, locationId: ghl.locationId, fieldId: ghl.fieldId,
      name, email, phone, address, service, date, shareUrl, crew,
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `GHL failed (${res.status})`);
  return data;
}

// Roles that clock in and can be put on a daily crew. Admin/manager run the back office.
const FIELD_ROLES = ["employee", "lead", "apprentice"];
const isManagerRole = (role) => role === "admin" || role === "manager";

// A crew's helpers are apprentices (or extra leads) attached to a lead. Each
// helper covers either the whole day (apptIds empty/absent) or a specific set of
// time slots (apptIds = those appointment ids). This is how a day gets split —
// e.g. one apprentice on the 9am, another on the 12 & 3.
// helpersOnAppt: who (helper ids) was on a given appointment in a crew.
function helpersOnAppt(crew, apptId) {
  if (crew.helpers) return crew.helpers.filter(h => !h.apptIds || h.apptIds.length === 0 || h.apptIds.includes(apptId)).map(h => h.id);
  return crew.apprenticeIds || []; // legacy shape: whole-day apprentices
}
// apptIdsForUser: the appointment ids a user is on for a day — as a crew lead
// (all the crew's appts) or as a helper (their time-slot coverage). Handles the
// legacy apprenticeIds shape too.
function apptIdsForUser(crews, userId) {
  const ids = [];
  (crews || []).forEach(c => {
    if (c.leadId === userId) { ids.push(...(c.appointmentIds || [])); return; }
    const h = (c.helpers || []).find(x => x.id === userId);
    if (h) { ids.push(...((h.apptIds && h.apptIds.length) ? h.apptIds : (c.appointmentIds || []))); }
    else if (!c.helpers && (c.apprenticeIds || []).includes(userId)) { ids.push(...(c.appointmentIds || [])); }
  });
  return [...new Set(ids)];
}

// ─── SHARED COMPONENTS ───────────────────────────────────────────────────────
function Avatar({ name, size = 36, color = "#E5E7EB" }) {
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", background: color, display: "flex", alignItems: "center", justifyContent: "center", color: "#0F172A", fontWeight: 700, fontSize: size * 0.35, fontFamily: "inherit", flexShrink: 0 }}>
      {getInitials(name)}
    </div>
  );
}

function Badge({ status }) {
  const labels = { pending: "Pending", approved: "Approved", active: "Clocked In", rejected: "Rejected", synced: "QB Synced" };
  const s = status === "approved" ? "approved" : status;
  return (
    <span style={{ background: statusColor[s] + "22", color: statusColor[s], border: `1px solid ${statusColor[s]}44`, borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase" }}>
      {labels[s] || s}
    </span>
  );
}

// Responsive helper — true on phone-width screens (< breakpoint px).
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const handler = (e) => setIsMobile(e.matches);
    setIsMobile(mq.matches);
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, [breakpoint]);
  return isMobile;
}

// Fixed bottom tab bar — phones only. Each item: { key, label, icon, onClick,
// active?, color?, badge?, primary? }. The `primary` item renders as a raised
// center action button (e.g. Add).
function BottomNav({ items }) {
  const list = items.filter(Boolean);
  return (
    <nav style={{ position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 600,
      background: "rgba(255,255,255,0.94)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
      borderTop: "1px solid #E5E7EB", paddingBottom: "env(safe-area-inset-bottom, 0px)",
      boxShadow: "0 -2px 16px rgba(15,23,42,0.06)", fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ maxWidth: 560, margin: "0 auto", display: "flex", alignItems: "flex-end", justifyContent: "space-around" }}>
        {list.map(it => {
          const accent = it.color || "#4A9E88";
          if (it.primary) {
            return (
              <button key={it.key} onClick={it.onClick} aria-label={it.label}
                style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, background: "none", border: "none", cursor: "pointer", padding: "8px 0 6px", fontFamily: "inherit" }}>
                <span style={{ width: 46, height: 46, marginTop: -20, borderRadius: "50%", background: "linear-gradient(135deg,#70C0A8,#4A9E88)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, fontWeight: 700, boxShadow: "0 6px 16px rgba(74,158,136,0.45)" }}>{it.icon}</span>
                <span style={{ fontSize: 10, fontWeight: 700, color: "#4A9E88" }}>{it.label}</span>
              </button>
            );
          }
          return (
            <button key={it.key} onClick={it.onClick} aria-label={it.label}
              style={{ flex: 1, position: "relative", display: "flex", flexDirection: "column", alignItems: "center", gap: 3, background: "none", border: "none", cursor: "pointer", padding: "9px 0 8px", fontFamily: "inherit" }}>
              <span style={{ fontSize: 19, lineHeight: 1, opacity: it.active ? 1 : 0.5 }}>{it.icon}</span>
              <span style={{ fontSize: 10.5, fontWeight: it.active ? 700 : 600, color: it.active ? accent : "#94A3B8" }}>{it.label}</span>
              {it.badge ? <span style={{ position: "absolute", top: 4, left: "calc(50% + 6px)", minWidth: 16, height: 16, padding: "0 4px", borderRadius: 8, background: "#EF4444", color: "#fff", fontSize: 9, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", boxSizing: "border-box" }}>{it.badge}</span> : null}
            </button>
          );
        })}
      </div>
    </nav>
  );
}

function Toast({ toast }) {
  if (!toast) return null;
  return (
    <div style={{ position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)", background: toast.color, color: "#fff", padding: "12px 24px", borderRadius: 12, fontWeight: 700, fontSize: 14, zIndex: 9999, boxShadow: "0 4px 20px rgba(0,0,0,0.6)", fontFamily: "'DM Sans', sans-serif", whiteSpace: "nowrap" }}>
      {toast.msg}
    </div>
  );
}

// ─── GPS CONSENT MODAL ───────────────────────────────────────────────────────
function GPSConsentModal({ userName, onAgree, onDecline, busy }) {
  const [checked, setChecked] = useState(false);
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ background: "#fff", borderRadius: 20, padding: 24, maxWidth: 420, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}>
        <div style={{ display: "inline-block", background: "#EF444415", color: "#EF4444", border: "1px solid #EF444433", borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 10 }}>Required to clock in</div>
        <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 22, fontWeight: 700, color: "#0F172A", marginBottom: 8 }}>GPS Tracking</div>
        <div style={{ color: "#475569", fontSize: 14, lineHeight: 1.55, marginBottom: 14 }}>
          Hey {userName?.split(" ")[0] || "there"} — to clock in with Mint Time, you must consent to location tracking. While you're on the clock we record your location so we can see job sites, route history, and breaks.
        </div>
        <ul style={{ color: "#475569", fontSize: 13, lineHeight: 1.7, paddingLeft: 18, margin: "0 0 14px 0" }}>
          <li>Tracking <b>only runs while you're clocked in</b>.</li>
          <li>It <b>stops the moment you clock out</b>.</li>
          <li>Trail history is kept for 45 days, then automatically deleted.</li>
        </ul>
        <label style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: 12, borderRadius: 10, background: "#F7F8FA", border: "1px solid #E5E7EB", cursor: "pointer", marginBottom: 14 }}>
          <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} style={{ marginTop: 3 }} />
          <span style={{ color: "#334155", fontSize: 13 }}>I consent to GPS tracking while I am clocked in.</span>
        </label>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={onDecline} disabled={busy}
            style={{ flex: 1, padding: "12px", borderRadius: 10, background: "#fff", border: "1px solid #E5E7EB", color: "#64748B", fontWeight: 600, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
            Cancel
          </button>
          <button onClick={onAgree} disabled={!checked || busy}
            style={{ flex: 2, padding: "12px", borderRadius: 10, background: checked ? "linear-gradient(135deg, #10B981, #059669)" : "#CBD5E1", border: "none", color: "#fff", fontWeight: 700, fontSize: 14, cursor: checked && !busy ? "pointer" : "default", fontFamily: "inherit", opacity: busy ? 0.7 : 1 }}>
            {busy ? "Saving…" : "I Agree & Clock In"}
          </button>
        </div>
        <div style={{ color: "#64748B", fontSize: 11, textAlign: "center", marginTop: 12 }}>
          Cancel without consenting and you won't be able to clock in. Talk to your manager if you have questions.
        </div>
      </div>
    </div>
  );
}

// ─── LOGIN SCREEN (Firebase Auth) ────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [showPass, setShowPass] = useState(false);

  const handleEmailLogin = async () => {
    if (!email || !password) { setError("Please enter your email and password."); return; }
    setLoading(true); setError("");
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      // Look up user profile in Firestore by email
      const q = query(collection(db, "users"), where("email", "==", cred.user.email));
      const snap = await getDocs(q);
      if (snap.empty) { await signOut(auth); setError("Your account exists but hasn't been set up in Mint Time yet. Contact your admin."); setLoading(false); return; }
      const profile = { id: snap.docs[0].id, ...snap.docs[0].data() };
      if (profile.active === false) { await signOut(auth); setError("Your account has been deactivated. Contact your admin."); setLoading(false); return; }
      onLogin(profile);
    } catch (e) {
      const msgs = { "auth/invalid-credential": "Incorrect email or password.", "auth/user-not-found": "No account found with that email.", "auth/wrong-password": "Incorrect password.", "auth/too-many-requests": "Too many attempts. Try again later." };
      setError(msgs[e.code] || "Sign in failed. Please try again.");
    }
    setLoading(false);
  };

  const handleGoogleLogin = async () => {
    setLoading(true); setError("");
    try {
      const cred = await signInWithPopup(auth, googleProvider);
      const q = query(collection(db, "users"), where("email", "==", cred.user.email));
      const snap = await getDocs(q);
      if (snap.empty) { await signOut(auth); setError("Your Google account hasn't been added to Mint Time yet. Contact your admin."); setLoading(false); return; }
      const profile = { id: snap.docs[0].id, ...snap.docs[0].data() };
      if (profile.active === false) { await signOut(auth); setError("Your account has been deactivated. Contact your admin."); setLoading(false); return; }
      onLogin(profile);
    } catch (e) {
      if (e.code !== "auth/popup-closed-by-user") setError("Google sign in failed. Please try again.");
    }
    setLoading(false);
  };

  const handleReset = async () => {
    if (!email) { setError("Enter your email address above first."); return; }
    setLoading(true); setError("");
    try {
      await sendPasswordResetEmail(auth, email);
      setResetSent(true);
    } catch (e) {
      setError("Could not send reset email. Check the address and try again.");
    }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: "100vh", background: "#F7F8FA", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "'DM Sans', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Space+Grotesk:wght@700&display=swap" rel="stylesheet" />

      <div style={{ marginBottom: 32, textAlign: "center" }}>
        <img src={MINT_LOGO} alt="Mint Mobile Detail" style={{ width: 80, height: 80, borderRadius: "50%", margin: "0 auto 16px", display: "block", boxShadow: "0 0 40px #70C0A844" }} />
        <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 24, fontWeight: 700, color: "#0F172A", letterSpacing: -0.5 }}>Mint Time</div>
        <div style={{ color: "#64748B", fontSize: 13, marginTop: 4 }}>Mint Mobile Detail</div>
      </div>

      <div style={{ width: "100%", maxWidth: 380, background: "#FFFFFF", borderRadius: 20, padding: 28, border: "1px solid #E5E7EB" }}>

        {resetSent ? (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📧</div>
            <div style={{ color: "#70C0A8", fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Check your email</div>
            <div style={{ color: "#64748B", fontSize: 13, marginBottom: 20 }}>We sent a password reset link to {email}</div>
            <button onClick={() => setResetSent(false)} style={{ width: "100%", padding: "12px", borderRadius: 12, background: "#F7F8FA", border: "1px solid #E5E7EB", color: "#334155", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>Back to Sign In</button>
          </div>
        ) : (
          <>
            {/* Google button */}
            <button onClick={handleGoogleLogin} disabled={loading}
              style={{ width: "100%", padding: "13px", borderRadius: 12, background: "#fff", border: "none", color: "#1F2937", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 16, opacity: loading ? 0.7 : 1 }}>
              <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
              Continue with Google
            </button>

            {/* Divider */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
              <div style={{ flex: 1, height: 1, background: "#E5E7EB" }} />
              <div style={{ color: "#64748B", fontSize: 12 }}>or sign in with email</div>
              <div style={{ flex: 1, height: 1, background: "#E5E7EB" }} />
            </div>

            {/* Email */}
            <div style={{ marginBottom: 12 }}>
              <input type="email" value={email} onChange={e => { setEmail(e.target.value); setError(""); }} placeholder="Email address"
                style={{ width: "100%", padding: "12px 14px", borderRadius: 12, background: "#F7F8FA", border: "1px solid #E5E7EB", color: "#0F172A", fontSize: 14, fontFamily: "inherit", boxSizing: "border-box" }}
                onKeyDown={e => e.key === "Enter" && handleEmailLogin()} />
            </div>

            {/* Password */}
            <div style={{ marginBottom: 6, position: "relative" }}>
              <input type={showPass ? "text" : "password"} value={password} onChange={e => { setPassword(e.target.value); setError(""); }} placeholder="Password"
                style={{ width: "100%", padding: "12px 44px 12px 14px", borderRadius: 12, background: "#F7F8FA", border: "1px solid #E5E7EB", color: "#0F172A", fontSize: 14, fontFamily: "inherit", boxSizing: "border-box" }}
                onKeyDown={e => e.key === "Enter" && handleEmailLogin()} />
              <button onClick={() => setShowPass(s => !s)} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#64748B", cursor: "pointer", fontSize: 16 }}>
                {showPass ? "🙈" : "👁"}
              </button>
            </div>

            {/* Forgot password */}
            <div style={{ textAlign: "right", marginBottom: 16 }}>
              <button onClick={handleReset} style={{ background: "none", border: "none", color: "#64748B", fontSize: 12, cursor: "pointer", fontFamily: "inherit", textDecoration: "underline" }}>
                Forgot password?
              </button>
            </div>

            {error && <div style={{ color: "#EF4444", fontSize: 13, marginBottom: 12, padding: "10px 14px", background: "#EF444411", borderRadius: 10, border: "1px solid #EF444433" }}>{error}</div>}

            <button onClick={handleEmailLogin} disabled={loading}
              style={{ width: "100%", padding: "14px", borderRadius: 12, background: "linear-gradient(135deg, #10B981, #059669)", border: "none", color: "#fff", fontWeight: 700, fontSize: 15, cursor: loading ? "default" : "pointer", fontFamily: "inherit", opacity: loading ? 0.7 : 1, transition: "all 0.2s" }}>
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </>
        )}
      </div>

      <div style={{ color: "#CBD5E1", fontSize: 11, marginTop: 20, textAlign: "center" }}>
        Contact your admin if you need access or have forgotten your password.
      </div>
    </div>
  );
}


// ─── ADD ENTRY MODAL (manager-only manual entry) ─────────────────────────────
function AddEntryModal({ allUsers, currentUser, onSave, onClose }) {
  const today = localDateISO();
  const employees = allUsers.filter(u => u.active !== false && FIELD_ROLES.includes(u.role));
  const [userId, setUserId] = useState(employees[0]?.id || "");
  const [date, setDate] = useState(today);
  const [clockInTime, setClockInTime] = useState("08:00");
  const [clockOutTime, setClockOutTime] = useState("17:00");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setError("");
    if (!userId) return setError("Pick an employee.");
    if (!date) return setError("Pick a date.");
    if (!clockInTime || !clockOutTime) return setError("Enter both clock in and clock out times.");
    if (!reason.trim()) return setError("Reason is required (e.g., 'Forgot to clock in').");

    const inDate = new Date(`${date}T${clockInTime}`);
    const outDate = new Date(`${date}T${clockOutTime}`);
    if (outDate <= inDate) return setError("Clock out must be after clock in.");
    const hours = Math.round((outDate - inDate) / 360000) / 10;

    const employee = employees.find(u => u.id === userId);
    const newEntry = {
      userId,
      userName: employee?.name || "Unknown",
      clockIn: Timestamp.fromDate(inDate),
      clockOut: Timestamp.fromDate(outDate),
      locationIn: null,
      locationOut: null,
      status: "pending",
      hours,
      qbSynced: false,
      manuallyCreated: true,
      createdReason: reason.trim(),
      createdByManagerId: currentUser?.id || null,
      createdByManagerName: currentUser?.name || null,
      createdAt: Timestamp.now(),
    };

    setSaving(true);
    try {
      await onSave(newEntry, { inDate, outDate });
      onClose();
    } catch (e) {
      console.error("Manual entry save failed:", e);
      setError("Save failed. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ background: "#fff", borderRadius: 20, padding: 24, maxWidth: 460, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div>
            <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 20, fontWeight: 700, color: "#0F172A" }}>Add Time Entry</div>
            <div style={{ color: "#64748B", fontSize: 12, marginTop: 2 }}>For an employee who forgot to clock in/out</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "1px solid #E5E7EB", color: "#64748B", borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontSize: 12, fontFamily: "inherit" }}>✕</button>
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={{ color: "#64748B", fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 6 }}>Employee</div>
          <select value={userId} onChange={e => setUserId(e.target.value)}
            style={{ width: "100%", padding: "10px 12px", borderRadius: 10, background: "#F7F8FA", border: "1px solid #E5E7EB", color: "#0F172A", fontSize: 14, fontFamily: "inherit", boxSizing: "border-box" }}>
            {employees.length === 0 && <option value="">No active employees</option>}
            {employees.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={{ color: "#64748B", fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 6 }}>Date</div>
          <input type="date" value={date} onChange={e => setDate(e.target.value)}
            style={{ width: "100%", padding: "10px 12px", borderRadius: 10, background: "#F7F8FA", border: "1px solid #E5E7EB", color: "#0F172A", fontSize: 14, fontFamily: "inherit", boxSizing: "border-box" }} />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
          <div>
            <div style={{ color: "#64748B", fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 6 }}>Clock In</div>
            <input type="time" value={clockInTime} onChange={e => setClockInTime(e.target.value)}
              style={{ width: "100%", padding: "10px 12px", borderRadius: 10, background: "#F7F8FA", border: "1px solid #E5E7EB", color: "#0F172A", fontSize: 14, fontFamily: "inherit", boxSizing: "border-box" }} />
          </div>
          <div>
            <div style={{ color: "#64748B", fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 6 }}>Clock Out</div>
            <input type="time" value={clockOutTime} onChange={e => setClockOutTime(e.target.value)}
              style={{ width: "100%", padding: "10px 12px", borderRadius: 10, background: "#F7F8FA", border: "1px solid #E5E7EB", color: "#0F172A", fontSize: 14, fontFamily: "inherit", boxSizing: "border-box" }} />
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={{ color: "#64748B", fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 6 }}>Reason <span style={{ color: "#EF4444" }}>*</span></div>
          <input type="text" value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g., Forgot to clock in"
            style={{ width: "100%", padding: "10px 12px", borderRadius: 10, background: "#F7F8FA", border: "1px solid #E5E7EB", color: "#0F172A", fontSize: 14, fontFamily: "inherit", boxSizing: "border-box" }} />
        </div>

        {error && <div style={{ color: "#EF4444", fontSize: 12, marginBottom: 10 }}>{error}</div>}

        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <button onClick={onClose} disabled={saving}
            style={{ flex: 1, padding: "12px", borderRadius: 10, background: "#fff", border: "1px solid #E5E7EB", color: "#64748B", fontWeight: 600, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving || !employees.length}
            style={{ flex: 2, padding: "12px", borderRadius: 10, background: "linear-gradient(135deg, #10B981, #059669)", border: "none", color: "#fff", fontWeight: 700, fontSize: 14, cursor: saving ? "default" : "pointer", fontFamily: "inherit", opacity: saving ? 0.7 : 1 }}>
            {saving ? "Saving…" : "Create Entry"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── TIME EDIT MODAL ──────────────────────────────────────────────────────────
function TimeEditModal({ entry, userRole, onSave, onClose }) {
  const toTimeInput = (d) => {
    if (!d) return "";
    const date = new Date(d);
    return `${String(date.getHours()).padStart(2,"0")}:${String(date.getMinutes()).padStart(2,"0")}`;
  };

  const [clockInTime, setClockInTime] = useState(toTimeInput(entry.clockIn));
  const [clockOutTime, setClockOutTime] = useState(toTimeInput(entry.clockOut));
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!reason.trim()) { setError("A reason is required to edit time entries."); return; }
    if (!clockInTime) { setError("Clock in time is required."); return; }

    // Build new dates using the same date as original but new times
    const baseDate = new Date(entry.clockIn);
    const [inH, inM] = clockInTime.split(":").map(Number);
    const newClockIn = new Date(baseDate);
    newClockIn.setHours(inH, inM, 0, 0);

    let newClockOut = null;
    if (clockOutTime) {
      const [outH, outM] = clockOutTime.split(":").map(Number);
      newClockOut = new Date(baseDate);
      newClockOut.setHours(outH, outM, 0, 0);
      // Handle overnight shift
      if (newClockOut < newClockIn) newClockOut.setDate(newClockOut.getDate() + 1);
    }

    if (newClockOut && newClockOut <= newClockIn) {
      setError("Clock out must be after clock in.");
      return;
    }

    const newHours = newClockOut
      ? Math.round((newClockOut - newClockIn) / 360000) / 10
      : null;

    const editRecord = {
      editedAt: new Date().toISOString(),
      editedBy: userRole,
      reason: reason.trim(),
      originalClockIn: entry.clockIn instanceof Date ? entry.clockIn.toISOString() : entry.clockIn,
      originalClockOut: entry.clockOut instanceof Date ? entry.clockOut?.toISOString() : entry.clockOut,
      originalHours: entry.hours,
    };

    setSaving(true);
    try {
      const updates = {
        clockIn: Timestamp.fromDate(newClockIn),
        clockOut: newClockOut ? Timestamp.fromDate(newClockOut) : null,
        hours: newHours,
        editedByEmployee: userRole === "employee" ? true : (entry.editedByEmployee || false),
        editedByManager: userRole !== "employee" ? true : (entry.editedByManager || false),
        editHistory: [...(entry.editHistory || []), editRecord],
        status: entry.status === "approved" ? "pending" : entry.status, // re-pend if was approved
      };
      await updateDoc(doc(db, "timeEntries", entry.id), updates);
      onSave({ ...entry, ...updates, clockIn: newClockIn, clockOut: newClockOut });
    } catch (e) {
      setError("Failed to save: " + e.message);
    }
    setSaving(false);
  };

  const isManager = userRole !== "employee";

  return (
    <div style={{ position: "fixed", inset: 0, background: "#0F172A80", zIndex: 800, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div style={{ background: "#FFFFFF", borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 480, padding: 24, border: "1px solid #E5E7EB", fontFamily: "'DM Sans', sans-serif" }}>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div>
            <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 18, fontWeight: 700, color: "#0F172A" }}>Edit Time Entry</div>
            <div style={{ color: "#64748B", fontSize: 12, marginTop: 2 }}>{entry.userName} · {fmtDate(entry.clockIn)}</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "1px solid #E5E7EB", color: "#64748B", borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontSize: 12, fontFamily: "inherit" }}>✕</button>
        </div>

        {/* Warning banner */}
        <div style={{ background: "#F59E0B11", border: "1px solid #F59E0B33", borderRadius: 12, padding: "10px 14px", marginBottom: 18 }}>
          <div style={{ color: "#F59E0B", fontSize: 12, fontWeight: 600 }}>
            ⚠ {isManager ? "Editing will re-flag this entry for approval if already approved." : "Your manager will be notified this entry was manually edited."}
          </div>
        </div>

        {/* Original times for reference */}
        <div style={{ background: "#F7F8FA", borderRadius: 10, padding: "10px 14px", marginBottom: 16, display: "flex", gap: 20 }}>
          <div>
            <div style={{ color: "#64748B", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>Original In</div>
            <div style={{ color: "#64748B", fontSize: 13 }}>{fmt(entry.clockIn)}</div>
          </div>
          <div>
            <div style={{ color: "#64748B", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>Original Out</div>
            <div style={{ color: "#64748B", fontSize: 13 }}>{fmt(entry.clockOut)}</div>
          </div>
          <div>
            <div style={{ color: "#64748B", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>Original Hours</div>
            <div style={{ color: "#64748B", fontSize: 13 }}>{entry.hours?.toFixed(2)}h</div>
          </div>
        </div>

        {/* New times */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
          <div>
            <div style={{ color: "#334155", fontSize: 12, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 6 }}>New Clock In</div>
            <input type="time" value={clockInTime} onChange={e => { setClockInTime(e.target.value); setError(""); }}
              style={{ width: "100%", padding: "10px 12px", borderRadius: 10, background: "#F7F8FA", border: "1px solid #E5E7EB", color: "#0F172A", fontSize: 15, fontFamily: "inherit", boxSizing: "border-box" }} />
          </div>
          <div>
            <div style={{ color: "#334155", fontSize: 12, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 6 }}>New Clock Out</div>
            <input type="time" value={clockOutTime} onChange={e => { setClockOutTime(e.target.value); setError(""); }}
              style={{ width: "100%", padding: "10px 12px", borderRadius: 10, background: "#F7F8FA", border: "1px solid #E5E7EB", color: "#0F172A", fontSize: 15, fontFamily: "inherit", boxSizing: "border-box" }} />
          </div>
        </div>

        {/* Calculated hours preview */}
        {clockInTime && clockOutTime && (
          <div style={{ background: "#70C0A811", border: "1px solid #70C0A833", borderRadius: 10, padding: "8px 14px", marginBottom: 16, textAlign: "center" }}>
            {(() => {
              const [inH, inM] = clockInTime.split(":").map(Number);
              const [outH, outM] = clockOutTime.split(":").map(Number);
              let mins = (outH * 60 + outM) - (inH * 60 + inM);
              if (mins < 0) mins += 1440;
              const h = Math.floor(mins / 60), m = mins % 60;
              return <span style={{ color: "#0D9488", fontWeight: 800, fontSize: 14 }}>New total: {h}h {m}m</span>;
            })()}
          </div>
        )}

        {/* Reason — required */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ color: "#334155", fontSize: 12, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 6 }}>
            Reason for Edit <span style={{ color: "#EF4444" }}>*</span>
          </div>
          <textarea
            value={reason}
            onChange={e => { setReason(e.target.value); setError(""); }}
            placeholder="e.g. Forgot to clock out, actually left at 5:00pm"
            rows={3}
            style={{ width: "100%", padding: "10px 14px", borderRadius: 10, background: "#F7F8FA", border: `1px solid ${error && !reason ? "#EF4444" : "#E5E7EB"}`, color: "#0F172A", fontSize: 13, fontFamily: "inherit", resize: "none", boxSizing: "border-box" }}
          />
          {error && <div style={{ color: "#EF4444", fontSize: 12, marginTop: 6 }}>{error}</div>}
        </div>

        <button onClick={handleSave} disabled={saving}
          style={{ width: "100%", padding: "14px", borderRadius: 12, background: saving ? "#E5E7EB" : "linear-gradient(135deg, #10B981, #059669)", border: "none", color: saving ? "#64748B" : "#fff", fontWeight: 700, fontSize: 15, cursor: saving ? "default" : "pointer", fontFamily: "inherit" }}>
          {saving ? "Saving..." : "Save Edit"}
        </button>
      </div>
    </div>
  );
}

// ─── MY TIME TREE ─────────────────────────────────────────────────────────────
// Collapsible Year → Month → Week hierarchy for the employee's own entries.
function MyTimeTree({ entries, onEdit }) {
  const getWeekStart = (d) => {
    const date = new Date(d);
    const day = date.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    const s = new Date(date); s.setDate(date.getDate() + diff); s.setHours(0,0,0,0);
    return s;
  };
  const weekKey = (d) => fmtDateISO(getWeekStart(d));
  const weekLabel = (d) => {
    const s = getWeekStart(d);
    const e = new Date(s); e.setDate(e.getDate() + 6);
    return `${s.toLocaleDateString([], { month: "short", day: "numeric" })} – ${e.toLocaleDateString([], { month: "short", day: "numeric" })}`;
  };
  const monthLabel = (d) => new Date(d).toLocaleDateString([], { month: "long" });

  const tree = {};
  entries.forEach(e => {
    const d = new Date(e.clockIn);
    const y = d.getFullYear(), m = d.getMonth(), wk = weekKey(d);
    if (!tree[y]) tree[y] = { total: 0, months: {} };
    if (!tree[y].months[m]) tree[y].months[m] = { total: 0, weeks: {}, label: monthLabel(d) };
    if (!tree[y].months[m].weeks[wk]) tree[y].months[m].weeks[wk] = { total: 0, entries: [], label: weekLabel(d) };
    const h = e.hours || 0;
    tree[y].total += h;
    tree[y].months[m].total += h;
    tree[y].months[m].weeks[wk].total += h;
    tree[y].months[m].weeks[wk].entries.push(e);
  });

  const now = new Date();
  const [expanded, setExpanded] = useState({
    [`y-${now.getFullYear()}`]: true,
    [`m-${now.getFullYear()}-${now.getMonth()}`]: true,
    [`w-${weekKey(now)}`]: true,
  });
  const toggle = (k) => setExpanded(s => ({ ...s, [k]: !s[k] }));

  const rowStyle = (depth) => ({
    display: "flex", justifyContent: "space-between", alignItems: "center",
    width: "100%", padding: "10px 14px", marginBottom: 6,
    borderRadius: 10, background: "#FFFFFF", border: "1px solid #E5E7EB",
    cursor: "pointer", fontFamily: "inherit", textAlign: "left",
    marginLeft: depth * 10,
  });

  const years = Object.keys(tree).sort((a,b) => b - a);

  return (
    <>
      {years.map(y => {
        const yKey = `y-${y}`;
        const yOpen = !!expanded[yKey];
        const months = Object.keys(tree[y].months).sort((a,b) => b - a);
        return (
          <div key={y} style={{ marginBottom: 6 }}>
            <button onClick={() => toggle(yKey)} style={rowStyle(0)}>
              <span style={{ color: "#0F172A", fontWeight: 700, fontSize: 14 }}>
                <span style={{ color: "#64748B", marginRight: 6 }}>{yOpen ? "▾" : "▸"}</span>{y}
              </span>
              <span style={{ color: "#70C0A8", fontWeight: 700, fontSize: 13, fontFamily: "'Space Grotesk', sans-serif" }}>{tree[y].total.toFixed(1)}h</span>
            </button>
            {yOpen && months.map(m => {
              const mKey = `m-${y}-${m}`;
              const mOpen = !!expanded[mKey];
              const weeks = Object.keys(tree[y].months[m].weeks).sort().reverse();
              return (
                <div key={m}>
                  <button onClick={() => toggle(mKey)} style={rowStyle(1)}>
                    <span style={{ color: "#334155", fontWeight: 600, fontSize: 13 }}>
                      <span style={{ color: "#64748B", marginRight: 6 }}>{mOpen ? "▾" : "▸"}</span>{tree[y].months[m].label}
                    </span>
                    <span style={{ color: "#334155", fontWeight: 600, fontSize: 12 }}>{tree[y].months[m].total.toFixed(1)}h</span>
                  </button>
                  {mOpen && weeks.map(wk => {
                    const wKey = `w-${wk}`;
                    const wOpen = !!expanded[wKey];
                    const wData = tree[y].months[m].weeks[wk];
                    return (
                      <div key={wk}>
                        <button onClick={() => toggle(wKey)} style={rowStyle(2)}>
                          <span style={{ color: "#64748B", fontWeight: 600, fontSize: 12 }}>
                            <span style={{ color: "#64748B", marginRight: 6 }}>{wOpen ? "▾" : "▸"}</span>{wData.label}
                          </span>
                          <span style={{ color: "#64748B", fontWeight: 600, fontSize: 12 }}>{wData.total.toFixed(1)}h · {wData.entries.length} {wData.entries.length === 1 ? "entry" : "entries"}</span>
                        </button>
                        {wOpen && wData.entries.map(e => (
                          <div key={e.id} style={{ marginLeft: 30, background: "#FFFFFF", borderRadius: 14, padding: "14px 16px", border: "1px solid #E5E7EB", marginBottom: 10 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                              <div style={{ color: "#0F172A", fontWeight: 600, fontSize: 14 }}>{fmtDate(e.clockIn)}</div>
                              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                                <Badge status={e.status} />
                                {e.qbSynced && <Badge status="synced" />}
                                {e.editedByEmployee && <span style={{ background: "#F59E0B22", color: "#F59E0B", border: "1px solid #F59E0B44", borderRadius: 20, padding: "2px 8px", fontSize: 10, fontWeight: 700 }}>⚠ EMP EDIT</span>}
                              </div>
                            </div>
                            <div style={{ color: "#64748B", fontSize: 12, display: "flex", gap: 16 }}>
                              <span>In: {fmt(e.clockIn)}</span>
                              <span>Out: {fmt(e.clockOut)}</span>
                              <span style={{ color: "#334155", fontWeight: 600 }}>{hoursLabel(e)}</span>
                            </div>
                            {e.locationIn && <div style={{ color: "#64748B", fontSize: 11, marginTop: 4 }}>📍 {e.locationIn.label}</div>}
                            {e.locationIn?.lat != null && (
                              <div style={{ marginTop: 8 }}>
                                <MapPin lat={e.locationIn.lat} lng={e.locationIn.lng} height={100} zoom={15} rounded={8} />
                              </div>
                            )}
                            {(e.status === "pending" || e.status === "active") && (
                              <button onClick={() => onEdit(e)}
                                style={{ marginTop: 8, width: "100%", padding: "7px", borderRadius: 8, background: "#F7F8FA", border: "1px solid #E5E7EB", color: "#64748B", fontSize: 12, cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}>
                                ✏ Edit Time
                              </button>
                            )}
                            {e.editedByEmployee && <div style={{ color: "#F59E0B", fontSize: 10, marginTop: 4, fontWeight: 600 }}>⚠ Manually edited</div>}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        );
      })}
    </>
  );
}

// ─── EMPLOYEE VIEW ────────────────────────────────────────────────────────────
function EmployeeView({ user, entries, appointments = [], onClock, onGrantConsent, locating, onLogout, onUpdateEntry, ghlConfig }) {
  const myEntries = entries.filter(e => e.userId === user.id).sort((a,b) => new Date(b.clockIn) - new Date(a.clockIn));
  const active = myEntries.find(e => e.status === "active");
  const [elapsed, setElapsed] = useState(0);
  const [loading, setLoading] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [showConsent, setShowConsent] = useState(false);
  const [savingConsent, setSavingConsent] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [checklistAppt, setChecklistAppt] = useState(null);
  const [membership, setMembership] = useState({}); // emailLower -> [{name,value}] from Square
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - new Date(active.clockIn)) / 1000)), 1000);
    return () => clearInterval(t);
  }, [active]);

  useEffect(() => {
    let cancelled = false;
    if (appointments.length) fetchMembership(appointments.map(a => a.email)).then(m => { if (!cancelled) setMembership(m); });
    return () => { cancelled = true; };
  }, [appointments]);

  const elapsedStr = () => {
    const h = Math.floor(elapsed / 3600), m = Math.floor((elapsed % 3600) / 60), s = elapsed % 60;
    return `${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}:${String(s).padStart(2,"0")}`;
  };

  const weekHours = myEntries.filter(e => {
    const d = new Date(e.clockIn), now = new Date();
    return d > new Date(now.setDate(now.getDate() - 7)) && e.clockOut;
  }).reduce((sum, e) => sum + (e.hours || 0), 0);

  const handleClock = () => {
    if (locating || loading) return;
    // Gate clock-in (but not clock-out) on GPS consent
    if (!active && !user.gpsConsent) {
      setShowConsent(true);
      return;
    }
    setLoading(true);
    onClock(user, active);
    setTimeout(() => setLoading(false), 3000);
  };

  const handleAgreeConsent = async () => {
    setSavingConsent(true);
    try {
      await onGrantConsent(user);
      setShowConsent(false);
      setLoading(true);
      onClock({ ...user, gpsConsent: true }, active);
      setTimeout(() => setLoading(false), 3000);
    } catch (e) {
      console.error("Consent save failed", e);
    } finally {
      setSavingConsent(false);
    }
  };


  return (
    <div style={{ minHeight: "100vh", background: PAGE_BG, fontFamily: "'DM Sans', sans-serif", paddingBottom: 32 }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Space+Grotesk:wght@700&display=swap" rel="stylesheet" />
      <div style={headerBar}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}><img src={MINT_LOGO} alt="Mint" style={{ width: 64, height: 64, display: "block", flexShrink: 0 }} /><div style={headerTitle}>Mint Time</div></div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Avatar name={user.name} size={32} />
          {!isMobile && <button onClick={onLogout} style={glassBtn}>Logout</button>}
        </div>
      </div>
      <div style={{ padding: "24px 20px", maxWidth: 480, margin: "0 auto", paddingBottom: isMobile ? "calc(96px + env(safe-area-inset-bottom, 0px))" : 24 }}>
        <div style={{ background: "linear-gradient(160deg, #FFFFFF 50%, #E6F6EF 100%)", borderRadius: 24, padding: 28, border: "1px solid #CDEBDF", boxShadow: "0 8px 24px rgba(74,158,136,0.10)", textAlign: "center", marginBottom: 20 }}>
          <div style={{ color: "#64748B", fontSize: 13, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 8 }}>
            {active ? "Currently Clocked In" : "Ready to Start?"}
          </div>
          {active && <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 42, fontWeight: 700, color: "#70C0A8", letterSpacing: -1, marginBottom: 4 }}>{elapsedStr()}</div>}
          {active && <div style={{ color: "#64748B", fontSize: 12, marginBottom: 12 }}>📍 {active.locationIn?.label} · {fmt(active.clockIn)}</div>}
          {active && active.locationIn?.lat != null && (
            <div style={{ marginBottom: 20 }}>
              <MapPin lat={active.locationIn.lat} lng={active.locationIn.lng} height={140} />
            </div>
          )}
          <button onClick={handleClock} disabled={loading || locating}
            style={{ width: 140, height: 140, borderRadius: "50%", background: active ? "linear-gradient(135deg, #EF4444, #B91C1C)" : "linear-gradient(135deg, #10B981, #059669)", border: "none", color: "#fff", fontSize: 18, fontWeight: 700, cursor: "pointer", boxShadow: active ? "0 0 40px #EF444440" : "0 0 40px #70C0A840", fontFamily: "'Space Grotesk', sans-serif", transition: "all 0.3s" }}>
            {locating ? "📍..." : loading ? "..." : active ? "CLOCK\nOUT" : "CLOCK\nIN"}
          </button>
          {!active && <div style={{ color: "#64748B", fontSize: 12, marginTop: 16 }}>{locating ? "Getting your address..." : "Full address captured automatically"}</div>}
        </div>

        <button onClick={() => setShowGuide(true)}
          style={{ width: "100%", marginBottom: 20, padding: "14px 16px", borderRadius: 16, background: "#FFFFFF", border: "1px solid #E5E7EB", color: "#0F172A", fontSize: 14, fontWeight: 700, fontFamily: "inherit", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          🧪 Product &amp; Mixing Guide
          <span style={{ color: "#64748B", fontWeight: 500, fontSize: 12 }}>— what to use & how to mix</span>
        </button>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 12, marginBottom: 20 }}>
          {[
            { label: "Hours This Week", value: weekHours.toFixed(1), color: "#0D9488", g: "linear-gradient(145deg, #2DD4BF, #0D9488)" },
            { label: "Pending", value: myEntries.filter(e => e.status === "pending").length, color: "#D97706", g: "linear-gradient(145deg, #FBBF24, #EA580C)" },
            { label: "QB Synced", value: myEntries.filter(e => e.qbSynced).length, color: "#7C3AED", g: "linear-gradient(145deg, #A78BFA, #7C3AED)" },
          ].map(s => (
            <div key={s.label} style={{ background: s.g, borderRadius: 16, padding: "16px 10px", textAlign: "center", boxShadow: `0 8px 18px ${s.color}44` }}>
              <div style={{ color: "#FFFFFF", fontFamily: "'Space Grotesk', sans-serif", fontSize: isMobile ? 23 : 28, fontWeight: 700 }}>{s.value}</div>
              <div style={{ color: "rgba(255,255,255,0.92)", fontSize: 11, marginTop: 2, fontWeight: 600 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {(user.role === "lead" || user.role === "apprentice") && (
          <div style={{ background: "#FFFFFF", borderRadius: 20, padding: 20, border: "1px solid #E5E7EB", marginBottom: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={{ color: "#0F172A", fontWeight: 700, fontSize: 15, fontFamily: "'Space Grotesk', sans-serif" }}>Today's Appointments</div>
              <span style={{ background: "#0EA5E918", color: "#0EA5E9", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700 }}>{appointments.length}</span>
            </div>
            {appointments.length === 0 && <div style={{ color: "#64748B", fontSize: 13, textAlign: "center", padding: "12px 0" }}>No appointments assigned yet for today.</div>}
            {[...appointments].sort((a, b) => (a.sortMins ?? 0) - (b.sortMins ?? 0)).map(a => (
              <div key={a.id} style={{ display: "flex", gap: 12, padding: "12px 0", borderBottom: "1px solid #F1F5F9" }}>
                <div style={{ width: 66, flexShrink: 0 }}>
                  <div style={{ color: "#64748B", fontSize: 10, fontWeight: 700, lineHeight: 1.2 }}>{fmtApptDate(a.date)}</div>
                  <div style={{ color: "#0EA5E9", fontWeight: 800, fontSize: 13, marginTop: 1 }}>{a.time}</div>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: "#0F172A", fontWeight: 700, fontSize: 14 }}>{a.customer}</div>
                  {membershipFor(membership, a.email).length > 0 && (
                    <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 3 }}>
                      {membershipFor(membership, a.email).map((m, i) => (
                        <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 3, background: "#F5F3FF", border: "1px solid #8B5CF655", color: "#6D28D9", borderRadius: 12, padding: "2px 9px", fontSize: 11, fontWeight: 700, lineHeight: 1.3 }}>⭐ {m.name}{m.value ? `: ${m.value}` : ""}</span>
                      ))}
                    </div>
                  )}
                  {a.service && <div style={{ color: "#475569", fontSize: 12, marginTop: 1 }}>{a.service}</div>}
                  {a.address && (
                    <a href={mapsUrl(a)} target="_blank" rel="noopener noreferrer"
                      style={{ display: "flex", gap: 4, alignItems: "flex-start", marginTop: 5, color: "#0D9488", fontSize: 13, fontWeight: 700, lineHeight: 1.35, textDecoration: "none" }}>
                      <span style={{ flexShrink: 0 }}>📍</span><span style={{ textDecoration: "underline" }}>{a.address}</span>
                    </a>
                  )}
                  {a.phone && (
                    <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                      <a href={`tel:${dialNumber(a.phone)}`}
                        style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "linear-gradient(135deg,#10B981,#059669)", color: "#fff", borderRadius: 9, padding: "7px 14px", fontSize: 12, fontWeight: 700, textDecoration: "none", boxShadow: "0 3px 8px rgba(5,150,105,0.3)" }}>📞 Call</a>
                      <a href={`sms:${dialNumber(a.phone)}`}
                        style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "linear-gradient(135deg,#3B82F6,#2563EB)", color: "#fff", borderRadius: 9, padding: "7px 14px", fontSize: 12, fontWeight: 700, textDecoration: "none", boxShadow: "0 3px 8px rgba(37,99,235,0.3)" }}>💬 Text</a>
                    </div>
                  )}
                  <button onClick={() => setChecklistAppt(a)}
                    style={{ marginTop: 12, width: "100%", padding: "10px", borderRadius: 10, background: "linear-gradient(135deg, #0EA5E9, #0369A1)", border: "none", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", boxShadow: "0 3px 10px rgba(3,105,161,0.3)" }}>
                    📋 Vehicle Checklist
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={{ color: "#64748B", fontSize: 12, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 12 }}>My Recent Time</div>
        {myEntries.length === 0 && <div style={{ color: "#64748B", textAlign: "center", padding: 24 }}>No time entries yet.</div>}
        <MyTimeTree entries={myEntries} onEdit={setEditEntry} />
      </div>
      {editEntry && (
        <TimeEditModal
          entry={editEntry}
          userRole="employee"
          onSave={(updated) => {
            onUpdateEntry(updated);
            setEditEntry(null);
          }}
          onClose={() => setEditEntry(null)}
        />
      )}
      {showConsent && (
        <GPSConsentModal
          userName={user.name}
          busy={savingConsent}
          onAgree={handleAgreeConsent}
          onDecline={() => setShowConsent(false)}
        />
      )}
      {showGuide && <ProductGuide onClose={() => setShowGuide(false)} />}
      {checklistAppt && <VehicleChecklist appt={checklistAppt} user={user} ghlConfig={ghlConfig} onClose={() => setChecklistAppt(null)} />}
      {isMobile && <BottomNav items={[
        { key: "home", label: "Home", icon: "🏠", active: true, onClick: () => window.scrollTo({ top: 0, behavior: "smooth" }) },
        { key: "guide", label: "Products", icon: "🧪", color: "#0EA5E9", onClick: () => setShowGuide(true) },
        { key: "logout", label: "Sign Out", icon: "🚪", color: "#EF4444", onClick: onLogout },
      ]} />}
    </div>
  );
}

// ─── QBO SETTINGS PANEL (admin only) ─────────────────────────────────────────
function QBOSettings({ allUsers, qbConfig, onSave, onClose }) {
  const [cfg, setCfg] = useState(qbConfig);
  const [users, setUsers] = useState(allUsers);
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    onSave(cfg, users);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const inp = (label, key, placeholder, hint) => (
    <div style={{ marginBottom: 16 }}>
      <div style={{ color: "#334155", fontSize: 12, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 6 }}>{label}</div>
      {hint && <div style={{ color: "#64748B", fontSize: 11, marginBottom: 6 }}>{hint}</div>}
      <input value={cfg[key] || ""} onChange={e => setCfg(p => ({ ...p, [key]: e.target.value }))} placeholder={placeholder}
        style={{ width: "100%", padding: "10px 14px", borderRadius: 10, background: "#F7F8FA", border: "1px solid #E5E7EB", color: "#0F172A", fontSize: 14, fontFamily: "inherit", boxSizing: "border-box" }} />
    </div>
  );

  return (
    <div style={{ position: "fixed", inset: 0, background: "#0F172A80", zIndex: 1000, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div style={{ background: "#FFFFFF", borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 560, maxHeight: "90vh", overflowY: "auto", padding: 24, border: "1px solid #E5E7EB" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <div>
            <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 18, fontWeight: 700, color: "#0F172A" }}>QuickBooks Settings</div>
            <div style={{ color: "#64748B", fontSize: 12, marginTop: 2 }}>Connect Mint Time to QuickBooks Online</div>
          </div>
          <button onClick={onClose} style={{ background: "#F7F8FA", border: "1px solid #E5E7EB", color: "#64748B", borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontSize: 12, fontFamily: "inherit" }}>✕ Close</button>
        </div>

        {/* Connection status */}
        <div style={{ background: cfg.connected ? "#F7F8FA" : "#FEF2F2", borderRadius: 12, padding: "12px 16px", border: `1px solid ${cfg.connected ? "#70C0A8" : "#EF4444"}33`, marginBottom: 20, display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 10, height: 10, borderRadius: "50%", background: cfg.connected ? "#70C0A8" : "#EF4444", boxShadow: cfg.connected ? "0 0 8px #70C0A8" : "none" }} />
          <div>
            <div style={{ color: cfg.connected ? "#70C0A8" : "#EF4444", fontWeight: 600, fontSize: 13 }}>{cfg.connected ? "Connected to QuickBooks Online" : "Not connected"}</div>
            {cfg.connected && <div style={{ color: "#64748B", fontSize: 11 }}>Company ID: {cfg.realmId} · Token expires: {cfg.tokenExpiry || "N/A"}</div>}
          </div>
        </div>

        {inp("Client ID", "clientId", "paste from developer.intuit.com", "Found in your QBO developer app → Keys & OAuth")}
        {inp("Client Secret", "clientSecret", "paste from developer.intuit.com", "Keep this private — never share it")}
        {inp("Company ID (Realm ID)", "realmId", "e.g. 9341452374937593", "In QBO: go to Settings → Account & Settings — it's in the URL bar")}
        {inp("Access Token", "accessToken", "paste after OAuth authorization", "Generated when you click Connect below")}
        {inp("Refresh Token", "refreshToken", "paste after OAuth authorization", "Used to auto-renew the access token")}

        <div style={{ background: "#F7F8FA", borderRadius: 12, padding: "14px 16px", border: "1px solid #E5E7EB", marginBottom: 20 }}>
          <div style={{ color: "#334155", fontWeight: 600, fontSize: 13, marginBottom: 8 }}>OAuth Authorization</div>
          <div style={{ color: "#64748B", fontSize: 12, marginBottom: 12 }}>After entering your Client ID and Secret, click below to authorize. You'll be redirected to QuickBooks to approve access, then returned here with tokens.</div>
          <button
            onClick={() => {
              if (!cfg.clientId) { alert("Enter your Client ID first"); return; }
              const params = new URLSearchParams({ client_id: cfg.clientId, response_type: "code", scope: "com.intuit.quickbooks.accounting", redirect_uri: "https://minttime.app/qb-callback", state: "mint-time" });
              alert(`In production this opens:\nhttps://appcenter.intuit.com/connect/oauth2?${params}\n\nFor now, complete OAuth in your developer portal and paste the tokens above.`);
            }}
            style={{ width: "100%", padding: "11px", borderRadius: 10, background: "linear-gradient(135deg, #2563EB, #1D4ED8)", border: "none", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
            🔗 Authorize with QuickBooks
          </button>
        </div>

        {/* Employee QBO ID mapping */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ color: "#334155", fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Employee → QBO ID Mapping</div>
          <div style={{ color: "#64748B", fontSize: 11, marginBottom: 12 }}>Find each employee's QBO ID in QuickBooks → Payroll → Employees. Click an employee and copy the ID from the URL.</div>
          {users.filter(u => u.role === "employee").map(u => (
            <div key={u.id} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <Avatar name={u.name} size={32} />
              <div style={{ flex: 1, color: "#0F172A", fontSize: 13, fontWeight: 600 }}>{u.name}</div>
              <input
                value={u.qbEmployeeId || ""}
                onChange={e => setUsers(prev => prev.map(p => p.id === u.id ? { ...p, qbEmployeeId: e.target.value } : p))}
                placeholder="QBO Employee ID"
                style={{ width: 160, padding: "8px 12px", borderRadius: 8, background: "#F7F8FA", border: "1px solid #E5E7EB", color: "#0F172A", fontSize: 12, fontFamily: "inherit" }}
              />
            </div>
          ))}
        </div>

        <button onClick={handleSave}
          style={{ width: "100%", padding: "14px", borderRadius: 12, background: saved ? "#70C0A8" : "linear-gradient(135deg, #10B981, #059669)", border: "none", color: "#fff", fontWeight: 700, fontSize: 15, cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s" }}>
          {saved ? "✓ Saved!" : "Save Settings"}
        </button>
      </div>
    </div>
  );
}

// ─── GHL / REVIEWS SETTINGS PANEL (admin only) ───────────────────────────────
// Connects Mint Time to GoHighLevel so an approved vehicle's photo link is
// dropped into the customer's contact (a note + a custom field). The API token
// is masked after entry — kept in Firestore, read only by the serverless proxy.
function GHLSettings({ ghlConfig, onSave, onClose }) {
  const [cfg, setCfg] = useState({ enabled: false, token: "", locationId: "", fieldId: "", ...ghlConfig });
  const [editingToken, setEditingToken] = useState(!ghlConfig?.token); // show masked once a token exists
  const [saved, setSaved] = useState(false);
  const hasToken = !!ghlConfig?.token;

  const handleSave = () => { onSave(cfg); setSaved(true); setTimeout(() => setSaved(false), 2000); };

  const field = (label, key, placeholder, hint) => (
    <div style={{ marginBottom: 16 }}>
      <div style={{ color: "#334155", fontSize: 12, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 6 }}>{label}</div>
      {hint && <div style={{ color: "#64748B", fontSize: 11, marginBottom: 6 }}>{hint}</div>}
      <input value={cfg[key] || ""} onChange={e => setCfg(p => ({ ...p, [key]: e.target.value }))} placeholder={placeholder}
        style={{ width: "100%", padding: "10px 14px", borderRadius: 10, background: "#F7F8FA", border: "1px solid #E5E7EB", color: "#0F172A", fontSize: 14, fontFamily: "inherit", boxSizing: "border-box" }} />
    </div>
  );

  return (
    <div style={{ position: "fixed", inset: 0, background: "#0F172A80", zIndex: 1000, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div style={{ background: "#FFFFFF", borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 560, maxHeight: "90vh", overflowY: "auto", padding: 24, border: "1px solid #E5E7EB" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <div>
            <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 18, fontWeight: 700, color: "#0F172A" }}>GHL / Reviews</div>
            <div style={{ color: "#64748B", fontSize: 12, marginTop: 2 }}>Send approved photo links to GoHighLevel</div>
          </div>
          <button onClick={onClose} style={{ background: "#F7F8FA", border: "1px solid #E5E7EB", color: "#64748B", borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontSize: 12, fontFamily: "inherit" }}>✕ Close</button>
        </div>

        <label style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20, cursor: "pointer" }}>
          <input type="checkbox" checked={!!cfg.enabled} onChange={e => setCfg(p => ({ ...p, enabled: e.target.checked }))} style={{ width: 18, height: 18 }} />
          <span style={{ color: "#334155", fontWeight: 600, fontSize: 14 }}>Enable sending to GoHighLevel</span>
        </label>

        <div style={{ marginBottom: 16 }}>
          <div style={{ color: "#334155", fontSize: 12, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 6 }}>API Token (Private Integration)</div>
          <div style={{ color: "#64748B", fontSize: 11, marginBottom: 6 }}>GHL → Settings → Private Integrations. Needs contact read/write scope. Kept hidden after saving.</div>
          {hasToken && !editingToken ? (
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <div style={{ flex: 1, padding: "10px 14px", borderRadius: 10, background: "#F0FDF4", border: "1px solid #86EFAC", color: "#166534", fontSize: 13, fontWeight: 600 }}>•••••••••••• saved</div>
              <button onClick={() => { setEditingToken(true); setCfg(p => ({ ...p, token: "" })); }} style={{ padding: "10px 14px", borderRadius: 10, border: "1px solid #E5E7EB", background: "#F7F8FA", color: "#334155", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Replace</button>
            </div>
          ) : (
            <input type="password" autoComplete="off" value={cfg.token || ""} onChange={e => setCfg(p => ({ ...p, token: e.target.value }))} placeholder="pit-xxxxxxxx…"
              style={{ width: "100%", padding: "10px 14px", borderRadius: 10, background: "#F7F8FA", border: "1px solid #E5E7EB", color: "#0F172A", fontSize: 14, fontFamily: "inherit", boxSizing: "border-box" }} />
          )}
        </div>

        {field("Location ID", "locationId", "e.g. ve9EPM428h8vShlRW1KT", "GHL → Settings → Business Profile (the sub-account ID)")}
        {field("Custom Field Key or ID", "fieldId", "contact.photo_url", "GHL → Settings → Custom Fields → your field. Paste its Key (shown under the field name, e.g. contact.photo_url) — no {{ }} braces — or its ID.")}

        <div style={{ background: "#FFFBEB", borderRadius: 10, padding: "10px 14px", border: "1px solid #F59E0B44", marginBottom: 20, color: "#92400E", fontSize: 11.5, lineHeight: 1.5 }}>
          On approval we add a <b>note</b> (kept forever, so repeat customers keep every visit's link) and set the <b>custom field</b> to the latest link for your review-request automation.
        </div>

        <button onClick={handleSave}
          style={{ width: "100%", padding: "14px", borderRadius: 12, background: saved ? "#70C0A8" : "linear-gradient(135deg, #10B981, #059669)", border: "none", color: "#fff", fontWeight: 700, fontSize: 15, cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s" }}>
          {saved ? "✓ Saved!" : "Save Settings"}
        </button>
      </div>
    </div>
  );
}

// ─── MANAGER DASHBOARD ────────────────────────────────────────────────────────
function ManagerView({ user, entries, allUsers, ghlConfig, onApprove, onReject, onUpdateEntry, onViewTrail, onAddEntry, onLogout, qbConfig, onOpenQBSettings, onOpenGhlSettings, onOpenReports, onOpenEmployees, onOpenAssignments, onPreview }) {
  const [tab, setTab] = useState("pending");
  const [filterUser, setFilterUser] = useState("all");
  const [toast, setToast] = useState(null);
  const [processing, setProcessing] = useState({});
  const [editingEntry, setEditingEntry] = useState(null);
  const [viewingHistory, setViewingHistory] = useState(null);
  const [showGuide, setShowGuide] = useState(false);
  const [showChecklists, setShowChecklists] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState(false); // this-week hours/cost by person
  const [apptsByKey, setApptsByKey] = useState({}); // `${dateISO}_${userId}` -> [appt] worked that day

  const showToast = (msg, color) => { setToast({ msg, color }); setTimeout(() => setToast(null), 2500); };

  const handleApproveLocal = (id, name) => {
    setProcessing(p => ({ ...p, [id]: "approving" }));
    setTimeout(() => {
      onApprove(id);
      setProcessing(p => { const n = {...p}; delete n[id]; return n; });
      showToast(`✓ Approved ${name.split(" ")[0]}'s time`, "#70C0A8");
    }, 300);
  };

  const handleRejectLocal = (id, name) => {
    setProcessing(p => ({ ...p, [id]: "rejecting" }));
    setTimeout(() => {
      onReject(id);
      setProcessing(p => { const n = {...p}; delete n[id]; return n; });
      showToast(`✕ Rejected ${name.split(" ")[0]}'s time`, "#EF4444");
    }, 300);
  };

  const isMobile = useIsMobile();
  const pending = entries.filter(e => e.status === "pending");
  const active = entries.filter(e => e.status === "active");

  const filtered = entries.filter(e => {
    if (tab === "pending") return e.status === "pending";
    if (tab === "active") return e.status === "active";
    if (tab === "approved") return e.status === "approved";
    return true;
  }).filter(e => filterUser === "all" || e.userId === filterUser);

  // Current week (local Mon 00:00 – Sun 23:59). "Worked so far" counts every
  // completed shift this week (clocked out, not rejected), pending or approved.
  const _now = new Date();
  const _dow = _now.getDay();
  const weekStart = new Date(_now); weekStart.setDate(_now.getDate() - (_dow === 0 ? 6 : _dow - 1)); weekStart.setHours(0, 0, 0, 0);
  const weekEnd = new Date(weekStart); weekEnd.setDate(weekStart.getDate() + 6); weekEnd.setHours(23, 59, 59, 999);
  const thisWeekEntries = entries.filter(e => e.clockOut && e.status !== "rejected" && new Date(e.clockIn) >= weekStart && new Date(e.clockIn) <= weekEnd);
  const weekHours = thisWeekEntries.reduce((s, e) => s + (e.hours || 0), 0);
  // Labor cost so far this week = each shift's hours × that employee's rate.
  const rateOf = (userId) => parseFloat(allUsers.find(u => u.id === userId)?.hourlyRate) || 0;
  const weekCost = thisWeekEntries.reduce((s, e) => s + (e.hours || 0) * rateOf(e.userId), 0);
  // Same shifts, grouped by person — so the card total can be audited row by row.
  const weekByPerson = (() => {
    const m = new Map();
    thisWeekEntries.forEach(e => {
      const cur = m.get(e.userId) || { userId: e.userId, name: e.userName || allUsers.find(u => u.id === e.userId)?.name || "Unknown", hours: 0, shifts: 0 };
      cur.hours += (e.hours || 0);
      cur.shifts += 1;
      m.set(e.userId, cur);
    });
    return [...m.values()].map(r => { const rate = rateOf(r.userId); return { ...r, rate, cost: r.hours * rate }; })
      .sort((a, b) => b.hours - a.hours);
  })();

  // Pull the appointments each person worked per day (from that day's crew
  // assignment + the appointments source) so hours can be validated against the
  // jobs. Bounded to recent dates present in the entries, refetched only when the
  // set of dates changes (not on every approve/reject).
  const apptDateSig = [...new Set(entries.filter(e => e.clockIn).map(e => fmtDateISO(e.clockIn)))].sort().join(",");
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 45);
      const dates = apptDateSig ? apptDateSig.split(",").filter(d => new Date(d + "T00:00:00") >= cutoff).sort().reverse().slice(0, 31) : [];
      if (!dates.length) { if (!cancelled) setApptsByKey({}); return; }
      const map = {};
      await Promise.all(dates.map(async (d) => {
        try {
          const [asnap, appts] = await Promise.all([
            getDoc(doc(db, "assignments", d)),
            fetchAppointments(d).catch(() => []),
          ]);
          const crews = asnap.exists() ? (asnap.data().crews || []) : [];
          const byId = new Map(appts.map(a => [String(a.id), a]));
          const userIds = new Set();
          crews.forEach(c => { userIds.add(c.leadId); (c.helpers || []).forEach(h => userIds.add(h.id)); (c.apprenticeIds || []).forEach(id => userIds.add(id)); });
          userIds.forEach(uid => {
            const list = apptIdsForUser(crews, uid).map(id => byId.get(String(id))).filter(Boolean).sort((a, b) => (a.sortMins ?? 0) - (b.sortMins ?? 0));
            if (list.length) map[`${d}_${uid}`] = list;
          });
        } catch { /* skip this date */ }
      }));
      if (!cancelled) setApptsByKey(map);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apptDateSig]);

  const isAdmin = user.role === "admin";
  const navItems = [
    { key: "home", label: "Home", icon: "🏠", active: true, badge: pending.length || null, onClick: () => window.scrollTo({ top: 0, behavior: "smooth" }) },
    isAdmin && { key: "assign", label: "Assign", icon: "📋", color: "#0EA5E9", onClick: onOpenAssignments },
    { key: "add", label: "Add", icon: "＋", primary: true, onClick: onAddEntry },
    { key: "reports", label: "Reports", icon: "📊", color: "#3B82F6", onClick: onOpenReports },
    isAdmin && { key: "team", label: "Team", icon: "👥", color: "#8B5CF6", onClick: onOpenEmployees },
  ];

  return (
    <div style={{ minHeight: "100vh", background: PAGE_BG, fontFamily: "'DM Sans', sans-serif", paddingBottom: 40 }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Space+Grotesk:wght@700&display=swap" rel="stylesheet" />
      <Toast toast={toast} />
      {editingEntry && (
        <TimeEditModal
          entry={editingEntry}
          userRole={user.role}
          onSave={(updated) => {
            onUpdateEntry(updated);
            setEditingEntry(null);
            showToast("✓ Time entry updated", "#70C0A8");
          }}
          onClose={() => setEditingEntry(null)}
        />
      )}
      {viewingHistory && (
        <div style={{ position: "fixed", inset: 0, background: "#0F172A80", zIndex: 800, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ background: "#FFFFFF", borderRadius: 20, width: "100%", maxWidth: 480, maxHeight: "80vh", overflowY: "auto", padding: 24, border: "1px solid #E5E7EB", fontFamily: "'DM Sans', sans-serif" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 16, fontWeight: 700, color: "#0F172A" }}>Edit History</div>
              <button onClick={() => setViewingHistory(null)} style={{ background: "none", border: "1px solid #E5E7EB", color: "#64748B", borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontSize: 12, fontFamily: "inherit" }}>✕</button>
            </div>
            <div style={{ color: "#64748B", fontSize: 13, marginBottom: 16 }}>{viewingHistory.userName} · {fmtDate(viewingHistory.clockIn)}</div>
            {(viewingHistory.editHistory || []).map((edit, i) => (
              <div key={i} style={{ background: "#F7F8FA", borderRadius: 12, padding: "12px 14px", marginBottom: 10, border: "1px solid #E5E7EB" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={{ color: edit.editedBy === "employee" ? "#F59E0B" : "#3B82F6", fontSize: 11, fontWeight: 700, textTransform: "uppercase" }}>
                    {edit.editedBy === "employee" ? "⚠ Employee Edit" : "Manager Edit"}
                  </span>
                  <span style={{ color: "#64748B", fontSize: 11 }}>{new Date(edit.editedAt).toLocaleString()}</span>
                </div>
                <div style={{ color: "#64748B", fontSize: 12, marginBottom: 6 }}>
                  {fmt(new Date(edit.originalClockIn))} – {edit.originalClockOut ? fmt(new Date(edit.originalClockOut)) : "—"} ({edit.originalHours?.toFixed(2)}h)
                  <span style={{ color: "#64748B" }}> → edited</span>
                </div>
                <div style={{ color: "#334155", fontSize: 13, fontStyle: "italic" }}>"{edit.reason}"</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={headerBar}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}><img src={MINT_LOGO} alt="Mint" style={{ width: 64, height: 64, display: "block", flexShrink: 0 }} /><div style={headerTitle}>Mint Time</div></div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {!isMobile && (<>
          {isAdmin && <button onClick={onOpenAssignments}
              style={{ background: "#F7F8FA", border: "1px solid #0EA5E944", color: "#0EA5E9", borderRadius: 8, padding: "6px 10px", cursor: "pointer", fontSize: 11, fontWeight: 700, fontFamily: "inherit" }}>
              📋 Assign
            </button>}
          <button onClick={onAddEntry}
              style={{ background: "#F7F8FA", border: "1px solid #70C0A844", color: "#4A9E88", borderRadius: 8, padding: "6px 10px", cursor: "pointer", fontSize: 11, fontWeight: 700, fontFamily: "inherit" }}>
              ＋ Add Entry
            </button>
          <button onClick={onOpenReports}
              style={{ background: "#F7F8FA", border: "1px solid #3B82F644", color: "#3B82F6", borderRadius: 8, padding: "6px 10px", cursor: "pointer", fontSize: 11, fontWeight: 700, fontFamily: "inherit" }}>
              📊 Reports
            </button>
          {user.role === "admin" && <button onClick={onOpenEmployees}
              style={{ background: "#F7F8FA", border: "1px solid #8B5CF644", color: "#8B5CF6", borderRadius: 8, padding: "6px 10px", cursor: "pointer", fontSize: 11, fontWeight: 700, fontFamily: "inherit" }}>
              👥 Team
            </button>}
          {user.role === "admin" && (
            <button onClick={onOpenQBSettings}
              style={{ background: qbConfig.connected ? "#F7F8FA" : "#FEF2F2", border: `1px solid ${qbConfig.connected ? "#70C0A844" : "#EF444444"}`, color: qbConfig.connected ? "#70C0A8" : "#EF4444", borderRadius: 8, padding: "6px 10px", cursor: "pointer", fontSize: 11, fontWeight: 700, fontFamily: "inherit" }}>
              {qbConfig.connected ? "● QB Connected" : "○ QB Setup"}
            </button>
          )}
          {user.role === "admin" && (
            <button onClick={onOpenGhlSettings}
              style={{ background: ghlConfig?.enabled ? "#F7F8FA" : "#FEF2F2", border: `1px solid ${ghlConfig?.enabled ? "#10B98144" : "#EF444444"}`, color: ghlConfig?.enabled ? "#059669" : "#EF4444", borderRadius: 8, padding: "6px 10px", cursor: "pointer", fontSize: 11, fontWeight: 700, fontFamily: "inherit" }}>
              {ghlConfig?.enabled ? "● GHL On" : "○ GHL Setup"}
            </button>
          )}
          </>)}
          <button onClick={() => setShowChecklists(true)} style={{ background: "#F7F8FA", border: "1px solid #0EA5E944", color: "#0369A1", borderRadius: 8, padding: "6px 10px", cursor: "pointer", fontSize: 11, fontWeight: 700, fontFamily: "inherit" }}>📸 Photos</button>
          <button onClick={() => setShowGuide(true)} style={{ background: "#F7F8FA", border: "1px solid #E5E7EB", color: "#475569", borderRadius: 8, padding: "6px 10px", cursor: "pointer", fontSize: 11, fontWeight: 700, fontFamily: "inherit" }}>🧪 Products</button>
          <button onClick={onLogout} style={glassBtn}>Logout</button>
        </div>
      </div>

      <div style={{ padding: "20px", maxWidth: isMobile ? 700 : 1080, margin: "0 auto", paddingBottom: isMobile ? "calc(96px + env(safe-area-inset-bottom, 0px))" : 20 }}>
        {/* KPIs */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 12, marginBottom: 20 }}>
          {[
            { label: "Clocked In", value: active.length, color: "#2563EB", g: "linear-gradient(145deg, #60A5FA, #2563EB)" },
            { label: "Pending", value: pending.length, color: "#D97706", g: "linear-gradient(145deg, #FBBF24, #EA580C)" },
            { label: "Hours This Week", value: weekHours.toFixed(1) + "h", color: "#0D9488", g: "linear-gradient(145deg, #2DD4BF, #0D9488)" },
            { label: "Labor Cost", value: "$" + Math.round(weekCost).toLocaleString(), color: "#7C3AED", g: "linear-gradient(145deg, #A78BFA, #7C3AED)" },
          ].map(k => (
            <div key={k.label} style={{ background: k.g, borderRadius: 16, padding: "16px 12px", textAlign: "center", boxShadow: `0 8px 18px ${k.color}44` }}>
              <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: isMobile ? 22 : 27, fontWeight: 700, color: "#FFFFFF" }}>{k.value}</div>
              <div style={{ color: "rgba(255,255,255,0.92)", fontSize: 11, marginTop: 2, fontWeight: 600 }}>{k.label}</div>
            </div>
          ))}
        </div>

        {/* This week by person — audit the Hours/Labor Cost totals row by row */}
        <div style={{ marginBottom: 20 }}>
          <button onClick={() => setShowBreakdown(v => !v)}
            style={{ background: "#FFFFFF", border: "1px solid #E5E7EB", borderRadius: 12, padding: "10px 14px", width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", fontFamily: "inherit" }}>
            <span style={{ color: "#334155", fontSize: 13, fontWeight: 700 }}>{showBreakdown ? "▾" : "▸"} This week by person</span>
            <span style={{ color: "#64748B", fontSize: 12, fontWeight: 600 }}>{weekByPerson.length} {weekByPerson.length === 1 ? "person" : "people"} · {weekHours.toFixed(1)}h · ${Math.round(weekCost).toLocaleString()}</span>
          </button>
          {showBreakdown && (
            <div style={{ background: "#FFFFFF", border: "1px solid #E5E7EB", borderTop: "none", borderRadius: "0 0 12px 12px", marginTop: -1, overflow: "hidden" }}>
              {weekByPerson.length === 0 && <div style={{ color: "#64748B", fontSize: 13, textAlign: "center", padding: 18 }}>No completed shifts yet this week.</div>}
              {weekByPerson.map((p, i) => (
                <div key={p.userId} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderTop: i ? "1px solid #F1F5F9" : "none" }}>
                  <Avatar name={p.name} size={30} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: "#0F172A", fontWeight: 600, fontSize: 13 }}>{p.name}</div>
                    <div style={{ color: "#64748B", fontSize: 11 }}>
                      {p.hours.toFixed(1)}h × {p.rate > 0 ? "$" + p.rate.toFixed(2) + "/hr" : <span style={{ color: "#EF4444", fontWeight: 700 }}>no rate set</span>} · {p.shifts} shift{p.shifts === 1 ? "" : "s"}
                    </div>
                  </div>
                  <div style={{ color: p.rate > 0 ? "#0F172A" : "#94A3B8", fontWeight: 700, fontSize: 14, fontFamily: "'Space Grotesk', sans-serif" }}>${Math.round(p.cost).toLocaleString()}</div>
                </div>
              ))}
              {weekByPerson.length > 0 && (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", borderTop: "2px solid #E5E7EB", background: "#F8FAFC" }}>
                  <div style={{ color: "#334155", fontWeight: 700, fontSize: 13 }}>Total · {weekHours.toFixed(1)}h</div>
                  <div style={{ color: "#0F172A", fontWeight: 700, fontSize: 15, fontFamily: "'Space Grotesk', sans-serif" }}>${Math.round(weekCost).toLocaleString()}</div>
                </div>
              )}
              {weekByPerson.some(p => p.rate === 0) && (
                <div style={{ color: "#B45309", fontSize: 11, fontWeight: 600, padding: "8px 14px", background: "#FFFBEB", borderTop: "1px solid #FDE68A" }}>
                  ⚠ Someone has no hourly rate set — the cost is undercounted until you add it in Team → Edit.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Preview a crew member's day */}
        {allUsers.some(u => u.active !== false && (u.role === "lead" || u.role === "apprentice")) && (
          <div style={{ background: "#FFFFFF", borderRadius: 14, padding: "12px 16px", border: "1px solid #E5E7EB", marginBottom: 16, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={{ color: "#334155", fontSize: 13, fontWeight: 700 }}>👁 See what a crew member sees:</span>
            <select value="" onChange={e => e.target.value && onPreview(e.target.value)} style={{ flex: 1, minWidth: 180, padding: "9px 12px", borderRadius: 10, background: "#F7F8FA", border: "1px solid #E5E7EB", color: "#334155", fontSize: 13, fontFamily: "inherit" }}>
              <option value="">Choose a lead or apprentice…</option>
              {allUsers.filter(u => u.active !== false && (u.role === "lead" || u.role === "apprentice")).sort((a, b) => a.name.localeCompare(b.name)).map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
            </select>
          </div>
        )}

        {/* Tabs */}
        <div style={{ display: "flex", gap: 4, marginBottom: 16, background: "#FFFFFF", borderRadius: 12, padding: 4, border: "1px solid #E5E7EB" }}>
          {[["pending", `Pending (${pending.length})`], ["active", `Active (${active.length})`], ["approved", "Approved"], ["all", "All"]].map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)}
              style={{ flex: 1, padding: "8px 4px", borderRadius: 9, border: "none", background: tab === key ? "linear-gradient(135deg, #10B981, #0D9488)" : "transparent", color: tab === key ? "#FFFFFF" : "#475569", fontWeight: 700, fontSize: 12, cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s" }}>
              {label}
            </button>
          ))}
        </div>

        <select value={filterUser} onChange={e => setFilterUser(e.target.value)}
          style={{ width: "100%", marginBottom: 16, padding: "10px 14px", borderRadius: 10, background: "#FFFFFF", border: "1px solid #E5E7EB", color: "#334155", fontSize: 13, fontFamily: "inherit" }}>
          <option value="all">All Employees</option>
          {allUsers.filter(u => FIELD_ROLES.includes(u.role)).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>

        {filtered.length === 0 && <div style={{ color: "#64748B", textAlign: "center", padding: 32 }}>No entries in this view.</div>}
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: 12, alignItems: "start" }}>
        {filtered.map(e => (
          <div key={e.id} style={{ background: "#FFFFFF", borderRadius: 16, padding: "16px 18px", border: `1px solid ${e.qbSynced ? "#8B5CF622" : "#E5E7EB"}`, marginBottom: 0 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Avatar name={e.userName} size={36} />
                <div>
                  <div style={{ color: "#0F172A", fontWeight: 600, fontSize: 15 }}>{e.userName}</div>
                  <div style={{ color: "#64748B", fontSize: 12 }}>{fmtDate(e.clockIn)}</div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                <Badge status={e.status} />
                {e.qbSynced && <Badge status="synced" />}
                {e.editedByEmployee && <span style={{ background: "#F59E0B22", color: "#F59E0B", border: "1px solid #F59E0B44", borderRadius: 20, padding: "2px 8px", fontSize: 10, fontWeight: 700 }}>⚠ EMP EDIT</span>}
                {e.manuallyCreated && <span title={e.createdReason || ""} style={{ background: "#3B82F622", color: "#3B82F6", border: "1px solid #3B82F644", borderRadius: 20, padding: "2px 8px", fontSize: 10, fontWeight: 700 }}>✎ MANUAL</span>}
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8, marginBottom: 10 }}>
              <div style={{ background: "#F7F8FA", borderRadius: 10, padding: "10px 12px" }}>
                <div style={{ color: "#64748B", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>Clock In</div>
                <div style={{ color: "#334155", fontWeight: 600, fontSize: 13 }}>{fmt(e.clockIn)}</div>
                {e.locationIn && <div style={{ color: "#64748B", fontSize: 10, marginTop: 2 }}>📍 {e.locationIn.label}</div>}
              </div>
              <div style={{ background: "#F7F8FA", borderRadius: 10, padding: "10px 12px" }}>
                <div style={{ color: "#64748B", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>Clock Out</div>
                <div style={{ color: "#334155", fontWeight: 600, fontSize: 13 }}>{fmt(e.clockOut)}</div>
                {e.locationOut && <div style={{ color: "#64748B", fontSize: 10, marginTop: 2 }}>📍 {e.locationOut.label}</div>}
              </div>
              <div style={{ background: "#F7F8FA", borderRadius: 10, padding: "10px 12px" }}>
                <div style={{ color: "#64748B", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>Duration</div>
                <div style={{ color: "#0D9488", fontWeight: 800, fontSize: 14 }}>{hoursLabel(e)}</div>
              </div>
            </div>
            {/* Appointments this person worked that day — validate hours vs jobs */}
            {(() => {
              const dayAppts = apptsByKey[`${fmtDateISO(e.clockIn)}_${e.userId}`];
              if (!dayAppts || !dayAppts.length) return null;
              return (
                <div style={{ marginBottom: 10, background: "#F0F9FF", border: "1px solid #0EA5E933", borderRadius: 10, padding: "8px 10px" }}>
                  <div style={{ color: "#0369A1", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 5 }}>
                    📋 Appointments that day · {dayAppts.length}
                  </div>
                  {dayAppts.map(a => (
                    <div key={a.id} style={{ display: "flex", gap: 8, alignItems: "baseline", padding: "2px 0" }}>
                      <span style={{ color: "#0EA5E9", fontWeight: 800, fontSize: 12, minWidth: 52, flexShrink: 0 }}>{a.time}</span>
                      <span style={{ flex: 1, minWidth: 0, color: "#334155", fontSize: 12 }}>
                        <span style={{ fontWeight: 700 }}>{a.customer}</span>{a.address ? <span style={{ color: "#64748B" }}> · {a.address}</span> : ""}
                      </span>
                    </div>
                  ))}
                </div>
              );
            })()}
            {(e.locationIn?.lat != null || e.locationOut?.lat != null) && (
              <div style={{ display: "grid", gridTemplateColumns: e.locationOut?.lat != null ? "repeat(2, minmax(0, 1fr))" : "1fr", gap: 8, marginBottom: 10 }}>
                {e.locationIn?.lat != null && (
                  <div>
                    <div style={{ color: "#64748B", fontSize: 10, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 4 }}>In Location</div>
                    <MapPin lat={e.locationIn.lat} lng={e.locationIn.lng} height={120} zoom={15} rounded={10} />
                  </div>
                )}
                {e.locationOut?.lat != null && (
                  <div>
                    <div style={{ color: "#64748B", fontSize: 10, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 4 }}>Out Location</div>
                    <MapPin lat={e.locationOut.lat} lng={e.locationOut.lng} height={120} zoom={15} rounded={10} />
                  </div>
                )}
              </div>
            )}
            {/* Edit button — managers can edit any entry */}
            <div style={{ display: "flex", gap: 8, marginBottom: e.status === "pending" ? 8 : 0 }}>
              <button onClick={() => setEditingEntry(e)}
                style={{ flex: 1, padding: "9px", borderRadius: 10, background: "#EEF2F6", border: "1px solid #CBD5E1", color: "#334155", fontSize: 12, cursor: "pointer", fontFamily: "inherit", fontWeight: 700 }}>
                ✏ Edit Time
              </button>
              <button onClick={() => onViewTrail && onViewTrail(e)}
                style={{ flex: 1, padding: "9px", borderRadius: 10, background: "#DFF5EE", border: "1px solid #10B98166", color: "#047857", fontSize: 12, cursor: "pointer", fontFamily: "inherit", fontWeight: 700 }}>
                🗺 View Trail
              </button>
              {e.editHistory?.length > 0 && (
                <button onClick={() => setViewingHistory(e)}
                  style={{ padding: "8px 12px", borderRadius: 10, background: "#F7F8FA", border: "1px solid #F59E0B44", color: "#F59E0B", fontSize: 12, cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}>
                  📋 {e.editHistory.length}
                </button>
              )}
            </div>

            {e.status === "pending" && (
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => handleApproveLocal(e.id, e.userName)} disabled={!!processing[e.id]}
                  style={{ flex: 1, padding: "11px", borderRadius: 10, background: processing[e.id] === "approving" ? "#047857" : "linear-gradient(135deg, #10B981, #059669)", border: "none", color: "#fff", fontWeight: 800, fontSize: 13, cursor: processing[e.id] ? "default" : "pointer", fontFamily: "inherit", opacity: processing[e.id] ? 0.7 : 1, boxShadow: "0 4px 12px rgba(5,150,105,0.4)" }}>
                  {processing[e.id] === "approving" ? "Approving..." : "✓ Approve"}
                </button>
                <button onClick={() => handleRejectLocal(e.id, e.userName)} disabled={!!processing[e.id]}
                  style={{ flex: 1, padding: "11px", borderRadius: 10, background: processing[e.id] === "rejecting" ? "#B91C1C" : "linear-gradient(135deg, #F43F5E, #E11D48)", border: "none", color: "#fff", fontWeight: 800, fontSize: 13, cursor: processing[e.id] ? "default" : "pointer", fontFamily: "inherit", opacity: processing[e.id] ? 0.7 : 1, boxShadow: "0 4px 12px rgba(225,29,72,0.4)" }}>
                  {processing[e.id] === "rejecting" ? "Rejecting..." : "✕ Reject"}
                </button>
              </div>
            )}
          </div>
        ))}
        </div>
      </div>
      {showGuide && <ProductGuide onClose={() => setShowGuide(false)} />}
      {showChecklists && <ChecklistReview allUsers={allUsers} user={user} ghlConfig={ghlConfig} onClose={() => setShowChecklists(false)} />}
      {isMobile && <BottomNav items={navItems} />}
    </div>
  );
}



// ─── REPORTS MODULE ───────────────────────────────────────────────────────────

function ReportsView({ entries: baseEntries, allUsers, onClose }) {
  const entries = baseEntries;
  const isMobile = useIsMobile();
  const [reportType, setReportType] = useState("payroll");

  // Sort helper: alphabetical by last name (last whitespace-separated token),
  // with full-name as a tiebreaker.
  const lastNameOf = (name) => (name || "").trim().split(/\s+/).slice(-1)[0]?.toLowerCase() || "";
  const byLastName = (a, b) => lastNameOf(a).localeCompare(lastNameOf(b)) || (a || "").localeCompare(b || "");

  const [weekOffset, setWeekOffset] = useState(0); // 0 = this week, -1 = last week
  const [monthOffset, setMonthOffset] = useState(0);
  const [selectedDate, setSelectedDate] = useState(localDateISO());
  const [tips, setTips] = useState({ loading: true }); // Square tips for the selected week, split per crew

  // ── Date helpers ──
  const getWeekRange = (offset = 0) => {
    const now = new Date();
    const day = now.getDay();
    const monday = new Date(now); monday.setDate(now.getDate() - (day === 0 ? 6 : day - 1) + offset * 7); monday.setHours(0,0,0,0);
    const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6); sunday.setHours(23,59,59,999);
    return { start: monday, end: sunday };
  };

  const getMonthRange = (offset = 0) => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() + offset, 1);
    const end = new Date(now.getFullYear(), now.getMonth() + offset + 1, 0, 23, 59, 59);
    return { start, end };
  };

  const fmtRange = (start, end) => `${start.toLocaleDateString([], { month: "short", day: "numeric" })} – ${end.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })}`;
  const fmtMonthLabel = (offset) => { const d = new Date(); d.setMonth(d.getMonth() + offset); return d.toLocaleDateString([], { month: "long", year: "numeric" }); };
  const fmtWeekLabel = (offset) => { if (offset === 0) return "This Week"; if (offset === -1) return "Last Week"; const { start } = getWeekRange(offset); return `Week of ${start.toLocaleDateString([], { month: "short", day: "numeric" })}`; };

  // ── Filtered entries per report ──
  const weekRange = getWeekRange(weekOffset);
  const monthRange = getMonthRange(monthOffset);

  const weekEntries = entries.filter(e => e.clockOut && e.status === "approved" && new Date(e.clockIn) >= weekRange.start && new Date(e.clockIn) <= weekRange.end);
  const dayEntries = entries.filter(e => e.clockOut && fmtDateISO(e.clockIn) === selectedDate).sort((a, b) => byLastName(a.userName, b.userName));
  const monthEntries = entries.filter(e => e.clockOut && e.status === "approved" && new Date(e.clockIn) >= monthRange.start && new Date(e.clockIn) <= monthRange.end);

  // Include anyone who can clock in — admins and managers can too, not just "employee" role.
  // Also include deactivated users that still have entries in any visible period,
  // plus synthesize "ghost" users for orphan entries whose userId no longer exists
  // in the users collection (e.g., a user was deleted and recreated). Use the
  // userName snapshot stored on the entry to label them.
  const allUserIds = new Set(allUsers.map(u => u.id));
  const viewEntries = [...weekEntries, ...monthEntries, ...dayEntries];
  const userIdsInView = new Set(viewEntries.map(e => e.userId));
  const orphanUsers = [...new Map(
    viewEntries
      .filter(e => !allUserIds.has(e.userId))
      .map(e => [e.userId, { id: e.userId, name: e.userName || "Unknown user", email: "", role: "employee", pin: "", active: false, orphan: true }])
  ).values()];
  const employees = [
    ...allUsers.filter(u => u.active !== false || userIdsInView.has(u.id)),
    ...orphanUsers,
  ].sort((a, b) => byLastName(a.name, b.name));

  // ── Payroll summary data ──
  const payrollData = employees.map(emp => {
    const empEntries = weekEntries.filter(e => e.userId === emp.id);
    const totalHours = empEntries.reduce((s, e) => s + (e.hours || 0), 0);
    const days = [...new Set(empEntries.map(e => fmtDateISO(e.clockIn)))].length;
    const rate = parseFloat(emp.hourlyRate) || 0;
    const pay = totalHours * rate;
    return { ...emp, entries: empEntries, totalHours, days, rate, pay, synced: empEntries.every(e => e.qbSynced) };
  }).sort((a, b) => (b.totalHours > 0) - (a.totalHours > 0) || byLastName(a.name, b.name));

  // ── Square tips for the week, matched to appointments and split 50/50 between
  // the lead and the apprentice(s) who worked each job. ──
  const nameById = (id) => allUsers.find(u => u.id === id)?.name || "Unknown";
  useEffect(() => {
    let cancelled = false;
    setTips({ loading: true });
    (async () => {
      const startISO = fmtDateISO(weekRange.start);
      const endISO = fmtDateISO(weekRange.end);
      const weekDays = Array.from({ length: 7 }, (_, i) => { const d = new Date(weekRange.start); d.setDate(d.getDate() + i); return fmtDateISO(d); });
      try {
        const resp = await fetchTips(startISO, endISO);
        if (resp.error) { if (!cancelled) setTips({ loading: false, error: true }); return; }
        const tipList = (resp.tips || []).filter(t => weekDays.includes(t.date));
        if (!tipList.length) { if (!cancelled) setTips({ loading: false, appts: [], perEmp: [], matchedTotal: 0, unmatched: [], unmatchedTotal: 0 }); return; }

        // Load appointments + crew assignment only for days that actually have tips.
        const tipDays = [...new Set(tipList.map(t => t.date))];
        const dayData = {};
        await Promise.all(tipDays.map(async (d) => {
          const [appts, asnap] = await Promise.all([fetchAppointments(d).catch(() => []), getDoc(doc(db, "assignments", d)).catch(() => null)]);
          dayData[d] = { appts, crews: asnap && asnap.exists() ? (asnap.data().crews || []) : [] };
        }));

        // Aggregate tips by (day, customer email) — a customer's day = one job.
        const byKey = {};
        tipList.forEach(t => {
          const em = (t.email || "").trim().toLowerCase();
          const key = `${t.date}__${em}`;
          byKey[key] = byKey[key] || { date: t.date, email: em, name: t.name, tip: 0 };
          byKey[key].tip += t.tip;
        });

        const perEmp = {}; // userId -> amount
        const credit = (uid, amt) => { if (!uid) return; perEmp[uid] = (perEmp[uid] || 0) + amt; };
        const apptRows = [];
        const unmatched = [];

        Object.values(byKey).forEach(g => {
          const dd = dayData[g.date];
          const appt = dd && g.email ? dd.appts.find(a => (a.email || "").trim().toLowerCase() === g.email) : null;
          if (!appt) { unmatched.push({ date: g.date, name: g.name || g.email || "Unknown customer", tip: g.tip }); return; }
          const crew = dd.crews.find(c => (c.appointmentIds || []).map(String).includes(String(appt.id)));
          const leadId = crew?.leadId || null;
          const helperIds = crew ? helpersOnAppt(crew, appt.id) : [];
          // 50/50 lead & apprentice. Apprentice half is shared among helpers.
          let leadAmt = 0; const helperCuts = [];
          if (leadId && helperIds.length) { leadAmt = g.tip / 2; const each = (g.tip / 2) / helperIds.length; helperIds.forEach(h => helperCuts.push({ id: h, amt: each })); }
          else if (leadId) { leadAmt = g.tip; }
          else if (helperIds.length) { const each = g.tip / helperIds.length; helperIds.forEach(h => helperCuts.push({ id: h, amt: each })); }
          credit(leadId, leadAmt);
          helperCuts.forEach(h => credit(h.id, h.amt));
          apptRows.push({
            date: g.date, customer: appt.customer, tip: g.tip,
            leadName: leadId ? nameById(leadId) : null, leadAmt,
            helpers: helperCuts.map(h => ({ name: nameById(h.id), amt: h.amt })),
            noCrew: !crew,
          });
        });

        const perEmpArr = Object.entries(perEmp).map(([id, amount]) => ({ id, name: nameById(id), amount })).sort((a, b) => b.amount - a.amount);
        const matchedTotal = apptRows.reduce((s, r) => s + r.tip, 0);
        const unmatchedTotal = unmatched.reduce((s, r) => s + r.tip, 0);
        if (!cancelled) setTips({ loading: false, appts: apptRows.sort((a, b) => (a.date < b.date ? -1 : 1)), perEmp: perEmpArr, matchedTotal, unmatched, unmatchedTotal });
      } catch (e) {
        if (!cancelled) setTips({ loading: false, error: true });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekOffset]);

  const totalPayrollHours = payrollData.reduce((s, e) => s + e.totalHours, 0);
  const totalPayrollPay = payrollData.reduce((s, e) => s + e.pay, 0);

  // ── Monthly data ──
  const monthlyData = employees.map(emp => {
    const empEntries = monthEntries.filter(e => e.userId === emp.id);
    const totalHours = empEntries.reduce((s, e) => s + (e.hours || 0), 0);
    const days = [...new Set(empEntries.map(e => fmtDateISO(e.clockIn)))].length;
    // weekly breakdown
    const weeks = [0,1,2,3].map(w => {
      const ws = getWeekRange(w - Math.ceil((new Date().getDate()) / 7) + (monthOffset !== 0 ? monthOffset * 4 : 0));
      const wEntries = empEntries.filter(e => new Date(e.clockIn) >= ws.start && new Date(e.clockIn) <= ws.end);
      return wEntries.reduce((s, e) => s + (e.hours || 0), 0);
    });
    return { ...emp, totalHours, days, weeks };
  }).sort((a, b) => (b.totalHours > 0) - (a.totalHours > 0) || byLastName(a.name, b.name));

  // ── CSV export ──
  const exportCSV = () => {
    let rows, filename;
    if (reportType === "payroll") {
      rows = [["Employee", "Total Hours", "Days Worked", "Hourly Rate", "Pay", "QB Synced"]];
      payrollData.forEach(e => rows.push([e.name, e.totalHours.toFixed(2), e.days, e.rate.toFixed(2), e.pay.toFixed(2), e.synced ? "Yes" : "No"]));
      rows.push(["TOTAL", totalPayrollHours.toFixed(2), "", "", totalPayrollPay.toFixed(2), ""]);
      filename = `payroll_${fmtDateISO(weekRange.start)}.csv`;
    } else if (reportType === "daily") {
      rows = [["Employee", "Date", "Clock In", "Clock Out", "Hours", "Location In", "Location Out", "Status"]];
      dayEntries.forEach(e => rows.push([e.userName, fmtDateISO(e.clockIn), fmt(e.clockIn), fmt(e.clockOut), (e.hours||0).toFixed(2), e.locationIn?.label||"", e.locationOut?.label||"", e.status]));
      filename = `attendance_${selectedDate}.csv`;
    } else {
      rows = [["Employee", "Total Hours", "Days Worked", "Week 1", "Week 2", "Week 3", "Week 4"]];
      monthlyData.forEach(e => rows.push([e.name, e.totalHours.toFixed(2), e.days, ...e.weeks.map(w => w.toFixed(1))]));
      filename = `monthly_${fmtDateISO(monthRange.start)}.csv`;
    }
    const csv = rows.map(r => r.map(c => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  // ── PDF export (print-based) ──
  const exportPDF = () => {
    const w = window.open("", "_blank");
    const title = reportType === "payroll" ? `Payroll Summary — ${fmtRange(weekRange.start, weekRange.end)}` : reportType === "daily" ? `Daily Attendance — ${selectedDate}` : `Monthly Overview — ${fmtMonthLabel(monthOffset)}`;
    let tableRows = "";

    if (reportType === "payroll") {
      tableRows = payrollData.map(e => `<tr><td>${e.name}</td><td>${e.totalHours.toFixed(2)}h</td><td>${e.days}</td><td>${e.rate > 0 ? "$" + e.rate.toFixed(2) + "/hr" : "—"}</td><td>${e.rate > 0 ? "$" + e.pay.toFixed(2) : "—"}</td><td style="color:${e.synced?"#16a34a":"#dc2626"}">${e.synced?"✓ Synced":"Pending"}</td></tr>`).join("");
      tableRows = `<table><thead><tr><th>Employee</th><th>Total Hours</th><th>Days</th><th>Rate</th><th>Pay</th><th>QB Status</th></tr></thead><tbody>${tableRows}</tbody></table><p><strong>Total Hours: ${totalPayrollHours.toFixed(2)}h · Total Pay: $${totalPayrollPay.toFixed(2)}</strong></p>`;
    } else if (reportType === "daily") {
      tableRows = dayEntries.map(e => `<tr><td>${e.userName}</td><td>${fmt(e.clockIn)}</td><td>${fmt(e.clockOut)}</td><td>${(e.hours||0).toFixed(2)}h</td><td>${e.locationIn?.label||""}</td><td>${e.status}</td></tr>`).join("");
      tableRows = `<table><thead><tr><th>Employee</th><th>In</th><th>Out</th><th>Hours</th><th>Location</th><th>Status</th></tr></thead><tbody>${tableRows}</tbody></table>`;
    } else {
      tableRows = monthlyData.map(e => `<tr><td>${e.name}</td><td>${e.totalHours.toFixed(2)}h</td><td>${e.days}</td><td>${e.weeks.map(w=>w.toFixed(1)+"h").join(" / ")}</td></tr>`).join("");
      tableRows = `<table><thead><tr><th>Employee</th><th>Total Hours</th><th>Days</th><th>Wk1 / Wk2 / Wk3 / Wk4</th></tr></thead><tbody>${tableRows}</tbody></table>`;
    }

    w.document.write(`<!DOCTYPE html><html><head><title>${title}</title><style>
      body{font-family:Arial,sans-serif;padding:32px;color:#111}
      h1{font-size:20px;margin-bottom:4px}
      .sub{color:#666;font-size:13px;margin-bottom:24px}
      table{width:100%;border-collapse:collapse;font-size:13px}
      th{background:#f3f4f6;text-align:left;padding:8px 12px;border-bottom:2px solid #e5e7eb}
      td{padding:8px 12px;border-bottom:1px solid #f3f4f6}
      tr:hover td{background:#fafafa}
      .footer{margin-top:24px;font-size:11px;color:#999}
    </style></head><body>
      <h1>🚗 Mint Mobile Detail — ${title}</h1>
      <div class="sub">Generated ${new Date().toLocaleString()} · Mint Time</div>
      ${tableRows}
      <div class="footer">This report is confidential and for internal use only.</div>
    </body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 400);
  };

  // ── Styles ──
  const card = { background: "#FFFFFF", borderRadius: 14, padding: "14px 16px", border: "1px solid #E5E7EB", marginBottom: 10 };
  const label = { color: "#64748B", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 3 };
  const val = { color: "#0F172A", fontWeight: 600, fontSize: 14 };

  return (
    <div style={{ position: "fixed", inset: 0, background: PAGE_BG, zIndex: 500, overflowY: "auto", overflowX: "hidden", fontFamily: "'DM Sans', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Space+Grotesk:wght@700&display=swap" rel="stylesheet" />

      {/* Header */}
      <div style={headerBarSticky}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}><img src={MINT_LOGO} alt="Mint" style={{ width: 64, height: 64, display: "block", flexShrink: 0 }} /><div style={headerTitle}>Reports</div></div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={exportCSV} style={{ background: "#F7F8FA", border: "1px solid #70C0A8", color: "#70C0A8", borderRadius: 8, padding: "7px 14px", cursor: "pointer", fontSize: 12, fontWeight: 700, fontFamily: "inherit" }}>↓ CSV</button>
          <button onClick={exportPDF} style={{ background: "#F7F8FA", border: "1px solid #3B82F6", color: "#3B82F6", borderRadius: 8, padding: "7px 14px", cursor: "pointer", fontSize: 12, fontWeight: 700, fontFamily: "inherit" }}>↓ PDF</button>
          <button onClick={onClose} style={glassBtn}>✕</button>
        </div>
      </div>

      <div style={{ padding: "20px", maxWidth: isMobile ? 700 : 960, margin: "0 auto" }}>

        {/* Report type tabs */}
        <div style={{ display: "flex", gap: 4, marginBottom: 20, background: "#FFFFFF", borderRadius: 12, padding: 4, border: "1px solid #E5E7EB" }}>
          {[["payroll", "💰 Payroll"], ["daily", "📋 Attendance"], ["monthly", "📅 Monthly"]].map(([key, label]) => (
            <button key={key} onClick={() => setReportType(key)}
              style={{ flex: 1, padding: "9px 4px", borderRadius: 9, border: "none", background: reportType === key ? "#70C0A8" : "transparent", color: reportType === key ? "#F7F8FA" : "#64748B", fontWeight: 700, fontSize: 12, cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s" }}>
              {label}
            </button>
          ))}
        </div>

        {/* ── PAYROLL REPORT ── */}
        {reportType === "payroll" && (
          <>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <button onClick={() => setWeekOffset(w => w - 1)} style={{ background: "#FFFFFF", border: "1px solid #E5E7EB", color: "#334155", borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontFamily: "inherit" }}>← Prev</button>
              <div style={{ textAlign: "center" }}>
                <div style={{ color: "#0F172A", fontWeight: 700, fontSize: 14 }}>{fmtWeekLabel(weekOffset)}</div>
                <div style={{ color: "#64748B", fontSize: 11 }}>{fmtRange(weekRange.start, weekRange.end)}</div>
              </div>
              <button onClick={() => setWeekOffset(w => Math.min(0, w + 1))} disabled={weekOffset === 0} style={{ background: "#FFFFFF", border: "1px solid #E5E7EB", color: weekOffset === 0 ? "#CBD5E1" : "#334155", borderRadius: 8, padding: "6px 12px", cursor: weekOffset === 0 ? "default" : "pointer", fontFamily: "inherit" }}>Next →</button>
            </div>

            {/* Summary KPIs */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10, marginBottom: 16 }}>
              {[
                { label: "Total Hours", value: totalPayrollHours.toFixed(1) + "h", color: "#70C0A8" },
                { label: "Total Pay", value: fmtUSD(totalPayrollPay), color: "#10B981" },
                { label: "Employees", value: payrollData.filter(e => e.totalHours > 0).length, color: "#3B82F6" },
                { label: "QB Synced", value: payrollData.filter(e => e.synced && e.totalHours > 0).length + "/" + payrollData.filter(e => e.totalHours > 0).length, color: "#8B5CF6" },
              ].map(k => (
                <div key={k.label} style={{ background: "#FFFFFF", borderRadius: 12, padding: "12px", border: "1px solid #E5E7EB", textAlign: "center" }}>
                  <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 22, fontWeight: 700, color: k.color }}>{k.value}</div>
                  <div style={{ color: "#64748B", fontSize: 11 }}>{k.label}</div>
                </div>
              ))}
            </div>

            {/* Per-employee rows */}
            {payrollData.map(emp => (
              <div key={emp.id} style={{ ...card, opacity: emp.totalHours === 0 ? 0.5 : 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: emp.totalHours > 0 ? 10 : 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#E5E7EB", display: "flex", alignItems: "center", justifyContent: "center", color: "#0F172A", fontWeight: 700, fontSize: 13 }}>{getInitials(emp.name)}</div>
                    <div>
                      <div style={{ color: "#0F172A", fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                        {emp.name}
                        {emp.active === false && <span style={{ background: "#F59E0B22", color: "#F59E0B", borderRadius: 20, padding: "1px 8px", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>{emp.orphan ? "Removed" : "Deactivated"}</span>}
                      </div>
                      <div style={{ color: "#64748B", fontSize: 11 }}>{emp.days} day{emp.days !== 1 ? "s" : ""} worked</div>
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 22, fontWeight: 700, color: "#70C0A8" }}>{emp.totalHours.toFixed(1)}h</div>
                    {emp.totalHours > 0 && (
                      <div style={{ color: emp.rate > 0 ? "#10B981" : "#94A3B8", fontSize: 13, fontWeight: 700, marginTop: 2 }}>
                        {emp.rate > 0 ? `${fmtUSD(emp.pay)} @ $${emp.rate.toFixed(2)}/hr` : "rate not set"}
                      </div>
                    )}
                    <div style={{ color: emp.synced ? "#8B5CF6" : "#F59E0B", fontSize: 11, fontWeight: 600 }}>{emp.synced ? "✓ QB Synced" : "⏳ Not synced"}</div>
                  </div>
                </div>
                {emp.totalHours > 0 && (
                  <div style={{ borderTop: "1px solid #E5E7EB", paddingTop: 8, marginTop: 4 }}>
                    {emp.entries.map(e => (
                      <div key={e.id} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: "1px solid #F0FDF4" }}>
                        <div style={{ color: "#64748B", fontSize: 12 }}>{fmtDate(e.clockIn)}</div>
                        <div style={{ color: "#334155", fontSize: 12 }}>{fmt(e.clockIn)} – {fmt(e.clockOut)}</div>
                        <div style={{ color: "#70C0A8", fontSize: 12, fontWeight: 600 }}>{(e.hours||0).toFixed(1)}h</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* ── Tips (Square), split 50/50 lead & apprentice ── */}
            <div style={{ marginTop: 8, marginBottom: 4, color: "#334155", fontSize: 13, fontWeight: 700, letterSpacing: 0.3, display: "flex", alignItems: "center", gap: 8 }}>
              💵 Tips (Square) — split 50/50 lead &amp; apprentice
            </div>
            {tips.loading && <div style={{ ...card, color: "#64748B", textAlign: "center" }}>Loading tips from Square…</div>}
            {!tips.loading && tips.error && <div style={{ ...card, color: "#B45309", fontSize: 13 }}>Couldn't load Square tips right now. They'll appear once Square is reachable.</div>}
            {!tips.loading && !tips.error && tips.appts && tips.appts.length === 0 && tips.unmatched.length === 0 && (
              <div style={{ ...card, color: "#64748B", textAlign: "center", fontSize: 13 }}>No card-reader tips recorded in Square for this week.</div>
            )}
            {!tips.loading && !tips.error && tips.perEmp && tips.perEmp.length > 0 && (
              <div style={card}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
                  <div style={{ color: "#0F172A", fontWeight: 700, fontSize: 13 }}>Tips by employee</div>
                  <div style={{ color: "#10B981", fontWeight: 700, fontSize: 13 }}>{fmtUSD(tips.matchedTotal)} matched</div>
                </div>
                {tips.perEmp.map(p => (
                  <div key={p.id} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderTop: "1px solid #F1F5F9" }}>
                    <div style={{ color: "#334155", fontSize: 13, fontWeight: 600 }}>{p.name}</div>
                    <div style={{ color: "#059669", fontSize: 13, fontWeight: 700 }}>{fmtUSD(p.amount)}</div>
                  </div>
                ))}
              </div>
            )}
            {!tips.loading && !tips.error && tips.appts && tips.appts.length > 0 && (
              <div style={card}>
                <div style={{ color: "#0F172A", fontWeight: 700, fontSize: 13, marginBottom: 8 }}>By appointment</div>
                {tips.appts.map((r, i) => (
                  <div key={i} style={{ padding: "8px 0", borderTop: i ? "1px solid #F1F5F9" : "none" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                      <div style={{ color: "#334155", fontSize: 13, fontWeight: 700 }}>{r.customer}</div>
                      <div style={{ color: "#10B981", fontSize: 13, fontWeight: 700 }}>{fmtUSD(r.tip)}</div>
                    </div>
                    <div style={{ color: "#64748B", fontSize: 11, marginTop: 2 }}>{fmtDate(r.date + "T00:00:00")}</div>
                    {r.noCrew ? (
                      <div style={{ color: "#B45309", fontSize: 12, marginTop: 3, fontWeight: 600 }}>⚠ No crew assigned that day — tip not credited. Build the crew in Assign to split it.</div>
                    ) : (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
                        {r.leadName && <span style={{ background: "#0EA5E914", color: "#0369A1", border: "1px solid #0EA5E944", borderRadius: 20, padding: "2px 9px", fontSize: 11, fontWeight: 700 }}>{r.leadName} (lead) {fmtUSD(r.leadAmt)}</span>}
                        {r.helpers.map((h, j) => <span key={j} style={{ background: "#F59E0B18", color: "#B45309", border: "1px solid #F59E0B44", borderRadius: 20, padding: "2px 9px", fontSize: 11, fontWeight: 700 }}>{h.name} {fmtUSD(h.amt)}</span>)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            {!tips.loading && !tips.error && tips.unmatched && tips.unmatched.length > 0 && (
              <div style={{ ...card, background: "#FFFBEB", border: "1px solid #F59E0B44" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
                  <div style={{ color: "#92400E", fontWeight: 700, fontSize: 13 }}>⚠ Unmatched tips</div>
                  <div style={{ color: "#92400E", fontWeight: 700, fontSize: 13 }}>{fmtUSD(tips.unmatchedTotal)}</div>
                </div>
                <div style={{ color: "#92400E", fontSize: 11, marginBottom: 6 }}>These Square tips couldn't be matched to an appointment (no linked customer, or the name/email didn't match a booking that day), so they aren't split yet.</div>
                {tips.unmatched.map((u, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", fontSize: 12, color: "#92400E" }}>
                    <span>{fmtDate(u.date + "T00:00:00")} · {u.name}</span><span style={{ fontWeight: 700 }}>{fmtUSD(u.tip)}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── DAILY ATTENDANCE ── */}
        {reportType === "daily" && (
          <>
            <div style={{ marginBottom: 16 }}>
              <div style={{ color: "#334155", fontSize: 12, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 8 }}>Select Date</div>
              <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} max={localDateISO()}
                style={{ width: "100%", padding: "10px 14px", borderRadius: 10, background: "#FFFFFF", border: "1px solid #E5E7EB", color: "#0F172A", fontSize: 14, fontFamily: "inherit", boxSizing: "border-box" }} />
            </div>

            {/* Summary */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 10, marginBottom: 16 }}>
              {[
                { label: "Showed Up", value: [...new Set(dayEntries.map(e => e.userId))].length, color: "#70C0A8" },
                { label: "Total Hours", value: dayEntries.reduce((s,e) => s+(e.hours||0), 0).toFixed(1) + "h", color: "#3B82F6" },
                { label: "No Show", value: employees.length - [...new Set(dayEntries.map(e => e.userId))].length, color: "#EF4444" },
              ].map(k => (
                <div key={k.label} style={{ background: "#FFFFFF", borderRadius: 12, padding: "12px", border: "1px solid #E5E7EB", textAlign: "center" }}>
                  <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 22, fontWeight: 700, color: k.color }}>{k.value}</div>
                  <div style={{ color: "#64748B", fontSize: 11 }}>{k.label}</div>
                </div>
              ))}
            </div>

            {dayEntries.length === 0 && (
              <div style={{ color: "#64748B", textAlign: "center", padding: 32, background: "#FFFFFF", borderRadius: 14, border: "1px solid #E5E7EB" }}>No entries for this date.</div>
            )}

            {dayEntries.map(e => (
              <div key={e.id} style={card}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#E5E7EB", display: "flex", alignItems: "center", justifyContent: "center", color: "#0F172A", fontWeight: 700, fontSize: 13 }}>{getInitials(e.userName)}</div>
                    <div style={{ color: "#0F172A", fontWeight: 600 }}>{e.userName}</div>
                  </div>
                  <span style={{ background: statusColor[e.status] + "22", color: statusColor[e.status], border: `1px solid ${statusColor[e.status]}44`, borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700, textTransform: "uppercase" }}>{e.status}</span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8 }}>
                  <div style={{ background: "#F7F8FA", borderRadius: 10, padding: "10px 12px" }}>
                    <div style={label}>Clock In</div>
                    <div style={val}>{fmt(e.clockIn)}</div>
                    {e.locationIn && <div style={{ color: "#64748B", fontSize: 10, marginTop: 2 }}>📍 {e.locationIn.label}</div>}
                  </div>
                  <div style={{ background: "#F7F8FA", borderRadius: 10, padding: "10px 12px" }}>
                    <div style={label}>Clock Out</div>
                    <div style={val}>{fmt(e.clockOut)}</div>
                    {e.locationOut && <div style={{ color: "#64748B", fontSize: 10, marginTop: 2 }}>📍 {e.locationOut.label}</div>}
                  </div>
                  <div style={{ background: "#F7F8FA", borderRadius: 10, padding: "10px 12px" }}>
                    <div style={label}>Hours</div>
                    <div style={{ color: "#0D9488", fontWeight: 800, fontSize: 14 }}>{(e.hours||0).toFixed(2)}h</div>
                  </div>
                </div>
              </div>
            ))}

            {/* No-show list */}
            {employees.filter(emp => !dayEntries.find(e => e.userId === emp.id)).length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div style={{ color: "#64748B", fontSize: 12, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 10 }}>Not Clocked In</div>
                {employees.filter(emp => !dayEntries.find(e => e.userId === emp.id)).map(emp => (
                  <div key={emp.id} style={{ ...card, display: "flex", alignItems: "center", gap: 10, opacity: 0.6 }}>
                    <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#E5E7EB", display: "flex", alignItems: "center", justifyContent: "center", color: "#0F172A", fontWeight: 700, fontSize: 12 }}>{getInitials(emp.name)}</div>
                    <div style={{ color: "#64748B", fontWeight: 600 }}>{emp.name}</div>
                    <span style={{ marginLeft: "auto", color: "#EF4444", fontSize: 11, fontWeight: 700 }}>No record</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── MONTHLY OVERVIEW ── */}
        {reportType === "monthly" && (
          <>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <button onClick={() => setMonthOffset(m => m - 1)} style={{ background: "#FFFFFF", border: "1px solid #E5E7EB", color: "#334155", borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontFamily: "inherit" }}>← Prev</button>
              <div style={{ textAlign: "center" }}>
                <div style={{ color: "#0F172A", fontWeight: 700, fontSize: 16, fontFamily: "'Space Grotesk', sans-serif" }}>{fmtMonthLabel(monthOffset)}</div>
                <div style={{ color: "#64748B", fontSize: 11 }}>{monthEntries.length} approved entries</div>
              </div>
              <button onClick={() => setMonthOffset(m => Math.min(0, m + 1))} disabled={monthOffset === 0} style={{ background: "#FFFFFF", border: "1px solid #E5E7EB", color: monthOffset === 0 ? "#CBD5E1" : "#334155", borderRadius: 8, padding: "6px 12px", cursor: monthOffset === 0 ? "default" : "pointer", fontFamily: "inherit" }}>Next →</button>
            </div>

            {/* Team total */}
            <div style={{ background: "linear-gradient(135deg, #F0FDF4, #ECFDF5)", borderRadius: 16, padding: "20px", border: "1px solid #70C0A833", marginBottom: 16, textAlign: "center" }}>
              <div style={{ color: "#64748B", fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Team Total</div>
              <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 48, fontWeight: 700, color: "#70C0A8", lineHeight: 1.1 }}>{monthEntries.reduce((s,e)=>s+(e.hours||0),0).toFixed(0)}h</div>
              <div style={{ color: "#64748B", fontSize: 13, marginTop: 4 }}>{[...new Set(monthEntries.map(e=>e.userId))].length} employees · {[...new Set(monthEntries.map(e=>fmtDateISO(e.clockIn)))].length} working days</div>
            </div>

            {/* Per employee monthly card */}
            {monthlyData.map(emp => {
              const maxHours = Math.max(...monthlyData.map(e => e.totalHours), 1);
              return (
                <div key={emp.id} style={card}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#E5E7EB", display: "flex", alignItems: "center", justifyContent: "center", color: "#0F172A", fontWeight: 700, fontSize: 13 }}>{getInitials(emp.name)}</div>
                      <div>
                        <div style={{ color: "#0F172A", fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                          {emp.name}
                          {emp.active === false && <span style={{ background: "#F59E0B22", color: "#F59E0B", borderRadius: 20, padding: "1px 8px", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>{emp.orphan ? "Removed" : "Deactivated"}</span>}
                        </div>
                        <div style={{ color: "#64748B", fontSize: 11 }}>{emp.days} days</div>
                      </div>
                    </div>
                    <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 24, fontWeight: 700, color: "#70C0A8" }}>{emp.totalHours.toFixed(1)}h</div>
                  </div>
                  {/* Progress bar */}
                  <div style={{ background: "#F7F8FA", borderRadius: 6, height: 6, marginBottom: 10 }}>
                    <div style={{ background: "linear-gradient(90deg, #70C0A8, #4A9E88)", borderRadius: 6, height: 6, width: `${(emp.totalHours / maxHours) * 100}%`, transition: "width 0.5s" }} />
                  </div>
                  {/* Week breakdown */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 6 }}>
                    {emp.weeks.map((w, i) => (
                      <div key={i} style={{ background: "#F7F8FA", borderRadius: 8, padding: "8px", textAlign: "center" }}>
                        <div style={{ color: "#64748B", fontSize: 10, marginBottom: 2 }}>Wk {i+1}</div>
                        <div style={{ color: w > 0 ? "#334155" : "#CBD5E1", fontWeight: 600, fontSize: 13 }}>{w > 0 ? w.toFixed(1)+"h" : "—"}</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </>
        )}


      </div>
    </div>
  );
}



// ─── EMPLOYEE MANAGEMENT ──────────────────────────────────────────────────────
function EmployeeManagement({ allUsers, entries = [], onSave, onClearEntries, onClose }) {
  const isMobile = useIsMobile();
  const [users, setUsers] = useState(allUsers);
  const [editing, setEditing] = useState(null); // null | 'new' | user object
  const [toast, setToast] = useState(null);
  const [confirmDeactivate, setConfirmDeactivate] = useState(null);
  const [confirmClear, setConfirmClear] = useState(null); // user object whose entries are being cleared
  const [clearing, setClearing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [creds, setCreds] = useState(null); // { name, email, tempPassword, existed } — credentials to hand a member
  const [copied, setCopied] = useState(false);

  const entryCountFor = (userId) => entries.filter(e => e.userId === userId).length;

  // The temp password is derived from the member's stored PIN, so the admin can
  // re-show it anytime without relying on email. (Only valid until the member
  // sets their own password.)
  const tempPasswordFor = (u) => "MintTime" + (u.pin || "") + "!";
  const APP_URL = APP_BASE_URL;
  const shareText = (c) => `Hi ${c.name}, here's your Mint Time login:\n${APP_URL}\nEmail: ${c.email}\nTemp password: ${c.tempPassword}\n\nLog in with that, then you can change your password anytime.`;
  const openCreds = (u) => { setCopied(false); setResetState("idle"); setCreds({ name: u.name, email: u.email, tempPassword: tempPasswordFor(u), existed: true }); };

  // Fire Firebase's built-in password reset email. Useful once a member has set
  // their own password (the temp one no longer applies). Note: delivery depends
  // on the project's email settings, and enumeration protection can make this a
  // silent no-op for an address without a login.
  const [resetState, setResetState] = useState("idle"); // idle | sending | sent | error
  const sendReset = async (email) => {
    setResetState("sending");
    try {
      await sendPasswordResetEmail(auth, email);
      setResetState("sent");
    } catch (e) {
      setResetState("error");
    }
  };

  const handleClearEntriesConfirmed = async () => {
    if (!confirmClear || !onClearEntries) return;
    setClearing(true);
    try {
      const { entries: n } = await onClearEntries(confirmClear.id);
      showToast(`✓ Cleared ${n} entr${n === 1 ? "y" : "ies"} for ${confirmClear.name}`, "#70C0A8");
      setConfirmClear(null);
    } catch (e) {
      showToast("Error clearing entries: " + e.message, "#EF4444");
    }
    setClearing(false);
  };

  const showToast = (msg, color) => { setToast({ msg, color }); setTimeout(() => setToast(null), 2500); };

  const blankEmployee = { id: "", name: "", email: "", role: "employee", pin: "", hourlyRate: "", qbEmployeeId: "", active: true };
  const [form, setForm] = useState(blankEmployee);

  const openNew = () => { setForm({ ...blankEmployee, id: "u" + Date.now() }); setEditing("new"); };
  const openEdit = (u) => { setForm({ ...u }); setEditing(u.id); };

  // Auto-generate a unique 4-digit PIN that doesn't collide with active users.
  // PIN is no longer surfaced in the UI — it's only used to derive a temp password
  // and as a stable per-user code in the data model.
  const generatePin = () => {
    const usedPins = new Set(users.filter(u => u.active !== false).map(u => u.pin));
    for (let i = 0; i < 200; i++) {
      const pin = String(Math.floor(1000 + Math.random() * 9000));
      if (!usedPins.has(pin)) return pin;
    }
    return String(Date.now()).slice(-4);
  };

  const handleSaveForm = async () => {
    if (!form.name.trim()) { showToast("Name is required", "#EF4444"); return; }
    if (!form.email.trim()) { showToast("Email is required", "#EF4444"); return; }

    // For new users, auto-generate a PIN. For edits, preserve the existing one.
    const pin = editing === "new" ? generatePin() : (form.pin || generatePin());

    setSaving(true);
    try {
      const userData = {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        role: form.role,
        pin,
        hourlyRate: parseFloat(form.hourlyRate) || 0,
        qbEmployeeId: form.qbEmployeeId || "",
        active: true,
      };

      if (editing === "new") {
        // Create Firebase Auth account with temp password on a SECONDARY auth
        // instance so the admin's primary session isn't bumped.
        const tempPassword = "MintTime" + pin + "!";
        const secondaryAuth = getSecondaryAuth();
        let existed = false;
        try {
          await createUserWithEmailAndPassword(secondaryAuth, userData.email, tempPassword);
        } catch (authErr) {
          if (authErr.code !== "auth/email-already-in-use") {
            showToast("Could not create login: " + authErr.message, "#EF4444");
            setSaving(false);
            return;
          }
          existed = true; // login already existed — the temp password may not apply
        }
        // Sign out of the secondary instance so it doesn't hold the new user's session
        try { await signOut(secondaryAuth); } catch {}
        // Save to Firestore
        const newId = "u" + Date.now();
        await setDoc(doc(db, "users", newId), userData);
        setUsers(prev => [...prev, { ...userData, id: newId }]);
        setEditing(null);
        // Show a persistent, copyable credentials card so the admin can hand the
        // login to the new member directly — no onboarding email is sent.
        setCopied(false);
        setResetState("idle");
        setCreds({ name: userData.name, email: userData.email, tempPassword, existed });
        setSaving(false);
        return;
      } else {
        // Update Firestore
        await updateDoc(doc(db, "users", form.id), userData);
        setUsers(prev => prev.map(u => u.id === form.id ? { ...userData, id: form.id } : u));
        showToast(`✓ ${form.name} updated`, "#70C0A8");
      }
      setEditing(null);
    } catch (e) {
      showToast("Error saving: " + e.message, "#EF4444");
    }
    setSaving(false);
  };

  const handleDeactivate = async (u) => {
    try {
      await updateDoc(doc(db, "users", u.id), { active: false });
      setUsers(prev => prev.map(p => p.id === u.id ? { ...p, active: false } : p));
      setConfirmDeactivate(null);
      showToast(`${u.name} deactivated — history preserved`, "#F59E0B");
    } catch (e) {
      showToast("Error deactivating: " + e.message, "#EF4444");
    }
  };

  const handleReactivate = async (u) => {
    try {
      await updateDoc(doc(db, "users", u.id), { active: true });
      setUsers(prev => prev.map(p => p.id === u.id ? { ...p, active: true } : p));
      showToast(`✓ ${u.name} reactivated`, "#70C0A8");
    } catch (e) {
      showToast("Error reactivating: " + e.message, "#EF4444");
    }
  };

  const handleDone = () => { onSave(users); onClose(); };

  const active = users.filter(u => u.active !== false);
  const inactive = users.filter(u => u.active === false);
  const roleColor = { admin: "#8B5CF6", manager: "#3B82F6", employee: "#70C0A8", lead: "#0EA5E9", apprentice: "#F59E0B" };

  const inp = (label, key, placeholder, type = "text", hint = "") => (
    <div style={{ marginBottom: 14 }}>
      <div style={{ color: "#334155", fontSize: 12, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
      {hint && <div style={{ color: "#64748B", fontSize: 11, marginBottom: 4 }}>{hint}</div>}
      <input
        type={type}
        value={form[key] || ""}
        onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
        placeholder={placeholder}
        maxLength={key === "pin" ? 4 : undefined}
        style={{ width: "100%", padding: "10px 14px", borderRadius: 10, background: "#F7F8FA", border: "1px solid #E5E7EB", color: "#0F172A", fontSize: 14, fontFamily: "inherit", boxSizing: "border-box" }}
      />
    </div>
  );

  return (
    <div style={{ position: "fixed", inset: 0, background: PAGE_BG, zIndex: 600, overflowY: "auto", overflowX: "hidden", fontFamily: "'DM Sans', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Space+Grotesk:wght@700&display=swap" rel="stylesheet" />
      {toast && <div style={{ position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)", background: toast.color, color: "#fff", padding: "12px 24px", borderRadius: 12, fontWeight: 700, fontSize: 14, zIndex: 9999, boxShadow: "0 4px 20px rgba(0,0,0,0.6)", fontFamily: "inherit", whiteSpace: "nowrap" }}>{toast.msg}</div>}

      {/* Confirm clear-entries modal */}
      {confirmClear && (
        <div style={{ position: "fixed", inset: 0, background: "#0F172A80", zIndex: 700, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div style={{ background: "#FFFFFF", borderRadius: 20, padding: 24, maxWidth: 360, width: "100%", border: "1px solid #E5E7EB" }}>
            <div style={{ color: "#0F172A", fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Clear all time entries for {confirmClear.name}?</div>
            <div style={{ color: "#64748B", fontSize: 13, marginBottom: 20 }}>
              This permanently deletes <b>{entryCountFor(confirmClear.id)}</b> time entr{entryCountFor(confirmClear.id) === 1 ? "y" : "ies"}. The user account is kept. <span style={{ color: "#EF4444", fontWeight: 600 }}>This cannot be undone.</span>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={handleClearEntriesConfirmed} disabled={clearing} style={{ flex: 1, padding: "11px", borderRadius: 10, background: clearing ? "#E5E7EB" : "#EF4444", border: "none", color: clearing ? "#94A3B8" : "#fff", fontWeight: 700, fontSize: 13, cursor: clearing ? "default" : "pointer", fontFamily: "inherit" }}>{clearing ? "Clearing..." : "Delete Entries"}</button>
              <button onClick={() => setConfirmClear(null)} disabled={clearing} style={{ flex: 1, padding: "11px", borderRadius: 10, background: "#F7F8FA", border: "1px solid #E5E7EB", color: "#64748B", fontWeight: 700, fontSize: 13, cursor: clearing ? "default" : "pointer", fontFamily: "inherit" }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm deactivate modal */}
      {confirmDeactivate && (
        <div style={{ position: "fixed", inset: 0, background: "#0F172A80", zIndex: 700, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div style={{ background: "#FFFFFF", borderRadius: 20, padding: 24, maxWidth: 340, width: "100%", border: "1px solid #E5E7EB" }}>
            <div style={{ color: "#0F172A", fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Deactivate {confirmDeactivate.name}?</div>
            <div style={{ color: "#64748B", fontSize: 13, marginBottom: 20 }}>They won't be able to log in. All their time history is preserved. You can reactivate them anytime.</div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => handleDeactivate(confirmDeactivate)} style={{ flex: 1, padding: "11px", borderRadius: 10, background: "#EF4444", border: "none", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>Deactivate</button>
              <button onClick={() => setConfirmDeactivate(null)} style={{ flex: 1, padding: "11px", borderRadius: 10, background: "#F7F8FA", border: "1px solid #E5E7EB", color: "#64748B", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Credentials card — shown after adding a member, or via "Login info".
          The app doesn't email members; the admin hands them this. */}
      {creds && (
        <div style={{ position: "fixed", inset: 0, background: "#0F172A80", zIndex: 720, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div style={{ background: "#FFFFFF", borderRadius: 20, padding: 24, maxWidth: 400, width: "100%", border: "1px solid #E5E7EB" }}>
            <div style={{ color: "#0F172A", fontWeight: 700, fontSize: 17, marginBottom: 4 }}>{creds.name}'s login</div>
            <div style={{ color: "#64748B", fontSize: 13, marginBottom: 16 }}>Send these to {creds.name.split(" ")[0]} — the app doesn't email it automatically.</div>

            {creds.existed && (
              <div style={{ background: "#FFFBEB", border: "1px solid #F59E0B44", borderRadius: 10, padding: "8px 12px", marginBottom: 14, color: "#92400E", fontSize: 12, fontWeight: 600 }}>
                A login for this email already existed. If they've set their own password, this temp one won't work — have them use “Forgot password?” instead.
              </div>
            )}

            <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 12, padding: 14, marginBottom: 16 }}>
              {[["Site", APP_URL], ["Email", creds.email], ["Temp password", creds.tempPassword]].map(([k, v]) => (
                <div key={k} style={{ marginBottom: k === "Temp password" ? 0 : 10 }}>
                  <div style={{ color: "#94A3B8", fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase" }}>{k}</div>
                  <div style={{ color: "#0F172A", fontSize: 14, fontWeight: 700, fontFamily: k === "Site" ? "inherit" : "monospace", wordBreak: "break-all" }}>{v}</div>
                </div>
              ))}
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={async () => { try { await navigator.clipboard.writeText(shareText(creds)); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { setCopied(false); } }}
                style={{ flex: 1, padding: "11px", borderRadius: 10, background: copied ? "#059669" : "#10B981", border: "none", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
                {copied ? "✓ Copied" : "Copy message"}
              </button>
              <button onClick={() => setCreds(null)} style={{ flex: 1, padding: "11px", borderRadius: 10, background: "#F7F8FA", border: "1px solid #E5E7EB", color: "#64748B", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>Done</button>
            </div>

            {/* Backup: let Firebase email a reset link (for members who've already
                changed their password, or if you'd rather they set their own). */}
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #F1F5F9", textAlign: "center" }}>
              <button onClick={() => sendReset(creds.email)} disabled={resetState === "sending"}
                style={{ background: "none", border: "none", color: resetState === "error" ? "#EF4444" : "#0369A1", fontSize: 13, fontWeight: 700, cursor: resetState === "sending" ? "default" : "pointer", fontFamily: "inherit", textDecoration: "underline" }}>
                {resetState === "sending" ? "Sending…" : resetState === "sent" ? "✓ Reset email sent" : resetState === "error" ? "Couldn't send — tap to retry" : "Or email a password-reset link"}
              </button>
              {resetState === "sent" && <div style={{ color: "#64748B", fontSize: 11, marginTop: 4 }}>Tell them to check spam if it doesn't arrive.</div>}
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit form */}
      {editing !== null && (
        <div style={{ position: "fixed", inset: 0, background: "#0F172A80", zIndex: 700, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
          <div style={{ background: "#FFFFFF", borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 480, maxHeight: "90vh", overflowY: "auto", padding: 24, border: "1px solid #E5E7EB" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 18, fontWeight: 700, color: "#0F172A" }}>{editing === "new" ? "Add Employee" : "Edit Employee"}</div>
              <button onClick={() => setEditing(null)} style={{ background: "none", border: "1px solid #E5E7EB", color: "#64748B", borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontSize: 12, fontFamily: "inherit" }}>✕</button>
            </div>

            {inp("Full Name", "name", "e.g. Jake Torres")}
            {inp("Email", "email", "e.g. jake@mintmobile.com", "email")}

            <div style={{ marginBottom: 14 }}>
              <div style={{ color: "#334155", fontSize: 12, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 4 }}>Role</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {["employee", "lead", "apprentice", "manager", "admin"].map(r => (
                  <button key={r} onClick={() => setForm(p => ({ ...p, role: r }))}
                    style={{ flex: "1 1 28%", padding: "10px 6px", borderRadius: 10, border: `2px solid ${form.role === r ? roleColor[r] : "#E5E7EB"}`, background: form.role === r ? roleColor[r] + "22" : "#F7F8FA", color: form.role === r ? roleColor[r] : "#64748B", fontWeight: 700, fontSize: 12, cursor: "pointer", fontFamily: "inherit", textTransform: "capitalize" }}>
                    {r}
                  </button>
                ))}
              </div>
            </div>

            {inp("Hourly Rate ($)", "hourlyRate", "e.g. 18.50", "number", "Used in the Payroll report to calculate weekly pay")}
            {inp("QBO Employee ID", "qbEmployeeId", "From QuickBooks Payroll (optional)", "text", "Find in QBO → Payroll → Employees → click employee → copy ID from URL")}

            <button onClick={handleSaveForm} disabled={saving}
              style={{ width: "100%", padding: "14px", borderRadius: 12, background: saving ? "#E5E7EB" : "linear-gradient(135deg, #10B981, #059669)", border: "none", color: saving ? "#64748B" : "#fff", fontWeight: 700, fontSize: 15, cursor: saving ? "default" : "pointer", fontFamily: "inherit", marginTop: 4 }}>
              {saving ? "Saving..." : editing === "new" ? "Add Employee" : "Save Changes"}
            </button>

            {/* Danger zone — clearing time entries lives here so it takes a
                deliberate two steps (open Edit → Clear → confirm) and can't be
                hit by accident from the list. */}
            {editing !== "new" && entryCountFor(form.id) > 0 && (
              <div style={{ marginTop: 24, paddingTop: 16, borderTop: "1px solid #F1F5F9" }}>
                <div style={{ color: "#94A3B8", fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 8 }}>Danger Zone</div>
                <button onClick={() => { setConfirmClear({ id: form.id, name: form.name }); setEditing(null); }}
                  style={{ width: "100%", padding: "11px", borderRadius: 10, background: "#FFFFFF", border: "1px solid #EF444455", color: "#EF4444", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
                  Clear all time entries ({entryCountFor(form.id)})
                </button>
                <div style={{ color: "#94A3B8", fontSize: 11, marginTop: 6, lineHeight: 1.4 }}>
                  Permanently deletes this person's clock in/out records. The account stays. You'll be asked to confirm.
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Header */}
      <div style={headerBarSticky}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <img src={MINT_LOGO} alt="Mint" style={{ width: 64, height: 64, display: "block", flexShrink: 0 }} />
          <div style={headerTitle}>Team</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={openNew} style={{ background: "#FFFFFF", border: "none", color: "#059669", borderRadius: 9, padding: "7px 14px", cursor: "pointer", fontSize: 12, fontWeight: 700, fontFamily: "inherit" }}>+ Add</button>
          <button onClick={handleDone} style={glassBtn}>✓ Done</button>
        </div>
      </div>

      <div style={{ padding: "20px", maxWidth: isMobile ? 600 : 880, margin: "0 auto" }}>

        {/* Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 10, marginBottom: 20 }}>
          {[
            { label: "Total Active", value: active.length, color: "#70C0A8" },
            { label: "Managers", value: active.filter(u => u.role === "manager" || u.role === "admin").length, color: "#8B5CF6" },
            { label: "Inactive", value: inactive.length, color: "#F59E0B" },
          ].map(k => (
            <div key={k.label} style={{ background: "#FFFFFF", borderRadius: 12, padding: "12px", border: "1px solid #E5E7EB", textAlign: "center" }}>
              <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 24, fontWeight: 700, color: k.color }}>{k.value}</div>
              <div style={{ color: "#64748B", fontSize: 11 }}>{k.label}</div>
            </div>
          ))}
        </div>

        {/* Active employees */}
        <div style={{ color: "#64748B", fontSize: 12, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 12 }}>Active Employees</div>
        {active.map(u => (
          <div key={u.id} style={{ background: "#FFFFFF", borderRadius: 14, padding: "14px 16px", border: "1px solid #E5E7EB", marginBottom: 10, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <div style={{ width: 44, height: 44, borderRadius: "50%", background: roleColor[u.role] + "22", border: `2px solid ${roleColor[u.role]}44`, display: "flex", alignItems: "center", justifyContent: "center", color: roleColor[u.role], fontWeight: 700, fontSize: 15, flexShrink: 0 }}>
              {getInitials(u.name)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: "#0F172A", fontWeight: 600, fontSize: 15 }}>{u.name}</div>
              <div style={{ color: "#64748B", fontSize: 12 }}>{u.email || "no email"}</div>
              <div style={{ display: "flex", gap: 8, marginTop: 4, flexWrap: "wrap" }}>
                <span style={{ background: roleColor[u.role] + "22", color: roleColor[u.role], borderRadius: 20, padding: "1px 8px", fontSize: 10, fontWeight: 700, textTransform: "capitalize" }}>{u.role}</span>
                {u.hourlyRate > 0 && <span style={{ background: "#10B98122", color: "#10B981", borderRadius: 20, padding: "1px 8px", fontSize: 10, fontWeight: 600 }}>${Number(u.hourlyRate).toFixed(2)}/hr</span>}
                {u.qbEmployeeId && <span style={{ background: "#8B5CF622", color: "#8B5CF6", borderRadius: 20, padding: "1px 8px", fontSize: 10, fontWeight: 600 }}>QB: {u.qbEmployeeId}</span>}
              </div>
            </div>
            <div style={{ display: "flex", gap: 6, flexShrink: 0, flexWrap: "wrap", justifyContent: isMobile ? "flex-start" : "flex-end", ...(isMobile ? { flexBasis: "100%", marginTop: 8 } : {}) }}>
              <button onClick={() => openEdit(u)} style={{ padding: "7px 12px", borderRadius: 8, background: "#F7F8FA", border: "1px solid #E5E7EB", color: "#334155", fontSize: 12, cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}>Edit</button>
              {u.pin && <button onClick={() => openCreds(u)} style={{ padding: "7px 12px", borderRadius: 8, background: "#F7F8FA", border: "1px solid #10B98144", color: "#059669", fontSize: 12, cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}>Login info</button>}
              {u.id !== "1" && <button onClick={() => setConfirmDeactivate(u)} style={{ padding: "7px 12px", borderRadius: 8, background: "#F7F8FA", border: "1px solid #EF444444", color: "#EF4444", fontSize: 12, cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}>Remove</button>}
            </div>
          </div>
        ))}

        {/* Inactive employees */}
        {inactive.length > 0 && (
          <>
            <div style={{ color: "#64748B", fontSize: 12, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 12, marginTop: 24 }}>Inactive — History Preserved</div>
            {inactive.map(u => (
              <div key={u.id} style={{ background: "#FFFFFF", borderRadius: 14, padding: "14px 16px", border: "1px solid #E5E7EB", marginBottom: 10, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", opacity: 0.6 }}>
                {/* inactive row */}
                <div style={{ width: 44, height: 44, borderRadius: "50%", background: "#E5E7EB", display: "flex", alignItems: "center", justifyContent: "center", color: "#64748B", fontWeight: 700, fontSize: 15, flexShrink: 0 }}>
                  {getInitials(u.name)}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ color: "#64748B", fontWeight: 600 }}>{u.name}</div>
                  <div style={{ color: "#64748B", fontSize: 12 }}>Deactivated</div>
                </div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0, flexWrap: "wrap", justifyContent: isMobile ? "flex-start" : "flex-end", ...(isMobile ? { flexBasis: "100%", marginTop: 8 } : {}) }}>
                  <button onClick={() => handleReactivate(u)} style={{ padding: "7px 12px", borderRadius: 8, background: "#F7F8FA", border: "1px solid #70C0A844", color: "#70C0A8", fontSize: 12, cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}>Reactivate</button>
                </div>
              </div>
            ))}
          </>
        )}

        {/* Orphan entries — userIds in timeEntries that don't match any user */}
        {(() => {
          const allUserIds = new Set(allUsers.map(u => u.id));
          const orphans = [...new Map(
            entries
              .filter(e => !allUserIds.has(e.userId))
              .map(e => [e.userId, { id: e.userId, name: e.userName || "Unknown user" }])
          ).values()];
          if (orphans.length === 0) return null;
          return (
            <>
              <div style={{ color: "#64748B", fontSize: 12, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 12, marginTop: 24 }}>Orphan Entries — User Removed</div>
              <div style={{ color: "#64748B", fontSize: 11, marginBottom: 10 }}>These time entries reference user IDs that no longer exist in the users collection. Clearing them removes only the entries.</div>
              {orphans.map(o => (
                <div key={o.id} style={{ background: "#FFFFFF", borderRadius: 14, padding: "14px 16px", border: "1px dashed #F59E0B44", marginBottom: 10, display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 44, height: 44, borderRadius: "50%", background: "#F59E0B22", display: "flex", alignItems: "center", justifyContent: "center", color: "#F59E0B", fontWeight: 700, fontSize: 15, flexShrink: 0 }}>
                    {getInitials(o.name)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: "#0F172A", fontWeight: 600, fontSize: 15 }}>{o.name}</div>
                    <div style={{ color: "#64748B", fontSize: 11, fontFamily: "monospace" }}>id: {o.id}</div>
                  </div>
                  <button onClick={() => setConfirmClear(o)} style={{ padding: "7px 12px", borderRadius: 8, background: "#F7F8FA", border: "1px solid #F59E0B44", color: "#F59E0B", fontSize: 12, cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}>Clear ({entryCountFor(o.id)})</button>
                </div>
              ))}
            </>
          );
        })()}
      </div>
    </div>
  );
}

// ─── DAILY ASSIGNMENTS (admin) ────────────────────────────────────────────────
// Pick a date, pull that day's appointments, and build flexible crews: each crew
// is a lead (role "lead") with 0+ apprentices (role "apprentice"); appointments
// are assigned to the lead's crew. Saved as assignments/{YYYY-MM-DD} in Firestore.
function AssignmentsView({ allUsers, user, onClose }) {
  const isMobile = useIsMobile();
  const today = localDateISO();
  const [dateISO, setDateISO] = useState(today);
  const [appointments, setAppointments] = useState([]);
  const [membership, setMembership] = useState({}); // emailLower -> [{name,value}] from Square
  const [crews, setCrews] = useState([]); // [{ leadId, helpers:[{id,apptIds}], appointmentIds:[] }]
  const [loading, setLoading] = useState(true);
  const [autoState, setAutoState] = useState("idle"); // idle | saving | saved | error
  const [loadError, setLoadError] = useState("");
  const [addLeadId, setAddLeadId] = useState("");
  const [drag, setDrag] = useState(null);         // { kind: "helper" | "appointment", id }
  const [dragOver, setDragOver] = useState(null); // leadId | "pool" | null
  const [slotEditor, setSlotEditor] = useState(null); // { leadId, personId } — splitting a helper across time slots
  const skipAutoSave = useRef(true); // don't auto-save the crews we just loaded

  const leads = allUsers.filter(u => u.active !== false && u.role === "lead");
  const apprentices = allUsers.filter(u => u.active !== false && u.role === "apprentice");
  const userById = (id) => allUsers.find(u => u.id === id);

  const shiftDate = (iso, days) => { const d = new Date(iso + "T00:00:00"); d.setDate(d.getDate() + days); return localDateISO(d); };
  const dateLabel = new Date(dateISO + "T00:00:00").toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" });
  const tomorrow = shiftDate(today, 1);

  // Load appointments + saved crews whenever the date changes. These are kept
  // independent on purpose: appointments come from the (mock/API) source, while
  // saved crews come from Firestore. If the Firestore read is blocked (e.g. the
  // `assignments` collection isn't covered by security rules yet), we still show
  // the appointments and line up every lead — a blocked read just means "no
  // saved data yet", not a blank screen.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError("");
    (async () => {
      let appts = [];
      try { appts = await fetchAppointments(dateISO); } catch (e) { /* source unavailable */ }

      let savedCrews = [];
      try {
        const snap = await getDoc(doc(db, "assignments", dateISO));
        if (snap.exists()) savedCrews = snap.data().crews || [];
      } catch (e) {
        if (!cancelled) setLoadError("Couldn't read saved assignments (check Firestore rules for the 'assignments' collection). You can still build and the screen will try to save.");
      }

      if (cancelled) return;
      setAppointments(appts);
      // Square membership (async — cards fill in when it arrives).
      fetchMembership(appts.map(a => a.email)).then(m => { if (!cancelled) setMembership(m); });
      // Normalize saved crews to the helpers shape (migrating the legacy
      // apprenticeIds → helpers, each whole-day by default).
      const normalize = (c) => ({
        leadId: c.leadId,
        appointmentIds: c.appointmentIds || [],
        helpers: c.helpers
          ? c.helpers.map(h => ({ id: h.id, apptIds: h.apptIds || [] }))
          : (c.apprenticeIds || []).map(id => ({ id, apptIds: [] })),
      });
      // Always line up every current lead as a crew box — EXCEPT a lead who's
      // saved as a helper in someone else's crew (they're pairing up that day).
      const savedByLead = new Map(savedCrews.map(c => [c.leadId, c]));
      const savedHelperIds = new Set(savedCrews.flatMap(c => (c.helpers || []).map(h => h.id).concat(c.apprenticeIds || [])));
      const merged = allUsers
        .filter(u => u.active !== false && u.role === "lead" && !savedHelperIds.has(u.id))
        .map(l => normalize(savedByLead.get(l.id) || { leadId: l.id }));
      skipAutoSave.current = true; // this setCrews is the load, not a user edit
      setCrews(merged);
      setLoading(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateISO]);

  const assignedApptIds = new Set(crews.flatMap(c => c.appointmentIds || []));
  const assignedHelperIds = new Set(crews.flatMap(c => (c.helpers || []).map(h => h.id)));
  const unassignedAppts = appointments.filter(a => !assignedApptIds.has(a.id));
  // A lead is "available" (re-addable as a crew, or draggable as a helper) when
  // they aren't already running a crew and aren't helping in one.
  const availableLeads = leads.filter(l => !crews.some(c => c.leadId === l.id) && !assignedHelperIds.has(l.id));
  const availableApprentices = apprentices.filter(a => !assignedHelperIds.has(a.id));
  const apptById = (id) => appointments.find(a => a.id === id);

  const addCrew = () => {
    if (!addLeadId) return;
    setCrews(prev => prev.some(c => c.leadId === addLeadId) ? prev : [...prev, { leadId: addLeadId, helpers: [], appointmentIds: [] }]);
    setAddLeadId("");
  };
  const removeCrew = (leadId) => setCrews(prev => prev.filter(c => c.leadId !== leadId));

  // Helpers (apprentices OR extra leads) attached to a crew. New helpers cover
  // the whole day (apptIds: []); the slot editor narrows them to time slots.
  const addHelper = (leadId, personId) => { if (!personId) return; setCrews(prev => prev.map(c => c.leadId === leadId ? ((c.helpers || []).some(h => h.id === personId) ? c : { ...c, helpers: [...(c.helpers || []), { id: personId, apptIds: [] }] }) : c)); };
  const removeHelper = (leadId, personId) => setCrews(prev => prev.map(c => c.leadId === leadId ? { ...c, helpers: (c.helpers || []).filter(h => h.id !== personId) } : c));
  const unassignHelper = (personId) => setCrews(prev => prev.map(c => ({ ...c, helpers: (c.helpers || []).filter(h => h.id !== personId) })));
  const moveHelper = (personId, toLeadId) => setCrews(prev => prev.map(c => {
    const without = (c.helpers || []).filter(h => h.id !== personId);
    return c.leadId === toLeadId ? { ...c, helpers: [...without, { id: personId, apptIds: [] }] } : { ...c, helpers: without };
  }));
  // Time-slot split: toggle one appointment for a helper. Empty apptIds = all day.
  const toggleHelperSlot = (leadId, personId, apptId) => setCrews(prev => prev.map(c => c.leadId === leadId ? {
    ...c, helpers: (c.helpers || []).map(h => h.id === personId
      ? { ...h, apptIds: (h.apptIds || []).includes(apptId) ? (h.apptIds || []).filter(x => x !== apptId) : [...(h.apptIds || []), apptId] }
      : h),
  } : c));
  const setHelperAllDay = (leadId, personId) => setCrews(prev => prev.map(c => c.leadId === leadId ? { ...c, helpers: (c.helpers || []).map(h => h.id === personId ? { ...h, apptIds: [] } : h) } : c));
  // Coverage label for a helper chip: "all day" or the covered time slots.
  const coverageLabel = (c, h) => {
    if (!h.apptIds || h.apptIds.length === 0) return "all day";
    const times = h.apptIds.map(id => apptById(id)?.time).filter(Boolean);
    return times.length ? times.join(", ") : "all day";
  };

  const assignAppt = (leadId, apptId) => { if (!apptId) return; setCrews(prev => prev.map(c => c.leadId === leadId ? { ...c, appointmentIds: [...(c.appointmentIds || []), apptId] } : c)); };
  // Unassigning an appointment also drops it from any helper's slot coverage.
  const unassignAppt = (apptId) => setCrews(prev => prev.map(c => ({
    ...c,
    appointmentIds: (c.appointmentIds || []).filter(id => id !== apptId),
    helpers: (c.helpers || []).map(h => ({ ...h, apptIds: (h.apptIds || []).filter(id => id !== apptId) })),
  })));
  const moveAppt = (apptId, toLeadId) => setCrews(prev => prev.map(c => {
    const without = (c.appointmentIds || []).filter(id => id !== apptId);
    // Moving an appt away also clears it from that crew's helper coverage.
    const helpers = (c.helpers || []).map(h => ({ ...h, apptIds: (h.apptIds || []).filter(id => id !== apptId) }));
    return c.leadId === toLeadId ? { ...c, appointmentIds: [...without, apptId], helpers } : { ...c, appointmentIds: without, helpers };
  }));

  // ── Drag & drop: move a helper or appointment to a crew, or back to the pool ──
  const dropOnCrew = (leadId) => {
    if (drag?.kind === "helper") moveHelper(drag.id, leadId);
    else if (drag?.kind === "appointment") moveAppt(drag.id, leadId);
    setDrag(null); setDragOver(null);
  };
  const dropOnPool = () => {
    if (drag?.kind === "helper") unassignHelper(drag.id);
    else if (drag?.kind === "appointment") unassignAppt(drag.id);
    setDrag(null); setDragOver(null);
  };
  const dragHandlers = (kind, id) => ({
    draggable: true,
    onDragStart: (e) => { setDrag({ kind, id }); e.dataTransfer.effectAllowed = "move"; },
    onDragEnd: () => { setDrag(null); setDragOver(null); },
  });

  // Auto-save: persist crews to Firestore a beat after any change, so nothing is
  // lost if you forget to save. The first crews change after a (re)load is the
  // load itself — skipAutoSave skips that so opening the screen doesn't write.
  useEffect(() => {
    if (loading || skipAutoSave.current) { skipAutoSave.current = false; return; }
    setAutoState("saving");
    const t = setTimeout(async () => {
      try {
        await setDoc(doc(db, "assignments", dateISO), {
          date: dateISO,
          // Persist helpers (with time-slot coverage), plus a flat apprenticeIds
          // list so any older client still shows crew members their appointments.
          crews: crews.map(c => ({ ...c, apprenticeIds: (c.helpers || []).map(h => h.id) })),
          updatedAt: Timestamp.now(),
          updatedBy: user?.name || user?.id || "admin",
        });
        setAutoState("saved");
        setLoadError("");
      } catch (e) {
        setAutoState("error");
        setLoadError("Changes can't be saved yet — Firestore rules need to allow the 'assignments' collection (one-time setup). Build now; it'll save once that's added.");
      }
    }, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crews]);

  const card = { background: "#FFFFFF", borderRadius: 14, padding: "14px 16px", border: "1px solid #E5E7EB", marginBottom: 12 };
  const apptRow = (a) => (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
      <div style={{ width: 66, flexShrink: 0, paddingTop: 1 }}>
        <div style={{ color: "#64748B", fontSize: 10, fontWeight: 700, lineHeight: 1.2 }}>{fmtApptDate(a.date)}</div>
        <div style={{ color: "#0EA5E9", fontWeight: 800, fontSize: 12, marginTop: 1 }}>{a.time}</div>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: "#0F172A", fontWeight: 700, fontSize: 13 }}>{a.customer}</div>
        {membershipFor(membership, a.email).length > 0 && (
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 3 }}>
            {membershipFor(membership, a.email).map((m, i) => (
              <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 3, background: "#F5F3FF", border: "1px solid #8B5CF655", color: "#6D28D9", borderRadius: 12, padding: "2px 9px", fontSize: 11, fontWeight: 700, lineHeight: 1.3 }}>⭐ {m.name}{m.value ? `: ${m.value}` : ""}</span>
            ))}
          </div>
        )}
        {a.service && <div style={{ color: "#475569", fontSize: 12, marginTop: 1, lineHeight: 1.35 }}>{a.service}</div>}
        {a.address && (
          <a href={mapsUrl(a)} target="_blank" rel="noopener noreferrer" draggable={false} onClick={e => e.stopPropagation()}
            style={{ display: "flex", gap: 4, alignItems: "flex-start", marginTop: 4, color: "#0D9488", fontSize: 12, fontWeight: 700, lineHeight: 1.35, textDecoration: "none" }}>
            <span style={{ flexShrink: 0 }}>📍</span><span style={{ textDecoration: "underline" }}>{a.address}</span>
          </a>
        )}
        {a.phone && (
          <div style={{ display: "flex", gap: 8, marginTop: 7 }}>
            <a href={`tel:${dialNumber(a.phone)}`} draggable={false} onClick={e => e.stopPropagation()}
              style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#DFF5EE", border: "1px solid #10B98155", color: "#047857", borderRadius: 8, padding: "4px 10px", fontSize: 11, fontWeight: 700, textDecoration: "none" }}>📞 Call</a>
            <a href={`sms:${dialNumber(a.phone)}`} draggable={false} onClick={e => e.stopPropagation()}
              style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#E6F1FB", border: "1px solid #3B82F655", color: "#1D4ED8", borderRadius: 8, padding: "4px 10px", fontSize: 11, fontWeight: 700, textDecoration: "none" }}>💬 Text</a>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div style={{ position: "fixed", inset: 0, background: PAGE_BG, zIndex: 550, overflowY: "auto", overflowX: "hidden", fontFamily: "'DM Sans', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Space+Grotesk:wght@700&display=swap" rel="stylesheet" />
      {/* Header */}
      <div style={headerBarSticky}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}><img src={MINT_LOGO} alt="Mint" style={{ width: 64, height: 64, display: "block", flexShrink: 0 }} /><div style={headerTitle}>Assignments</div></div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: autoState === "error" ? "#FFE4E6" : "rgba(255,255,255,0.95)" }}>
            {autoState === "saving" ? "Saving…" : autoState === "error" ? "⚠ Not saved" : autoState === "saved" ? "✓ Saved" : "Auto-saves"}
          </span>
          <button onClick={onClose} style={glassBtn}>✕ Close</button>
        </div>
      </div>

      <div style={{ padding: "20px", maxWidth: isMobile ? 560 : 980, margin: "0 auto" }}>
        {/* Date navigator */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <button onClick={() => setDateISO(shiftDate(dateISO, -1))} style={{ background: "#FFFFFF", border: "1px solid #E5E7EB", borderRadius: 10, padding: "8px 12px", cursor: "pointer", fontSize: 14, fontFamily: "inherit", color: "#334155" }}>‹</button>
          <div style={{ flex: 1, textAlign: "center" }}>
            <div style={{ color: "#0F172A", fontWeight: 700, fontSize: 15 }}>{dateLabel}{dateISO === today ? " · Today" : dateISO === tomorrow ? " · Tomorrow" : ""}</div>
            <input type="date" value={dateISO} onChange={e => setDateISO(e.target.value)} style={{ marginTop: 4, border: "1px solid #E5E7EB", borderRadius: 8, padding: "4px 8px", fontFamily: "inherit", fontSize: 12, color: "#64748B" }} />
          </div>
          <button onClick={() => setDateISO(shiftDate(dateISO, 1))} style={{ background: "#FFFFFF", border: "1px solid #E5E7EB", borderRadius: 10, padding: "8px 12px", cursor: "pointer", fontSize: 14, fontFamily: "inherit", color: "#334155" }}>›</button>
        </div>

        {/* Quick day jumps — the night-before workflow is "set up tomorrow" */}
        <div style={{ display: "flex", gap: 8, marginBottom: 16, justifyContent: "center" }}>
          {[["Today", today], ["Tomorrow", tomorrow]].map(([label, d]) => {
            const on = dateISO === d;
            return (
              <button key={label} onClick={() => setDateISO(d)}
                style={{ padding: "7px 18px", borderRadius: 999, border: on ? "none" : "1px solid #E5E7EB", background: on ? "linear-gradient(135deg, #10B981, #0D9488)" : "#FFFFFF", color: on ? "#FFFFFF" : "#475569", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", boxShadow: on ? "0 4px 12px rgba(13,148,136,0.35)" : "none" }}>
                {label}
              </button>
            );
          })}
        </div>

        <div style={{ background: "#0EA5E90F", border: "1px solid #0EA5E933", borderRadius: 10, padding: "8px 14px", marginBottom: 16, color: "#0369A1", fontSize: 12, fontWeight: 600 }}>
          {isMobile ? "Tip: use the ＋ dropdowns to add helpers & appointments to a crew. Tap a helper's time to split their day." : "Tip: drag apprentices or a second lead onto a crew (all-day by default). Tap a helper's time chip to split them across time slots. Drag back to the pool to unassign."}
        </div>

        {loadError && (
          <div style={{ background: "#FEF2F2", border: "1px solid #EF444433", borderRadius: 10, padding: "10px 14px", marginBottom: 16, color: "#B91C1C", fontSize: 12, fontWeight: 600 }}>
            ⚠ {loadError}
          </div>
        )}

        {loading ? (
          <div style={{ color: "#64748B", textAlign: "center", padding: 40 }}>Loading appointments…</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "minmax(0, 1fr) minmax(0, 1fr)", gap: 20, alignItems: "start" }}>
            {/* Crews column */}
            <div style={{ minWidth: 0 }}>
              <div style={{ color: "#64748B", fontSize: 12, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 10 }}>Crews</div>

              {/* Add crew */}
              <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                <select value={addLeadId} onChange={e => setAddLeadId(e.target.value)} style={{ flex: 1, padding: "10px 12px", borderRadius: 10, background: "#FFFFFF", border: "1px solid #E5E7EB", color: "#334155", fontSize: 13, fontFamily: "inherit" }}>
                  <option value="">{availableLeads.length ? "Re-add a lead…" : "All leads shown"}</option>
                  {availableLeads.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
                <button onClick={addCrew} disabled={!addLeadId} style={{ background: addLeadId ? "#0EA5E9" : "#E5E7EB", border: "none", color: addLeadId ? "#fff" : "#94A3B8", borderRadius: 10, padding: "0 16px", cursor: addLeadId ? "pointer" : "default", fontSize: 13, fontWeight: 700, fontFamily: "inherit" }}>＋ Crew</button>
              </div>

              {crews.length === 0 && <div style={{ color: "#64748B", textAlign: "center", padding: 24, background: "#FFFFFF", borderRadius: 14, border: "1px dashed #E5E7EB" }}>{leads.length === 0 ? "No leads yet — open Team and set someone's role to Lead." : "All leads are marked out for this day. Re-add one above."}</div>}

              {crews.map(c => {
                const lead = userById(c.leadId);
                const crewAppts = (c.appointmentIds || []).map(apptById).filter(Boolean).sort((a, b) => (a.sortMins ?? 0) - (b.sortMins ?? 0));
                const isOver = dragOver === c.leadId && !!drag;
                return (
                  <div key={c.leadId}
                    onDragOver={(e) => { if (drag) { e.preventDefault(); if (dragOver !== c.leadId) setDragOver(c.leadId); } }}
                    onDragLeave={() => { if (dragOver === c.leadId) setDragOver(null); }}
                    onDrop={() => dropOnCrew(c.leadId)}
                    style={{ ...card, transition: "box-shadow .12s, border-color .12s", ...(isOver ? { borderColor: "#0EA5E9", boxShadow: "0 0 0 3px #0EA5E933", background: "#F0F9FF" } : {}) }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Avatar name={lead?.name || "?"} size={32} color="#0EA5E922" />
                        <div>
                          <div style={{ color: "#0F172A", fontWeight: 700, fontSize: 14 }}>{lead?.name || "Unknown lead"}</div>
                          <div style={{ color: "#0EA5E9", fontSize: 10, fontWeight: 700, textTransform: "uppercase" }}>Lead · {crewAppts.length} appt{crewAppts.length === 1 ? "" : "s"}</div>
                        </div>
                      </div>
                      <button onClick={() => removeCrew(c.leadId)} style={{ background: "none", border: "1px solid #E5E7EB", color: "#EF4444", borderRadius: 8, padding: "4px 10px", cursor: "pointer", fontSize: 11, fontFamily: "inherit", fontWeight: 700 }}>Remove</button>
                    </div>

                    {/* Helpers — apprentices or a second lead. All-day by default;
                        tap the time chip to split across specific time slots. */}
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center", marginBottom: 10 }}>
                      {(c.helpers || []).map(h => {
                        const p = userById(h.id);
                        const isLead = p?.role === "lead";
                        const split = !!(h.apptIds && h.apptIds.length);
                        const editing = slotEditor && slotEditor.leadId === c.leadId && slotEditor.personId === h.id;
                        return (
                          <span key={h.id} {...dragHandlers("helper", h.id)} title="Drag to move, or to the pool to remove"
                            style={{ display: "inline-flex", alignItems: "center", gap: 6, background: isLead ? "#0EA5E914" : "#F59E0B18", color: isLead ? "#0369A1" : "#B45309", border: `1px solid ${isLead ? "#0EA5E944" : "#F59E0B44"}`, borderRadius: 20, padding: "3px 6px 3px 8px", fontSize: 11, fontWeight: 700, cursor: "grab" }}>
                            ⠿ {p?.name || "?"}{isLead ? " ·lead" : ""}
                            <button onClick={() => setSlotEditor(editing ? null : { leadId: c.leadId, personId: h.id })}
                              title="Split across time slots"
                              style={{ display: "inline-flex", alignItems: "center", gap: 2, background: split ? (isLead ? "#0EA5E9" : "#B45309") : "transparent", color: split ? "#fff" : (isLead ? "#0369A1" : "#B45309"), border: `1px solid ${isLead ? "#0EA5E966" : "#B4530966"}`, borderRadius: 12, padding: "1px 7px", fontSize: 10, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                              🕑 {coverageLabel(c, h)} ▾
                            </button>
                            <span onClick={() => removeHelper(c.leadId, h.id)} style={{ cursor: "pointer" }}>✕</span>
                          </span>
                        );
                      })}
                      {(c.helpers || []).length === 0 && <span style={{ color: "#64748B", fontSize: 11, fontStyle: "italic" }}>Drag an apprentice or lead here</span>}
                      {(availableApprentices.length > 0 || availableLeads.length > 0) && (
                        <select value="" onChange={e => addHelper(c.leadId, e.target.value)} style={{ padding: "4px 8px", borderRadius: 8, background: "#F7F8FA", border: "1px dashed #E5E7EB", color: "#64748B", fontSize: 11, fontFamily: "inherit" }}>
                          <option value="">＋ Helper</option>
                          {availableApprentices.length > 0 && <optgroup label="Apprentices">{availableApprentices.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}</optgroup>}
                          {availableLeads.length > 0 && <optgroup label="Pair a lead">{availableLeads.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}</optgroup>}
                        </select>
                      )}
                    </div>

                    {/* Time-slot splitter for the selected helper */}
                    {slotEditor && slotEditor.leadId === c.leadId && (() => {
                      const h = (c.helpers || []).find(x => x.id === slotEditor.personId);
                      if (!h) return null;
                      const allDay = !(h.apptIds && h.apptIds.length);
                      const chip = (on) => ({ display: "inline-flex", alignItems: "center", gap: 6, background: on ? "#0EA5E9" : "#FFFFFF", color: on ? "#fff" : "#334155", border: `1px solid ${on ? "#0EA5E9" : "#E5E7EB"}`, borderRadius: 8, padding: "5px 9px", fontSize: 11, fontWeight: 700, cursor: "pointer", marginRight: 6, marginBottom: 6 });
                      return (
                        <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 10, padding: 10, marginBottom: 10 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: "#334155", marginBottom: 8 }}>
                            {userById(h.id)?.name}: which time slots?
                            <span style={{ color: "#64748B", fontWeight: 600 }}> — pick the appointments they're on, or All day.</span>
                          </div>
                          <div style={{ display: "flex", flexWrap: "wrap" }}>
                            <span onClick={() => setHelperAllDay(c.leadId, h.id)} style={chip(allDay)}>All day</span>
                            {crewAppts.map(a => (
                              <span key={a.id} onClick={() => toggleHelperSlot(c.leadId, h.id, a.id)} style={chip(!allDay && (h.apptIds || []).includes(a.id))}>
                                {a.time} · {a.customer}
                              </span>
                            ))}
                          </div>
                          {crewAppts.length === 0 && <div style={{ color: "#94A3B8", fontSize: 11 }}>Assign appointments to this crew first, then split.</div>}
                          <button onClick={() => setSlotEditor(null)} style={{ marginTop: 4, background: "#0EA5E9", border: "none", color: "#fff", borderRadius: 8, padding: "5px 14px", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Done</button>
                        </div>
                      );
                    })()}

                    {/* Assigned appointments — each shows who's on that slot */}
                    {crewAppts.map(a => {
                      const onSlot = [lead?.name, ...helpersOnAppt(c, a.id).map(id => userById(id)?.name)].filter(Boolean);
                      return (
                        <div key={a.id} {...dragHandlers("appointment", a.id)}
                          style={{ padding: "8px 10px", background: "#F7F8FA", borderRadius: 10, marginBottom: 6, cursor: "grab" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div style={{ flex: 1, minWidth: 0 }}>{apptRow(a)}</div>
                            <button onClick={() => unassignAppt(a.id)} style={{ background: "none", border: "none", color: "#64748B", cursor: "pointer", fontSize: 14 }}>✕</button>
                          </div>
                          <div style={{ marginTop: 6, paddingTop: 6, borderTop: "1px dashed #E2E8F0", color: "#475569", fontSize: 11, fontWeight: 600 }}>
                            👥 {onSlot.join(" · ")}
                          </div>
                        </div>
                      );
                    })}
                    {unassignedAppts.length > 0 && (
                      <select value="" onChange={e => assignAppt(c.leadId, e.target.value)} style={{ width: "100%", marginTop: 4, padding: "8px 10px", borderRadius: 10, background: "#FFFFFF", border: "1px dashed #0EA5E966", color: "#0EA5E9", fontSize: 12, fontFamily: "inherit", fontWeight: 600 }}>
                        <option value="">＋ Assign an appointment…</option>
                        {unassignedAppts.map(a => <option key={a.id} value={a.id}>{a.time} · {a.customer}</option>)}
                      </select>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Pools column — drag items out of here onto a crew; drag back here to unassign */}
            <div
              onDragOver={(e) => { if (drag) { e.preventDefault(); if (dragOver !== "pool") setDragOver("pool"); } }}
              onDragLeave={() => { if (dragOver === "pool") setDragOver(null); }}
              onDrop={dropOnPool}
              style={{ minWidth: 0, borderRadius: 16, padding: dragOver === "pool" && drag ? 8 : 0, outline: dragOver === "pool" && drag ? "2px dashed #94A3B8" : "none", outlineOffset: 2, transition: "padding .1s" }}>

              {/* Apprentice pool */}
              <div style={{ color: "#64748B", fontSize: 12, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 10 }}>Apprentices · {availableApprentices.length} free</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16, padding: 12, background: "#FFFFFF", borderRadius: 14, border: "1px solid #E5E7EB", minHeight: 44 }}>
                {apprentices.length === 0 && <span style={{ color: "#64748B", fontSize: 12 }}>No apprentices yet — add some in Team.</span>}
                {availableApprentices.map(a => (
                  <span key={a.id} {...dragHandlers("helper", a.id)} title="Drag onto a crew"
                    style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#F59E0B14", color: "#B45309", border: "1px solid #F59E0B44", borderRadius: 20, padding: "5px 10px", fontSize: 12, fontWeight: 700, cursor: "grab" }}>
                    ⠿ {a.name}
                  </span>
                ))}
                {availableApprentices.length === 0 && apprentices.length > 0 && <span style={{ color: "#70C0A8", fontSize: 12, fontWeight: 600 }}>✓ All apprentices assigned</span>}
              </div>

              {/* Pairable leads — drag a lead onto another crew when there aren't
                  enough apprentices, so two leads run the day together. A lead only
                  appears here once you Remove their own crew box above. */}
              {availableLeads.length > 0 && (
                <>
                  <div style={{ color: "#64748B", fontSize: 12, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 10 }}>Leads free to pair · {availableLeads.length}</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 22, padding: 12, background: "#FFFFFF", borderRadius: 14, border: "1px solid #E5E7EB", minHeight: 44 }}>
                    {availableLeads.map(l => (
                      <span key={l.id} {...dragHandlers("helper", l.id)} title="Drag onto another crew to pair leads"
                        style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#0EA5E914", color: "#0369A1", border: "1px solid #0EA5E944", borderRadius: 20, padding: "5px 10px", fontSize: 12, fontWeight: 700, cursor: "grab" }}>
                        ⠿ {l.name}
                      </span>
                    ))}
                  </div>
                </>
              )}

              {/* Appointment pool */}
              <div style={{ color: "#64748B", fontSize: 12, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 10 }}>Unassigned Appointments · {unassignedAppts.length}</div>
              {appointments.length === 0 && <div style={{ color: "#64748B", textAlign: "center", padding: 24, background: "#FFFFFF", borderRadius: 14, border: "1px solid #E5E7EB" }}>No appointments for this date.</div>}
              {unassignedAppts.length === 0 && appointments.length > 0 && <div style={{ color: "#70C0A8", textAlign: "center", padding: 24, background: "#FFFFFF", borderRadius: 14, border: "1px solid #70C0A833", fontWeight: 600 }}>✓ All appointments assigned</div>}
              {unassignedAppts.sort((a, b) => (a.sortMins ?? 0) - (b.sortMins ?? 0)).map(a => (
                <div key={a.id} {...dragHandlers("appointment", a.id)} title="Drag onto a crew" style={{ ...card, cursor: "grab" }}>
                  {apptRow(a)}
                  {crews.length > 0 && (
                    <select value="" onChange={e => assignAppt(e.target.value, a.id)} style={{ width: "100%", marginTop: 10, padding: "8px 10px", borderRadius: 10, background: "#F7F8FA", border: "1px solid #E5E7EB", color: "#334155", fontSize: 12, fontFamily: "inherit", fontWeight: 600 }}>
                      <option value="">Assign to crew…</option>
                      {crews.map(c => <option key={c.leadId} value={c.leadId}>{userById(c.leadId)?.name || "Crew"}</option>)}
                    </select>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── PRODUCT & MIXING GUIDE ───────────────────────────────────────────────────
// Van quick-reference the crew can open at clock-in: what each product is for,
// how to mix it, and how to use it. Content lives in productGuide.js.
function ProductGuide({ onClose }) {
  const isMobile = useIsMobile();
  const twoCol = { display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: 16, alignItems: "start" };
  const sectionTitle = (t) => <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 17, fontWeight: 700, color: "#0F172A", margin: "26px 0 10px" }}>{t}</div>;
  const subLabel = (t, color) => <div style={{ color, fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", margin: "12px 0 6px" }}>{t}</div>;

  const decisionRows = (rows, color) => (
    <div style={{ background: "#FFFFFF", borderRadius: 14, border: "1px solid #E5E7EB", overflow: "hidden" }}>
      {rows.map((r, i) => (
        <div key={i} style={{ display: "flex", gap: 12, alignItems: "center", padding: "11px 14px", borderTop: i ? "1px solid #F1F5F9" : "none" }}>
          <div style={{ flex: 1, minWidth: 0, color: "#334155", fontSize: 13 }}>{r.need}</div>
          <div style={{ textAlign: "right", flexShrink: 0 }}>
            <div style={{ color, fontWeight: 700, fontSize: 13 }}>{r.grab}</div>
            <div style={{ color: "#64748B", fontSize: 11, fontWeight: 600 }}>{r.mix}</div>
          </div>
        </div>
      ))}
    </div>
  );

  const orderList = (steps, color) => (
    <div style={{ background: "#FFFFFF", borderRadius: 14, border: "1px solid #E5E7EB", padding: "10px 16px" }}>
      {steps.map((s, i) => (
        <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "8px 0", borderTop: i ? "1px solid #F1F5F9" : "none" }}>
          <div style={{ width: 22, height: 22, borderRadius: "50%", background: color, color: "#fff", fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{i + 1}</div>
          <div style={{ color: "#334155", fontSize: 13, lineHeight: 1.4, paddingTop: 2 }}>{s}</div>
        </div>
      ))}
    </div>
  );

  return (
    <div style={{ position: "fixed", inset: 0, background: PAGE_BG, zIndex: 700, overflowY: "auto", overflowX: "hidden", fontFamily: "'DM Sans', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Space+Grotesk:wght@700&display=swap" rel="stylesheet" />

      <div style={headerBarSticky}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}><img src={MINT_LOGO} alt="Mint" style={{ width: 64, height: 64, display: "block", flexShrink: 0 }} /><div style={headerTitle}>Chemical How-To Guide</div></div>
        <button onClick={onClose} style={glassBtn}>✕ Close</button>
      </div>

      <div style={{ padding: "18px 16px", maxWidth: isMobile ? 600 : 900, margin: "0 auto", paddingBottom: 44 }}>
        <div style={{ background: "#FFFBEB", border: "1px solid #F59E0B44", borderRadius: 12, padding: "12px 14px", color: "#92400E", fontSize: 13, fontWeight: 600, lineHeight: 1.5, marginBottom: 4 }}>{GUIDE_INTRO}</div>

        {sectionTitle("Which product do I grab?")}
        <div style={{ color: "#64748B", fontSize: 12, marginBottom: 6 }}>Ask what you're trying to <b>do</b>, then grab the product.</div>
        {subLabel("Interior", "#0EA5E9")}
        {decisionRows(DECISION_INTERIOR, "#0EA5E9")}
        {subLabel("Exterior", "#0D9488")}
        {decisionRows(DECISION_EXTERIOR, "#0D9488")}

        {/* Clean vs Dress */}
        <div style={{ marginTop: 22, background: "#0F172A", borderRadius: 16, padding: "16px 18px" }}>
          <div style={{ color: "#34E0B0", fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", marginBottom: 6 }}>Read this first — CLEAN vs DRESS</div>
          <div style={{ color: "#FFFFFF", fontWeight: 700, fontSize: 16, marginBottom: 10 }}>{CLEAN_VS_DRESS.headline}</div>
          {CLEAN_VS_DRESS.points.map((p, i) => (
            <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6 }}>
              <span style={{ color: "#34E0B0", flexShrink: 0 }}>•</span>
              <span style={{ color: "#CBD5E1", fontSize: 13, lineHeight: 1.45 }}>{p}</span>
            </div>
          ))}
        </div>

        {sectionTitle("The order you work in")}
        <div style={twoCol}>
          <div>{subLabel("Interior", "#0EA5E9")}{orderList(ORDER_INTERIOR, "#0EA5E9")}</div>
          <div>{subLabel("Exterior", "#0D9488")}{orderList(ORDER_EXTERIOR, "#0D9488")}</div>
        </div>

        {sectionTitle("Product reference")}
        <div style={twoCol}>
          {PRODUCTS.map(p => (
            <div key={p.name} style={{ background: "#FFFFFF", borderRadius: 16, border: "1px solid #E5E7EB", overflow: "hidden" }}>
              <div style={{ background: p.color + "14", borderBottom: `1px solid ${p.color}33`, padding: "14px 16px", display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 14, height: 40, borderRadius: 4, background: p.color, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: "#0F172A", fontWeight: 700, fontSize: 15, fontFamily: "'Space Grotesk', sans-serif" }}>{p.name}</div>
                  <div style={{ color: p.color, fontSize: 12, fontWeight: 700 }}>{p.tag}</div>
                </div>
              </div>
              <div style={{ padding: "14px 16px" }}>
                {[["When to use", p.when], ["Mix", p.mix], ["How", p.how]].map(([label, text]) => (
                  <div key={label} style={{ marginBottom: 10 }}>
                    <div style={{ color: p.color, fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", marginBottom: 2 }}>{label}</div>
                    <div style={{ color: "#334155", fontSize: 13, lineHeight: 1.45 }}>{text}</div>
                  </div>
                ))}
                {p.never && (
                  <div style={{ background: "#FEF2F2", border: "1px solid #EF444433", borderRadius: 8, padding: "8px 10px", marginTop: 4 }}>
                    <div style={{ color: "#B91C1C", fontSize: 12, fontWeight: 600, lineHeight: 1.4 }}>⚠ {p.never}</div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {sectionTitle("Common questions")}
        {FAQS.map((f, i) => (
          <div key={i} style={{ background: "#FFFFFF", borderRadius: 14, border: "1px solid #E5E7EB", padding: "14px 16px", marginBottom: 10 }}>
            <div style={{ color: "#0F172A", fontWeight: 700, fontSize: 14, marginBottom: 5 }}>{f.q}</div>
            <div style={{ color: "#334155", fontSize: 13, lineHeight: 1.5 }}>{f.a}</div>
          </div>
        ))}

        {sectionTitle("Dilution cheat sheet")}
        <div style={{ background: "#FFFFFF", borderRadius: 14, border: "1px solid #E5E7EB", overflow: "hidden" }}>
          {CHEAT_SHEET.map((r, i) => (
            <div key={i} style={{ display: "flex", gap: 10, alignItems: "center", padding: "11px 14px", borderTop: i ? "1px solid #F1F5F9" : "none" }}>
              <div style={{ width: isMobile ? 96 : 130, flexShrink: 0, color: "#0F172A", fontWeight: 700, fontSize: 13 }}>{r.product}</div>
              <div style={{ width: 84, flexShrink: 0, color: "#0D9488", fontWeight: 700, fontSize: 12 }}>{r.ratio}</div>
              <div style={{ flex: 1, minWidth: 0, color: "#64748B", fontSize: 12 }}>{r.job}</div>
            </div>
          ))}
        </div>

        <div style={{ color: "#94A3B8", fontSize: 12, textAlign: "center", marginTop: 20, fontStyle: "italic" }}>Clean before you dress. Company products only.</div>
      </div>
    </div>
  );
}

// ─── IN-APP CAMERA ────────────────────────────────────────────────────────────
// Live viewfinder via getUserMedia. The captured frame is handed straight to the
// checklist and uploaded — it never touches the phone's photo library.
function CameraCapture({ onCapture, onClose }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState("");
  const [ready, setReady] = useState(false);
  const [shots, setShots] = useState(0);   // burst count this session
  const [flash, setFlash] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" }, width: { ideal: 1600 }, height: { ideal: 1200 } }, audio: false });
        if (!active) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) { videoRef.current.srcObject = stream; try { await videoRef.current.play(); } catch {} }
        setReady(true);
      } catch (e) {
        setError("Camera unavailable — allow camera access for this site in your browser settings, then reopen.");
      }
    })();
    return () => { active = false; if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop()); };
  }, []);

  const snap = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    // Draw the frame straight into a size-capped canvas (single pass, low memory).
    // Smaller cap + a bit more compression => faster uploads on weak signal.
    const maxDim = 1280;
    const scale = Math.min(1, maxDim / Math.max(v.videoWidth, v.videoHeight));
    const w = Math.round(v.videoWidth * scale), h = Math.round(v.videoHeight * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(v, 0, 0, w, h);
    canvas.toBlob(b => {
      canvas.width = 0; canvas.height = 0; // release memory promptly
      if (b) { onCapture(b); setShots(s => s + 1); setFlash(true); setTimeout(() => setFlash(false), 140); }
    }, "image/jpeg", 0.62);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "#000", zIndex: 1100, display: "flex", flexDirection: "column", fontFamily: "'DM Sans', sans-serif" }}>
      {flash && <div style={{ position: "absolute", inset: 0, background: "#fff", opacity: 0.6, zIndex: 2 }} />}
      <div style={{ padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", color: "#fff" }}>
        <span style={{ fontWeight: 700, fontSize: 14 }}>Take photo{shots > 0 ? ` · ${shots} taken` : ""}</span>
        <button onClick={onClose} style={{ background: shots > 0 ? "#10B981" : "rgba(255,255,255,0.16)", border: shots > 0 ? "none" : "1px solid rgba(255,255,255,0.4)", color: "#fff", borderRadius: 8, padding: "6px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>{shots > 0 ? "Done" : "Close"}</button>
      </div>
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
        {error
          ? <div style={{ color: "#fff", padding: 24, textAlign: "center", fontSize: 14, lineHeight: 1.5 }}>{error}</div>
          : <video ref={videoRef} playsInline muted style={{ maxWidth: "100%", maxHeight: "100%" }} />}
      </div>
      {!error && (
        <div style={{ padding: "16px", display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
          <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 12 }}>Tap to shoot — take as many as you need, then Done</div>
          <button onClick={snap} disabled={!ready} aria-label="Capture photo"
            style={{ width: 76, height: 76, borderRadius: "50%", background: !ready ? "#64748B" : "#10B981", border: "5px solid #fff", cursor: ready ? "pointer" : "default", boxShadow: "0 4px 16px rgba(0,0,0,0.5)" }} />
        </div>
      )}
    </div>
  );
}

// ─── VEHICLE CHECKLIST ────────────────────────────────────────────────────────
// Per-appointment inside-out checklist with multiple Before/After photos per
// area, captured in-app and stored in Firebase Storage (checklists/{apptId}/…).
function VehicleChecklist({ appt, user, ghlConfig, onClose }) {
  const areas = areasForService(appt.service);
  const [data, setData] = useState({});   // saved/completed: { [areaKey]: { before:[{url,path}], after:[...] } }
  const [loading, setLoading] = useState(true);
  const [cam, setCam] = useState(null);   // { areaKey, phase } | null
  const [viewer, setViewer] = useState(null);
  const [msg, setMsg] = useState("");
  const [queue, setQueue] = useState([]); // pending uploads: { id, areaKey, phase, blob, localUrl, status:"uploading"|"failed" }
  const [approval, setApproval] = useState(null); // { by, at, shareUrl } once approved
  const [approving, setApproving] = useState("");  // "" | "working" | "sent" | "error"
  const canApprove = APPROVER_ROLES.includes(user?.role);
  // Live mirror of the saved areas — every write builds on THIS, never a stale
  // render closure, so rapid before/after captures can't overwrite each other.
  const dataRef = useRef({});
  const approvalRef = useRef({});         // approval fields, folded into every save so they survive
  const queueRef = useRef([]);            // live mirror of `queue` for unload logging
  const logEv = (event, extra) => logPhoto(user, appt, event, extra);
  const countPhotos = (map) => Object.values(map || {}).reduce(
    (n, a) => n + (a?.before?.length || 0) + (a?.after?.length || 0), 0);

  useEffect(() => {
    let cancelled = false;
    logEv("open");
    (async () => {
      try {
        const snap = await getDoc(doc(db, "checklists", appt.id));
        const d = snap.exists() ? snap.data() : {};
        const loaded = d.areas || {};
        if (!cancelled) {
          dataRef.current = loaded; setData(loaded);
          if (d.customerApproved) { const a = { by: d.approvedBy || "", at: d.approvedAt || null, shareUrl: d.shareUrl || "" }; approvalRef.current = { customerApproved: true, approvedBy: a.by, approvedAt: a.at, shareUrl: a.shareUrl, ghlSent: !!d.ghlSent }; setApproval(a); if (d.ghlSent) setApproving("sent"); }
          logEv("loaded", { existing: countPhotos(loaded) });
        }
      } catch (e) { if (!cancelled) { dataRef.current = {}; setData({}); setMsg("Couldn't load saved photos — check your signal and reopen."); logEv("load_error", { error: String(e?.code || e?.message || e) }); } }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [appt.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Smoking-gun signal: user backgrounds or closes the app while uploads are
  // still pending. Best-effort — the write may not flush if the tab is killed.
  useEffect(() => { queueRef.current = queue; }, [queue]);
  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === "hidden" && queueRef.current.length > 0) {
        logEv("hidden_with_pending", {
          pending: queueRef.current.filter(x => x.status === "uploading").length,
          failed: queueRef.current.filter(x => x.status === "failed").length,
        });
      }
    };
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", onHide);
    return () => { document.removeEventListener("visibilitychange", onHide); window.removeEventListener("pagehide", onHide); };
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  // Save the completed-photo map to Firestore. Throws on failure so the caller
  // (the background uploader) can retry.
  const save = async (nextAreas) => {
    dataRef.current = nextAreas;
    setData(nextAreas);
    if (!appt.id) throw new Error("missing appointment id");
    await setDoc(doc(db, "checklists", appt.id), {
      appointmentId: appt.id, service: appt.service || "", customer: appt.customer || "",
      date: appt.date || "", email: appt.email || "", phone: appt.phone || "", address: appt.address || "",
      areas: nextAreas, updatedAt: Timestamp.now(), updatedBy: user?.name || user?.id || "",
      ...approvalRef.current,   // customerApproved / approvedBy / approvedAt / shareUrl — folded in so a later save can't drop them
    });
  };

  // Lead/manager marks the photos OK to send. Creates the public share link,
  // records the approval on the checklist, and pushes the link into GHL (a
  // permanent note + the latest-link custom field). Approval is saved even if
  // the GHL push fails, so it can be retried.
  const approveAndSend = async () => {
    if (approving === "working") return;
    if (countPhotos(dataRef.current) === 0) { setMsg("Take some photos before approving."); return; }
    setApproving("working"); setMsg("");
    logEv("approve_start", {});
    try {
      const shareUrl = approvalRef.current.shareUrl
        || (await createChecklistShare({ appointmentId: appt.id, service: appt.service, customer: appt.customer, date: appt.date, areasMap: dataRef.current })).url;
      approvalRef.current = { customerApproved: true, approvedBy: user?.name || user?.id || "", approvedAt: Timestamp.now(), shareUrl };
      await save(dataRef.current);
      setApproval({ by: approvalRef.current.approvedBy, at: approvalRef.current.approvedAt, shareUrl });
      try {
        const r = await sendChecklistToGhl(ghlConfig, { name: appt.customer, email: appt.email, phone: appt.phone, address: appt.address, service: appt.service, date: appt.date, shareUrl, crew: user?.name || "" });
        if (!r?.skipped) { approvalRef.current = { ...approvalRef.current, ghlSent: true }; await save(dataRef.current); }
        setApproving(r?.skipped ? "approved" : "sent");
        logEv("approve_sent", { skipped: !!r?.skipped });
      } catch (ghlErr) {
        setApproving("error"); setMsg("Approved & saved, but GHL send failed: " + ghlErr.message);
        logEv("approve_ghl_failed", { error: String(ghlErr.message) });
      }
    } catch (e) {
      setApproving("error"); setMsg("Couldn't approve — " + e.message);
      logEv("approve_failed", { error: String(e.message) });
    }
  };

  const unapprove = async () => {
    approvalRef.current = { customerApproved: false };
    try { await save(dataRef.current); } catch {}
    setApproval(null); setApproving("");
  };

  // Background upload with backoff retry. On success the photo moves from the
  // pending queue into the saved map; on repeated failure it's marked "failed"
  // with a Retry button. Nothing here blocks the camera.
  const uploadItem = async (item, attempt = 0) => {
    const t0 = Date.now();
    logEv("upload_start", { id: item.id, areaKey: item.areaKey, phase: item.phase, attempt, bytes: item.blob?.size ?? null });
    try {
      const { url, path } = await uploadChecklistPhoto(appt.id, item.areaKey, item.phase, item.blob);
      const base = dataRef.current;
      const cur = base[item.areaKey] || { before: [], after: [] };
      const next = { ...base, [item.areaKey]: { ...cur, [item.phase]: [...(cur[item.phase] || []), { url, path }] } };
      await save(next);
      setQueue(q => q.filter(x => x.id !== item.id));
      try { URL.revokeObjectURL(item.localUrl); } catch {}
      logEv("upload_ok", { id: item.id, areaKey: item.areaKey, phase: item.phase, attempt, ms: Date.now() - t0, total: countPhotos(next) });
    } catch (e) {
      const error = String(e?.code || e?.message || e);
      if (attempt < 5) { logEv("upload_retry", { id: item.id, attempt, ms: Date.now() - t0, error }); setTimeout(() => uploadItem(item, attempt + 1), Math.min(8000, 1200 * (attempt + 1))); }
      else { logEv("upload_failed", { id: item.id, attempt, ms: Date.now() - t0, error }); setQueue(q => q.map(x => x.id === item.id ? { ...x, status: "failed" } : x)); }
    }
  };

  // Instant: preview locally right away and upload in the background.
  const enqueue = (blob) => {
    if (!cam || !blob) return;
    const item = { id: `${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, areaKey: cam.areaKey, phase: cam.phase, blob, localUrl: URL.createObjectURL(blob), status: "uploading" };
    logEv("capture", { id: item.id, areaKey: item.areaKey, phase: item.phase, bytes: blob?.size ?? null });
    setQueue(q => [...q, item]);
    uploadItem(item);
  };

  const retry = (item) => { setQueue(q => q.map(x => x.id === item.id ? { ...x, status: "uploading" } : x)); uploadItem(item, 0); };
  const cancelPending = (id) => setQueue(q => { const it = q.find(x => x.id === id); if (it) { try { URL.revokeObjectURL(it.localUrl); } catch {} } return q.filter(x => x.id !== id); });

  const removePhoto = async (areaKey, phase, idx) => {
    const base = dataRef.current;
    const cur = base[areaKey] || { before: [], after: [] };
    const list = cur[phase] || [];
    const photo = list[idx];
    logEv("remove", { areaKey, phase, idx, hadPath: !!photo?.path });
    try { await save({ ...base, [areaKey]: { ...cur, [phase]: list.filter((_, i) => i !== idx) } }); } catch (e) { setMsg("Couldn't remove — " + e.message); }
    if (photo?.path) { try { await deleteObject(storageRef(storage, photo.path)); } catch {} }
  };

  const pendingFor = (areaKey, phase) => queue.filter(q => q.areaKey === areaKey && q.phase === phase);
  const hasPhase = (areaKey, phase) => (data[areaKey]?.[phase]?.length > 0) || pendingFor(areaKey, phase).length > 0;
  const areaDone = (key) => hasPhase(key, "before") && hasPhase(key, "after");
  const doneCount = areas.filter(a => areaDone(a.key)).length;
  const pendingCount = queue.filter(q => q.status === "uploading").length;
  const failedCount = queue.filter(q => q.status === "failed").length;

  const handleClose = () => {
    if (queue.length > 0) {
      logEv("close_with_pending", { pending: pendingCount, failed: failedCount });
      if (!window.confirm(`${queue.length} photo(s) are still uploading or failed to upload. If you leave now they may not save. Leave anyway?`)) { logEv("close_cancelled", { pending: pendingCount, failed: failedCount }); return; }
      logEv("close_confirmed_leave", { pending: pendingCount, failed: failedCount });
    }
    onClose();
  };

  const openLightbox = (url) => {
    const flat = [];
    areas.forEach(area => {
      (data[area.key]?.before || []).forEach(p => flat.push({ url: p.url, caption: `${area.label} — Before` }));
      (data[area.key]?.after || []).forEach(p => flat.push({ url: p.url, caption: `${area.label} — After` }));
    });
    setViewer({ photos: flat, index: Math.max(0, flat.findIndex(f => f.url === url)) });
  };

  const thumb = (p, areaKey, phase, idx) => (
    <div key={idx} style={{ position: "relative", width: 64, height: 64, borderRadius: 8, overflow: "hidden", border: "1px solid #E5E7EB", flexShrink: 0 }}>
      <img src={p.url} alt="" onClick={() => openLightbox(p.url)} style={{ width: "100%", height: "100%", objectFit: "cover", cursor: "pointer" }} />
      <button onClick={() => removePhoto(areaKey, phase, idx)} style={{ position: "absolute", top: 1, right: 1, width: 18, height: 18, borderRadius: "50%", background: "rgba(0,0,0,0.6)", color: "#fff", border: "none", fontSize: 11, cursor: "pointer", lineHeight: 1 }}>✕</button>
    </div>
  );

  const pendingThumb = (item) => (
    <div key={item.id} style={{ position: "relative", width: 64, height: 64, borderRadius: 8, overflow: "hidden", border: `1px solid ${item.status === "failed" ? "#EF4444" : "#E5E7EB"}`, flexShrink: 0 }}>
      <img src={item.localUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.65 }} />
      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {item.status === "failed"
          ? <button onClick={() => retry(item)} style={{ background: "#EF4444", color: "#fff", border: "none", borderRadius: 6, padding: "3px 7px", fontSize: 10, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Retry</button>
          : <span style={{ color: "#fff", fontSize: 16 }}>⏳</span>}
      </div>
      <button onClick={() => cancelPending(item.id)} style={{ position: "absolute", top: 1, right: 1, width: 18, height: 18, borderRadius: "50%", background: "rgba(0,0,0,0.6)", color: "#fff", border: "none", fontSize: 11, cursor: "pointer", lineHeight: 1 }}>✕</button>
    </div>
  );

  const phaseRow = (area, phase, color) => {
    const list = data[area.key]?.[phase] || [];
    const pend = pendingFor(area.key, phase);
    return (
      <div style={{ marginTop: 8 }}>
        <div style={{ color, fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 5 }}>{phase} · {list.length + pend.length}</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {list.map((p, idx) => thumb(p, area.key, phase, idx))}
          {pend.map(pendingThumb)}
          <button onClick={() => setCam({ areaKey: area.key, phase })}
            style={{ width: 64, height: 64, borderRadius: 8, background: "#F7F8FA", border: `1px dashed ${color}88`, color, fontSize: 22, cursor: "pointer", fontFamily: "inherit", flexShrink: 0 }}>＋</button>
        </div>
      </div>
    );
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: PAGE_BG, zIndex: 1000, overflowY: "auto", overflowX: "hidden", fontFamily: "'DM Sans', sans-serif" }}>
      <div style={headerBarSticky}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ ...headerTitle, fontSize: 16, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{appt.customer || "Vehicle"}</div>
            <div style={{ color: "rgba(255,255,255,0.9)", fontSize: 11, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{appt.service}</div>
          </div>
        </div>
        <button onClick={handleClose} style={glassBtn}>✕ Close</button>
      </div>

      <div style={{ padding: "16px", maxWidth: 640, margin: "0 auto" }}>
        <div style={{ background: "#FFFFFF", borderRadius: 12, padding: "12px 14px", border: "1px solid #E5E7EB", marginBottom: 14, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ color: "#334155", fontSize: 13, fontWeight: 700 }}>Progress: {doneCount} / {areas.length} areas</span>
          <span style={{ color: doneCount === areas.length ? "#059669" : "#64748B", fontSize: 13, fontWeight: 700 }}>{doneCount === areas.length ? "✓ Complete" : `${areas.length - doneCount} left`}</span>
        </div>

        <div style={{ background: "#0EA5E90F", border: "1px solid #0EA5E933", borderRadius: 10, padding: "10px 14px", marginBottom: 14, color: "#0369A1", fontSize: 13, fontWeight: 700, textAlign: "center" }}>
          📸 Take Before and After photos of each area — work inside out.
        </div>

        {(pendingCount > 0 || failedCount > 0) && (
          <div style={{ background: failedCount > 0 ? "#FEF2F2" : "#FFFBEB", border: `1px solid ${failedCount > 0 ? "#EF444433" : "#F59E0B44"}`, borderRadius: 10, padding: "10px 14px", marginBottom: 14, color: failedCount > 0 ? "#B91C1C" : "#92400E", fontSize: 12.5, fontWeight: 700 }}>
            {pendingCount > 0 && <div>⏳ {pendingCount} photo{pendingCount > 1 ? "s" : ""} uploading in the background — you can keep shooting.</div>}
            {failedCount > 0 && <div>⚠ {failedCount} photo{failedCount > 1 ? "s" : ""} failed to upload — tap Retry on the photo. Don't close until they're saved.</div>}
          </div>
        )}

        {msg && <div style={{ background: "#FEF2F2", border: "1px solid #EF444433", borderRadius: 10, padding: "10px 14px", marginBottom: 14, color: "#B91C1C", fontSize: 12, fontWeight: 600 }}>⚠ {msg}</div>}

        {!loading && canApprove && (
          approval ? (
            <div style={{ background: "#ECFDF5", border: "1px solid #10B98155", borderRadius: 12, padding: "12px 14px", marginBottom: 14 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <div style={{ color: "#047857", fontWeight: 700, fontSize: 13 }}>✓ Approved — OK to send to customer{approval.by ? ` · ${approval.by}` : ""}</div>
                <button onClick={unapprove} style={{ background: "none", border: "1px solid #10B98155", color: "#047857", borderRadius: 8, padding: "4px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Undo</button>
              </div>
              {approving === "error" && <button onClick={approveAndSend} style={{ marginTop: 8, background: "#EF4444", color: "#fff", border: "none", borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Retry GHL send</button>}
              {approving === "sent" && <div style={{ color: "#059669", fontSize: 11.5, marginTop: 6, fontWeight: 600 }}>Sent to GoHighLevel ✓</div>}
              {approval.shareUrl && <div style={{ marginTop: 8, display: "flex", gap: 6, alignItems: "center" }}>
                <input readOnly value={approval.shareUrl} onFocus={e => e.target.select()} style={{ flex: 1, minWidth: 0, padding: "6px 9px", borderRadius: 8, border: "1px solid #A7F3D0", fontSize: 11, fontFamily: "inherit", color: "#065F46", background: "#fff" }} />
                <button onClick={() => { try { navigator.clipboard.writeText(approval.shareUrl); } catch {} }} style={{ padding: "6px 11px", borderRadius: 8, border: "none", background: "#10B981", color: "#fff", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Copy</button>
              </div>}
            </div>
          ) : (
            <button onClick={approveAndSend} disabled={approving === "working"}
              style={{ width: "100%", background: approving === "working" ? "#94A3B8" : "linear-gradient(135deg,#10B981,#059669)", color: "#fff", border: "none", borderRadius: 12, padding: "13px", fontSize: 14, fontWeight: 700, cursor: approving === "working" ? "default" : "pointer", fontFamily: "inherit", marginBottom: 14, boxShadow: "0 3px 10px rgba(5,150,105,0.25)" }}>
              {approving === "working" ? "Approving…" : "✓ Approve — OK to send to customer"}
            </button>
          )
        )}

        {loading ? <div style={{ color: "#64748B", textAlign: "center", padding: 30 }}>Loading checklist…</div> : areas.map((area, i) => {
          const done = areaDone(area.key);
          return (
            <div key={area.key} style={{ background: "#FFFFFF", borderRadius: 14, padding: "14px 16px", border: `1px solid ${done ? "#10B98155" : "#E5E7EB"}`, marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 22, height: 22, borderRadius: "50%", background: done ? "#10B981" : "#E5E7EB", color: done ? "#fff" : "#94A3B8", fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{done ? "✓" : i + 1}</div>
                <div style={{ color: "#0F172A", fontWeight: 700, fontSize: 14 }}>{area.label}</div>
              </div>
              {area.note && <div style={{ color: "#B45309", fontSize: 12, fontWeight: 600, marginTop: 4, marginLeft: 30 }}>⚠ {area.note}</div>}
              <div style={{ marginLeft: 30 }}>
                {phaseRow(area, "before", "#D97706")}
                {phaseRow(area, "after", "#059669")}
              </div>
            </div>
          );
        })}
      </div>

      {cam && <CameraCapture onCapture={enqueue} onClose={() => setCam(null)} />}
      {viewer && <Lightbox photos={viewer.photos} index={viewer.index} onClose={() => setViewer(null)} />}
    </div>
  );
}

// ─── CHECKLIST REVIEW (admin/manager) ─────────────────────────────────────────
// Browse completed vehicle checklists by date and view every area's before/after
// photos. Read-only.
function ChecklistReview({ allUsers = [], user, ghlConfig, onClose }) {
  const today = localDateISO();
  const [dateISO, setDateISO] = useState(today);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [viewer, setViewer] = useState(null);
  const [busy, setBusy] = useState("");        // vehicle id currently downloading/sharing
  const [shareLink, setShareLink] = useState(null); // { id, url }
  const [crewByAppt, setCrewByAppt] = useState({}); // appointmentId -> { lead, apprentices:[] }
  const [apptInfo, setApptInfo] = useState({});     // appointmentId -> { time, address }
  const [refreshTick, setRefreshTick] = useState(0); // bumped on focus to re-pull

  // Re-pull this day's appointments + checklists when the screen regains focus,
  // so booking changes (type changes, new jobs) appear without a manual reload.
  useEffect(() => {
    let last = Date.now();
    const refresh = () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - last < 8000) return;
      last = Date.now();
      setRefreshTick(t => t + 1);
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => { window.removeEventListener("focus", refresh); document.removeEventListener("visibilitychange", refresh); };
  }, []);

  const shiftDate = (iso, days) => { const d = new Date(iso + "T00:00:00"); d.setDate(d.getDate() + days); return localDateISO(d); };
  const yesterday = shiftDate(today, -1);

  const collectPhotos = (c) => {
    const out = [];
    areasForService(c.service).forEach(area => {
      (c.areas?.[area.key]?.before || []).forEach((p, i) => out.push({ url: p.url, name: `${area.key}_before_${i + 1}.jpg` }));
      (c.areas?.[area.key]?.after || []).forEach((p, i) => out.push({ url: p.url, name: `${area.key}_after_${i + 1}.jpg` }));
    });
    return out;
  };

  const downloadAll = async (c) => {
    const photos = collectPhotos(c);
    if (!photos.length) return;
    setBusy(c.id);
    try {
      const r = await fetch("/api/checklist-zip", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name: `${c.customer || "vehicle"}_${c.date || ""}`, photos }) });
      if (!r.ok) throw new Error("zip failed");
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${(c.customer || "vehicle")}_${c.date || ""}.zip`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { alert("Download failed — try again."); }
    setBusy("");
  };

  const createShare = async (c) => {
    setBusy(c.id);
    try {
      const areasData = areasForService(c.service).map(area => ({
        label: area.label,
        before: (c.areas?.[area.key]?.before || []).map(p => p.url),
        after: (c.areas?.[area.key]?.after || []).map(p => p.url),
      })).filter(a => a.before.length || a.after.length);
      const sid = `${c.id}-${Math.random().toString(36).slice(2, 8)}`;
      await setDoc(doc(db, "shares", sid), { vehicle: c.customer || "Your vehicle", service: c.service || "", date: c.date || "", areas: areasData, createdAt: Timestamp.now() });
      const url = `${APP_BASE_URL}/?share=${sid}`;
      try { await navigator.clipboard.writeText(url); } catch {}
      setShareLink({ id: c.id, url });
    } catch (e) { alert("Couldn't create link — make sure the 'shares' Firestore rule is published."); }
    setBusy("");
  };

  // Approve a vehicle's photos as OK-to-send and push the link to GHL (note +
  // custom field). Records approval on the checklist so it isn't re-sent.
  const [approvedIds, setApprovedIds] = useState({}); // id -> "sent" | "error"
  const canApprove = APPROVER_ROLES.includes(user?.role);
  const approveAndSend = async (c) => {
    if (!c.email && !c.phone) { alert("This job has no customer email or phone on file, so GHL can't match the contact. Re-open the checklist on the newest visit, or add contact info in GHL."); return; }
    setBusy(c.id);
    try {
      const { url } = await createChecklistShare({ appointmentId: c.id, service: c.service, customer: c.customer, date: c.date, areasMap: c.areas });
      await setDoc(doc(db, "checklists", c.id), { customerApproved: true, approvedBy: user?.name || user?.id || "", approvedAt: Timestamp.now(), shareUrl: url }, { merge: true });
      const cr = crewByAppt[c.id];
      const crew = cr ? [cr.lead, ...(cr.apprentices || [])].filter(Boolean).join(", ") : (c.updatedBy || "");
      await sendChecklistToGhl(ghlConfig, { name: c.customer, email: c.email, phone: c.phone, address: c.address, service: c.service, date: c.date, shareUrl: url, crew });
      // Only mark ghlSent AFTER the push actually succeeds — approved != sent.
      await setDoc(doc(db, "checklists", c.id), { ghlSent: true, ghlSentAt: Timestamp.now() }, { merge: true });
      setList(prev => prev.map(x => x.id === c.id ? { ...x, customerApproved: true, ghlSent: true } : x));
      setApprovedIds(p => ({ ...p, [c.id]: "sent" }));
    } catch (e) {
      setApprovedIds(p => ({ ...p, [c.id]: "error" }));
      alert("Send failed: " + e.message);
    }
    setBusy("");
  };
  const dateLabel = new Date(dateISO + "T00:00:00").toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [snap, asnap, appts] = await Promise.all([
          getDocs(query(collection(db, "checklists"), where("date", "==", dateISO))),
          getDoc(doc(db, "assignments", dateISO)),
          fetchAppointments(dateISO).catch(() => []),
        ]);
        if (cancelled) return;
        // Build the list from every appointment that day, merged with its
        // checklist doc when one exists — so jobs with NO photos still show up
        // (flagged "No photos yet") instead of silently disappearing.
        const clByAppt = {};
        snap.docs.forEach(d => { const c = { id: d.id, ...d.data() }; clByAppt[String(c.appointmentId || d.id)] = c; });
        const rows = (appts || []).map(a => {
          const cl = clByAppt[String(a.id)] || null;
          return {
            id: String(a.id),
            // Prefer the LIVE appointment's customer/type over the saved
            // checklist so a booking change (e.g. Mint → Interior) shows here;
            // fall back to the saved values only if the appointment is gone.
            customer: a.customer || cl?.customer || "Vehicle",
            service: a.service || cl?.service || "",
            date: dateISO,
            email: cl?.email || a.email || "", phone: cl?.phone || a.phone || "", address: cl?.address || a.address || "",
            areas: cl?.areas || {},
            customerApproved: cl?.customerApproved || false, ghlSent: cl?.ghlSent || false,
            shareUrl: cl?.shareUrl || "", updatedBy: cl?.updatedBy || "",
            hasChecklist: !!cl, sortMins: a.sortMins ?? 9999,
          };
        });
        // Keep any checklist docs that don't map to a live appointment (safety).
        const apptIds = new Set((appts || []).map(a => String(a.id)));
        snap.docs.forEach(d => { const c = { id: d.id, ...d.data() }; const aid = String(c.appointmentId || d.id); if (!apptIds.has(aid)) rows.push({ ...c, areas: c.areas || {}, hasChecklist: true, sortMins: 9999 }); });
        rows.sort((a, b) => (a.sortMins - b.sortMins) || (a.customer || "").localeCompare(b.customer || ""));
        setList(rows);
        // Map each appointment to its crew (lead + apprentices) for that day.
        const nameOf = (id) => allUsers.find(u => u.id === id)?.name || "";
        const map = {};
        (asnap.exists() ? (asnap.data().crews || []) : []).forEach(c => {
          const lead = nameOf(c.leadId);
          (c.appointmentIds || []).forEach(aid => {
            // Only the helpers actually on this time slot (whole-day or this appt).
            const apprentices = helpersOnAppt(c, aid).map(nameOf).filter(Boolean);
            map[String(aid)] = { lead, apprentices };
          });
        });
        setCrewByAppt(map);
        // Map each appointment to its time slot + address.
        const info = {};
        (appts || []).forEach(a => { info[String(a.id)] = { time: a.time || "", address: a.address || "" }; });
        setApptInfo(info);
      } catch (e) { if (!cancelled) { setList([]); setCrewByAppt({}); } }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateISO, refreshTick]);

  const openLightbox = (c, url) => {
    const flat = [];
    areasForService(c.service).forEach(area => {
      (c.areas?.[area.key]?.before || []).forEach(p => flat.push({ url: p.url, caption: `${area.label} — Before` }));
      (c.areas?.[area.key]?.after || []).forEach(p => flat.push({ url: p.url, caption: `${area.label} — After` }));
    });
    setViewer({ photos: flat, index: Math.max(0, flat.findIndex(f => f.url === url)) });
  };

  const photoStrip = (c, photos, label, color) => (
    <div style={{ marginTop: 6 }}>
      <div style={{ color, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>{label} · {photos.length}</div>
      {photos.length === 0
        ? <div style={{ color: "#94A3B8", fontSize: 12, fontStyle: "italic" }}>none</div>
        : <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {photos.map((p, i) => <img key={i} src={p.url} alt="" onClick={() => openLightbox(c, p.url)} style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 8, border: "1px solid #E5E7EB", cursor: "pointer" }} />)}
          </div>}
    </div>
  );

  return (
    <div style={{ position: "fixed", inset: 0, background: PAGE_BG, zIndex: 620, overflowY: "auto", overflowX: "hidden", fontFamily: "'DM Sans', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Space+Grotesk:wght@700&display=swap" rel="stylesheet" />
      <div style={headerBarSticky}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}><img src={MINT_LOGO} alt="Mint" style={{ width: 64, height: 64, display: "block", flexShrink: 0 }} /><div style={headerTitle}>Checklist Photos</div></div>
        <button onClick={onClose} style={glassBtn}>✕ Close</button>
      </div>

      <div style={{ padding: "16px", maxWidth: 760, margin: "0 auto" }}>
        {/* Date navigator */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <button onClick={() => setDateISO(shiftDate(dateISO, -1))} style={{ background: "#FFFFFF", border: "1px solid #E5E7EB", borderRadius: 10, padding: "8px 12px", cursor: "pointer", fontSize: 14, fontFamily: "inherit", color: "#334155" }}>‹</button>
          <div style={{ flex: 1, textAlign: "center" }}>
            <div style={{ color: "#0F172A", fontWeight: 700, fontSize: 15 }}>{dateLabel}{dateISO === today ? " · Today" : dateISO === yesterday ? " · Yesterday" : ""}</div>
            <input type="date" value={dateISO} onChange={e => setDateISO(e.target.value)} style={{ marginTop: 4, border: "1px solid #E5E7EB", borderRadius: 8, padding: "4px 8px", fontFamily: "inherit", fontSize: 12, color: "#64748B" }} />
          </div>
          <button onClick={() => setDateISO(shiftDate(dateISO, 1))} style={{ background: "#FFFFFF", border: "1px solid #E5E7EB", borderRadius: 10, padding: "8px 12px", cursor: "pointer", fontSize: 14, fontFamily: "inherit", color: "#334155" }}>›</button>
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 16, justifyContent: "center" }}>
          {[["Today", today], ["Yesterday", yesterday]].map(([label, d]) => {
            const on = dateISO === d;
            return <button key={label} onClick={() => setDateISO(d)} style={{ padding: "7px 18px", borderRadius: 999, border: on ? "none" : "1px solid #E5E7EB", background: on ? "linear-gradient(135deg, #10B981, #0D9488)" : "#FFFFFF", color: on ? "#FFFFFF" : "#475569", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>{label}</button>;
          })}
        </div>

        {loading ? <div style={{ color: "#64748B", textAlign: "center", padding: 30 }}>Loading…</div>
          : list.length === 0 ? <div style={{ color: "#64748B", textAlign: "center", padding: 30, background: "#FFFFFF", borderRadius: 14, border: "1px solid #E5E7EB" }}>No appointments scheduled for this day.</div>
          : list.map(c => {
            const areas = areasForService(c.service);
            const done = areas.filter(a => c.areas?.[a.key]?.before?.length && c.areas?.[a.key]?.after?.length).length;
            const hasPhotos = areas.some(a => c.areas?.[a.key]?.before?.length || c.areas?.[a.key]?.after?.length);
            const open = openId === c.id;
            const isSent = c.ghlSent || approvedIds[c.id] === "sent";
            const isApprovedNotSent = !isSent && c.customerApproved;
            return (
              <div key={c.id} style={{ background: "#FFFFFF", borderRadius: 14, border: `1px solid ${isSent ? "#10B98155" : isApprovedNotSent ? "#F59E0B55" : "#E5E7EB"}`, marginBottom: 12, overflow: "hidden" }}>
                <button onClick={() => setOpenId(open ? null : c.id)} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "14px 16px", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ color: "#0F172A", fontWeight: 700, fontSize: 15 }}>{c.customer || "Vehicle"}</div>
                    <div style={{ color: "#64748B", fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.service}</div>
                    {(() => {
                      const crew = crewByAppt[c.id];
                      const handledBy = crew?.lead
                        ? `${crew.lead}${crew.apprentices?.length ? " · " + crew.apprentices.join(", ") : ""}`
                        : (c.updatedBy || "");
                      return handledBy ? <div style={{ color: "#0369A1", fontSize: 12, fontWeight: 700, marginTop: 3 }}>👤 {handledBy}</div> : null;
                    })()}
                    {(() => {
                      const info = apptInfo[c.id];
                      if (!info || (!info.time && !info.address)) return null;
                      return (
                        <div style={{ color: "#64748B", fontSize: 12, marginTop: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {info.time && <span style={{ color: "#0EA5E9", fontWeight: 700 }}>🕐 {info.time}</span>}
                          {info.time && info.address ? "  ·  " : ""}
                          {info.address && <span>📍 {info.address}</span>}
                        </div>
                      );
                    })()}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                    {isSent
                      ? <span style={{ background: "#10B981", color: "#fff", borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 700 }}>✓ Sent</span>
                      : isApprovedNotSent
                        ? <span style={{ background: "#F59E0B", color: "#fff", borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 700 }}>⚠ Not sent</span>
                        : null}
                    {hasPhotos
                      ? <span style={{ background: done === areas.length ? "#10B98122" : "#F59E0B22", color: done === areas.length ? "#059669" : "#B45309", borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 700 }}>{done}/{areas.length}</span>
                      : <span style={{ background: "#FEE2E2", color: "#B91C1C", borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 700 }}>No photos</span>}
                    <span style={{ color: "#94A3B8", fontSize: 16 }}>{open ? "▾" : "▸"}</span>
                  </div>
                </button>
                {open && (
                  <div style={{ padding: "0 16px 14px" }}>
                    {!hasPhotos && (
                      <div style={{ background: "#FEF2F2", border: "1px solid #EF444433", borderRadius: 10, padding: "12px 14px", color: "#B91C1C", fontSize: 12.5, fontWeight: 600 }}>
                        📷 No photos taken for this job yet{crewByAppt[c.id]?.lead ? ` — assigned to ${crewByAppt[c.id].lead}` : ""}.
                      </div>
                    )}
                    {hasPhotos && (<>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
                      <button onClick={() => downloadAll(c)} disabled={busy === c.id}
                        style={{ padding: "8px 14px", borderRadius: 9, border: "none", background: busy === c.id ? "#94A3B8" : "linear-gradient(135deg, #10B981, #059669)", color: "#fff", fontSize: 12, fontWeight: 700, cursor: busy === c.id ? "default" : "pointer", fontFamily: "inherit" }}>{busy === c.id ? "Working…" : "⬇ Download all"}</button>
                      <button onClick={() => createShare(c)} disabled={busy === c.id}
                        style={{ padding: "8px 14px", borderRadius: 9, border: "1px solid #0EA5E966", background: "#F0F9FF", color: "#0369A1", fontSize: 12, fontWeight: 700, cursor: busy === c.id ? "default" : "pointer", fontFamily: "inherit" }}>🔗 Share link</button>
                      {canApprove && ghlConfig?.enabled && (
                        <button onClick={() => approveAndSend(c)} disabled={busy === c.id}
                          style={{ padding: "8px 14px", borderRadius: 9, border: isSent ? "1px solid #059669" : "none", background: busy === c.id ? "#94A3B8" : isSent ? "#ECFDF5" : isApprovedNotSent ? "#F59E0B" : "#10B981", color: isSent ? "#059669" : "#fff", fontSize: 12, fontWeight: 700, cursor: busy === c.id ? "default" : "pointer", fontFamily: "inherit" }}>
                          {busy === c.id ? "Sending…" : isSent ? "✓ Sent to HighLevel — resend" : isApprovedNotSent ? "⚠ Approved — send to HighLevel" : "✅ Approve & send to customer"}
                        </button>
                      )}
                    </div>
                    {shareLink?.id === c.id && (
                      <div style={{ background: "#F0F9FF", border: "1px solid #0EA5E933", borderRadius: 10, padding: "10px 12px", marginBottom: 6 }}>
                        <div style={{ color: "#0369A1", fontSize: 11, fontWeight: 700, marginBottom: 6 }}>✓ Link copied — send it to the customer</div>
                        <div style={{ display: "flex", gap: 6 }}>
                          <input readOnly value={shareLink.url} onFocus={e => e.target.select()} style={{ flex: 1, minWidth: 0, padding: "7px 10px", borderRadius: 8, border: "1px solid #CBD5E1", fontSize: 12, fontFamily: "inherit", color: "#334155" }} />
                          <button onClick={() => { try { navigator.clipboard.writeText(shareLink.url); } catch {} }} style={{ padding: "7px 12px", borderRadius: 8, border: "none", background: "#0EA5E9", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Copy</button>
                        </div>
                      </div>
                    )}
                    {areas.map((area, i) => {
                      const ph = c.areas?.[area.key] || {};
                      return (
                        <div key={area.key} style={{ borderTop: "1px solid #F1F5F9", paddingTop: 10, marginTop: 10 }}>
                          <div style={{ color: "#0F172A", fontWeight: 700, fontSize: 13 }}>{i + 1}. {area.label}</div>
                          {photoStrip(c, ph.before || [], "Before", "#D97706")}
                          {photoStrip(c, ph.after || [], "After", "#059669")}
                        </div>
                      );
                    })}
                    </>)}
                  </div>
                )}
              </div>
            );
          })}
      </div>

      {viewer && <Lightbox photos={viewer.photos} index={viewer.index} onClose={() => setViewer(null)} />}
    </div>
  );
}

// ─── LIGHTBOX ─────────────────────────────────────────────────────────────────
// Full-screen gallery: swipe (touch), ‹ › arrows, and keyboard nav across a list
// of { url, caption } — so you never have to close one to see the next.
function Lightbox({ photos, index, onClose }) {
  const [i, setI] = useState(index || 0);
  const touchX = useRef(null);
  const prev = (e) => { if (e) e.stopPropagation(); setI(x => (x - 1 + photos.length) % photos.length); };
  const next = (e) => { if (e) e.stopPropagation(); setI(x => (x + 1) % photos.length); };
  useEffect(() => {
    const onKey = (e) => { if (e.key === "ArrowLeft") prev(); else if (e.key === "ArrowRight") next(); else if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos.length]);
  if (!photos || !photos.length) return null;
  const cur = photos[i] || photos[0];
  const nav = { position: "absolute", top: "50%", transform: "translateY(-50%)", width: 46, height: 46, borderRadius: "50%", background: "rgba(255,255,255,0.16)", border: "1px solid rgba(255,255,255,0.4)", color: "#fff", fontSize: 24, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center" };
  return (
    <div onClick={onClose}
      onTouchStart={e => { touchX.current = e.touches[0].clientX; }}
      onTouchEnd={e => { if (touchX.current == null) return; const dx = e.changedTouches[0].clientX - touchX.current; if (Math.abs(dx) > 40) { dx > 0 ? prev() : next(); } touchX.current = null; }}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.93)", zIndex: 1300, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', sans-serif" }}>
      <button onClick={e => { e.stopPropagation(); onClose(); }} style={{ position: "absolute", top: 14, right: 16, background: "rgba(255,255,255,0.16)", border: "1px solid rgba(255,255,255,0.4)", color: "#fff", borderRadius: 8, padding: "6px 12px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>✕</button>
      <img src={cur.url} alt="" onClick={e => e.stopPropagation()} style={{ maxWidth: "92%", maxHeight: "78%", borderRadius: 8, objectFit: "contain" }} />
      {cur.caption && <div style={{ color: "#fff", fontSize: 13, fontWeight: 600, marginTop: 12, textAlign: "center", padding: "0 16px" }}>{cur.caption}</div>}
      <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 12, marginTop: 4 }}>{i + 1} / {photos.length}</div>
      {photos.length > 1 && <>
        <button onClick={prev} style={{ ...nav, left: 8 }}>‹</button>
        <button onClick={next} style={{ ...nav, right: 8 }}>›</button>
      </>}
    </div>
  );
}

// ─── PUBLIC SHARE PAGE ────────────────────────────────────────────────────────
// Opened via ?share=<id> with no login. Reads a public shares/{id} doc (photo
// URLs only, no PII beyond the vehicle label) and shows a before/after gallery.
function SharePage({ shareId }) {
  const [data, setData] = useState(null); // null=loading, false=missing, object=ok
  const [viewer, setViewer] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "shares", shareId));
        if (!cancelled) setData(snap.exists() ? snap.data() : false);
      } catch (e) { if (!cancelled) setData(false); }
    })();
    return () => { cancelled = true; };
  }, [shareId]);

  const openLightbox = (url) => {
    const flat = [];
    (data.areas || []).forEach(a => {
      (a.before || []).forEach(u => flat.push({ url: u, caption: `${a.label} — Before` }));
      (a.after || []).forEach(u => flat.push({ url: u, caption: `${a.label} — After` }));
    });
    setViewer({ photos: flat, index: Math.max(0, flat.findIndex(f => f.url === url)) });
  };

  const strip = (urls, label, color) => urls.length > 0 && (
    <div style={{ marginTop: 8 }}>
      <div style={{ color, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 5 }}>{label}</div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {urls.map((u, i) => <img key={i} src={u} alt="" onClick={() => openLightbox(u)} style={{ width: 96, height: 96, objectFit: "cover", borderRadius: 10, border: "1px solid #E5E7EB", cursor: "pointer" }} />)}
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: PAGE_BG, fontFamily: "'DM Sans', sans-serif", paddingBottom: 40 }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Space+Grotesk:wght@700&display=swap" rel="stylesheet" />
      <div style={headerBar}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <img src={MINT_LOGO} alt="Mint Mobile Detail" style={{ width: 36, height: 36, borderRadius: "50%", background: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.18)" }} />
          <div>
            <div style={headerTitle}>Mint Mobile Detail</div>
            <div style={{ color: "rgba(255,255,255,0.9)", fontSize: 12 }}>Before &amp; After</div>
          </div>
        </div>
      </div>
      <div style={{ padding: "18px 16px", maxWidth: 760, margin: "0 auto" }}>
        {data === null ? <div style={{ color: "#64748B", textAlign: "center", padding: 40 }}>Loading photos…</div>
          : data === false ? <div style={{ color: "#64748B", textAlign: "center", padding: 40, background: "#FFFFFF", borderRadius: 14, border: "1px solid #E5E7EB" }}>This link isn't available.</div>
          : (
            <>
              <div style={{ marginBottom: 16 }}>
                <div style={{ color: "#0F172A", fontFamily: "'Space Grotesk', sans-serif", fontSize: 22, fontWeight: 700 }}>{data.vehicle}</div>
                <div style={{ color: "#64748B", fontSize: 13, marginTop: 2 }}>{data.service}{data.date ? ` · ${fmtApptDate(data.date)}` : ""}</div>
              </div>
              {(data.areas || []).map((area, i) => (
                <div key={i} style={{ background: "#FFFFFF", borderRadius: 14, padding: "14px 16px", border: "1px solid #E5E7EB", marginBottom: 12 }}>
                  <div style={{ color: "#0F172A", fontWeight: 700, fontSize: 14 }}>{area.label}</div>
                  {strip(area.before || [], "Before", "#D97706")}
                  {strip(area.after || [], "After", "#059669")}
                </div>
              ))}
              <div style={{ color: "#94A3B8", fontSize: 12, textAlign: "center", marginTop: 16 }}>Thank you for choosing Mint Mobile Detail.</div>
            </>
          )}
      </div>
      {viewer && <Lightbox photos={viewer.photos} index={viewer.index} onClose={() => setViewer(null)} />}
    </div>
  );
}

// ─── ROOT APP ─────────────────────────────────────────────────────────────────
export default function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [entries, setEntries] = useState(MOCK_ENTRIES);
  const [allUsers, setAllUsers] = useState(MOCK_USERS);
  const [locating, setLocating] = useState(false);
  const [showQBSettings, setShowQBSettings] = useState(false);
  const [showReports, setShowReports] = useState(false);
  const [showEmployees, setShowEmployees] = useState(false);
  const [qbConfig, setQbConfig] = useState({ connected: false, clientId: "", clientSecret: "", realmId: "", accessToken: "", refreshToken: "", tokenExpiry: "" });
  const [showGhlSettings, setShowGhlSettings] = useState(false);
  const [ghlConfig, setGhlConfig] = useState({ enabled: false, token: "", locationId: "", fieldId: "" });
  const [trailEntry, setTrailEntry] = useState(null);
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [showAssignments, setShowAssignments] = useState(false);
  const [myAppointments, setMyAppointments] = useState([]); // today's appts for the logged-in lead/apprentice
  const [previewUserId, setPreviewUserId] = useState(null); // admin previewing what a crew member sees
  const [previewAppointments, setPreviewAppointments] = useState([]);
  const previewUser = allUsers.find(u => u.id === previewUserId) || null;
  const [apptRefreshTick, setApptRefreshTick] = useState(0); // bumped on focus to re-pull appointments

  // Re-pull appointments when the app regains focus / becomes visible, so
  // booking changes (new jobs, type changes) show without a manual reload.
  useEffect(() => {
    let last = Date.now();
    const refresh = () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - last < 8000) return; // debounce rapid focus events
      last = Date.now();
      setApptRefreshTick(t => t + 1);
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => { window.removeEventListener("focus", refresh); document.removeEventListener("visibilitychange", refresh); };
  }, []);

  // Load the logged-in field worker's assigned appointments for today, derived
  // from today's crew assignment (Firestore) + the appointment source.
  useEffect(() => {
    if (!user || !FIELD_ROLES.includes(user.role)) { setMyAppointments([]); return; }
    let cancelled = false;
    (async () => {
      const dateISO = localDateISO();
      try {
        const snap = await getDoc(doc(db, "assignments", dateISO));
        const crews = snap.exists() ? (snap.data().crews || []) : [];
        const ids = apptIdsForUser(crews, user.id);
        if (!ids.length) { if (!cancelled) setMyAppointments([]); return; }
        const appts = await fetchAppointments(dateISO);
        if (!cancelled) setMyAppointments(appts.filter(a => ids.includes(a.id)));
      } catch (e) {
        if (!cancelled) setMyAppointments([]);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.role, apptRefreshTick]);

  // Load today's appointments for the crew member the admin is previewing.
  useEffect(() => {
    if (!previewUserId) { setPreviewAppointments([]); return; }
    let cancelled = false;
    (async () => {
      const dateISO = localDateISO();
      try {
        const snap = await getDoc(doc(db, "assignments", dateISO));
        const crews = snap.exists() ? (snap.data().crews || []) : [];
        const ids = apptIdsForUser(crews, previewUserId);
        if (!ids.length) { if (!cancelled) setPreviewAppointments([]); return; }
        const appts = await fetchAppointments(dateISO);
        if (!cancelled) setPreviewAppointments(appts.filter(a => ids.includes(a.id)));
      } catch (e) {
        if (!cancelled) setPreviewAppointments([]);
      }
    })();
    return () => { cancelled = true; };
  }, [previewUserId]);

  // Live-track the current employee's active shift. Only the logged-in employee
  // writes their own GPS points — admins viewing the same active entry don't
  // double-write. Tracking auto-stops the moment status flips away from "active".
  const myActiveEntry = user && FIELD_ROLES.includes(user.role)
    ? entries.find(e => e.userId === user.id && e.status === "active")
    : null;
  useLiveTracking(db, myActiveEntry?.id, !!myActiveEntry && !!user?.gpsConsent);

  // Client-side 45-day retention. Runs once per manager session, in the background.
  // Stays well under Spark free-tier limits because MAX_DELETES_PER_RUN caps work.
  useEffect(() => {
    if (!user || !isManagerRole(user.role)) return;
    if (!entries.length) return;
    let cancelled = false;
    const t = setTimeout(() => { if (!cancelled) cleanupOldTracks(db, entries); }, 4000);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.role]);

  // Re-authenticate on page reload + load all users and entries from Firestore
  useEffect(() => {
    const loadData = async () => {
      try {
        const usersSnap = await getDocs(collection(db, "users"));
        if (!usersSnap.empty) {
          setAllUsers(usersSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        }
      } catch (e) { console.log("Could not load users:", e); }

      try {
        const entriesSnap = await getDocs(query(collection(db, "timeEntries"), orderBy("clockIn", "desc")));
        if (!entriesSnap.empty) {
          const loaded = entriesSnap.docs.map(d => {
            const data = d.data();
            return {
              id: d.id,
              ...data,
              clockIn: data.clockIn?.toDate ? data.clockIn.toDate() : new Date(data.clockIn),
              clockOut: data.clockOut?.toDate ? data.clockOut.toDate() : (data.clockOut ? new Date(data.clockOut) : null),
            };
          });
          setEntries(loaded);
        }
      } catch (e) { console.log("Could not load entries:", e); }

    };

    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const q = query(collection(db, "users"), where("email", "==", firebaseUser.email));
          const snap = await getDocs(q);
          if (!snap.empty) {
            const profile = { id: snap.docs[0].id, ...snap.docs[0].data() };
            if (profile.active !== false) {
              setUser(profile);
              await loadData();
              setAuthLoading(false);
              return;
            }
          }
        } catch {}
        await signOut(auth);
      }
      setUser(null);
      setAuthLoading(false);
    });
    return () => unsub();
  }, []);

  const getLocation = () => new Promise((res) => {
    if (!navigator.geolocation) { res({ lat: 35.46756, lng: -97.51643, label: "GPS 35.46756, -97.51643" }); return; }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        const label = await googleGeocode(lat, lng);
        res({ lat, lng, label });
      },
      () => res({ lat: 35.46756, lng: -97.51643, label: "GPS 35.46756, -97.51643 (demo)" }),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  });

  const handleClock = async (u, activeEntry) => {
    setLocating(true);
    const loc = await getLocation();
    setLocating(false);
    if (activeEntry) {
      const hours = Math.round((Date.now() - new Date(activeEntry.clockIn)) / 360000) / 10;
      const updates = { clockOut: Timestamp.now(), locationOut: loc, status: "pending", hours };
      setEntries(prev => prev.map(e => e.id === activeEntry.id ? { ...e, ...updates, clockOut: new Date() } : e));
      try { await updateDoc(doc(db, "timeEntries", activeEntry.id), updates); }
      catch (e) { console.error("Clock out save failed:", e); }
    } else {
      const newEntry = {
        userId: u.id, userName: u.name,
        clockIn: Timestamp.now(), clockOut: null,
        locationIn: loc, locationOut: null,
        status: "active", hours: null, qbSynced: false
      };
      try {
        const docRef = await addDoc(collection(db, "timeEntries"), newEntry);
        const entryId = docRef.id;
        setEntries(prev => [{ ...newEntry, id: entryId, clockIn: new Date() }, ...prev]);
      } catch (e) {
        console.error("Clock in save failed:", e);
        setEntries(prev => [{ ...newEntry, id: "e" + Date.now(), clockIn: new Date() }, ...prev]);
      }
    }
  };

  const handleCreateManualEntry = async (newEntry, { inDate, outDate }) => {
    const docRef = await addDoc(collection(db, "timeEntries"), newEntry);
    // Convert Timestamps back to Date for local state (mirrors handleClock)
    setEntries(prev => [{ ...newEntry, id: docRef.id, clockIn: inDate, clockOut: outDate }, ...prev]);
  };

  const handleGrantConsent = async (u) => {
    const consentAt = Timestamp.now();
    try { await updateDoc(doc(db, "users", u.id), { gpsConsent: true, gpsConsentAt: consentAt }); }
    catch (e) { console.error("Consent save failed:", e); }
    setUser(prev => prev ? { ...prev, gpsConsent: true, gpsConsentAt: consentAt } : prev);
    setAllUsers(prev => prev.map(x => x.id === u.id ? { ...x, gpsConsent: true, gpsConsentAt: consentAt } : x));
  };

  const handleUpdateEntry = (updated) => setEntries(prev => prev.map(e => e.id === updated.id ? updated : e));

  const handleApprove = async (id) => {
    setEntries(prev => prev.map(e => e.id === id ? { ...e, status: "approved" } : e));
    try { await updateDoc(doc(db, "timeEntries", id), { status: "approved" }); }
    catch (e) { console.error("Approve save failed:", e); }
  };

  const handleReject = async (id) => {
    setEntries(prev => prev.map(e => e.id === id ? { ...e, status: "rejected" } : e));
    try { await updateDoc(doc(db, "timeEntries", id), { status: "rejected" }); }
    catch (e) { console.error("Reject save failed:", e); }
  };

  // Bulk-delete every timeEntry for a single userId. Returns the count
  // so the UI can toast the result.
  const handleClearEntriesForUser = async (userId) => {
    const entryDocs = entries.filter(e => e.userId === userId);
    await Promise.all(
      entryDocs.map(e => deleteDoc(doc(db, "timeEntries", e.id)).catch(err => console.error("Delete entry failed:", err)))
    );
    setEntries(prev => prev.filter(e => e.userId !== userId));
    return { entries: entryDocs.length };
  };

  // Load the GHL integration config once a user is signed in. Field leads need
  // it to push approved photo links; admins edit it on the Settings page.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "settings", "ghl"));
        if (!cancelled && snap.exists()) setGhlConfig({ enabled: false, token: "", locationId: "", fieldId: "", ...snap.data() });
      } catch { /* leave defaults */ }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveGhlSettings = async (cfg) => {
    setGhlConfig(cfg);
    try { await setDoc(doc(db, "settings", "ghl"), cfg); } catch (e) { alert("Couldn't save GHL settings — " + e.message); }
  };

  const handleSaveQBSettings = (cfg, updatedUsers) => {
    setQbConfig({ ...cfg, connected: !!(cfg.clientId && cfg.realmId && cfg.accessToken) });
    setAllUsers(prev => prev.map(u => { const updated = updatedUsers.find(uu => uu.id === u.id); return updated ? { ...u, qbEmployeeId: updated.qbEmployeeId } : u; }));
  };

  // Public before/after gallery — opened via ?share=<id>, no login required.
  const shareId = typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("share") : null;
  if (shareId) return <SharePage shareId={shareId} />;

  if (authLoading) return (
    <div style={{ minHeight: "100vh", background: "#F7F8FA", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 16, fontFamily: "'DM Sans', sans-serif" }}>
      <img src={MINT_LOGO} alt="Mint" style={{ width: 64, height: 64, borderRadius: "50%", animation: "pulse 1.5s infinite" }} />
      <div style={{ color: "#64748B", fontSize: 13 }}>Loading...</div>
      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>
    </div>
  );

  if (!user) return <LoginScreen onLogin={setUser} />;

  return (
    <>
      {showQBSettings && <QBOSettings allUsers={allUsers} qbConfig={qbConfig} onSave={handleSaveQBSettings} onClose={() => setShowQBSettings(false)} />}
      {showGhlSettings && <GHLSettings ghlConfig={ghlConfig} onSave={handleSaveGhlSettings} onClose={() => setShowGhlSettings(false)} />}
      {showReports && <ReportsView entries={entries} allUsers={allUsers} onClose={() => setShowReports(false)} />}
      {showEmployees && <EmployeeManagement allUsers={allUsers} entries={entries} onSave={(u) => setAllUsers(u)} onClearEntries={handleClearEntriesForUser} onClose={() => setShowEmployees(false)} />}
      {showAssignments && <AssignmentsView allUsers={allUsers} user={user} onClose={() => setShowAssignments(false)} />}
      {isManagerRole(user.role)
        ? <ManagerView user={user} entries={entries} allUsers={allUsers} ghlConfig={ghlConfig} onApprove={handleApprove} onReject={handleReject} onUpdateEntry={handleUpdateEntry} onViewTrail={setTrailEntry} onAddEntry={() => setShowAddEntry(true)} onLogout={async () => { await signOut(auth); setUser(null); }} qbConfig={qbConfig} onOpenQBSettings={() => setShowQBSettings(true)} onOpenGhlSettings={() => setShowGhlSettings(true)} onOpenReports={() => setShowReports(true)} onOpenEmployees={() => setShowEmployees(true)} onOpenAssignments={() => setShowAssignments(true)} onPreview={setPreviewUserId} />
        : <EmployeeView user={user} entries={entries} appointments={myAppointments} ghlConfig={ghlConfig} onClock={handleClock} onGrantConsent={handleGrantConsent} locating={locating} onLogout={async () => { await signOut(auth); setUser(null); }} onUpdateEntry={handleUpdateEntry} />
      }
      {previewUser && (
        <div style={{ position: "fixed", inset: 0, zIndex: 900, overflowY: "auto", background: "#FFFFFF" }}>
          <div style={{ position: "sticky", top: 0, zIndex: 30, background: "#0F172A", color: "#fff", padding: "9px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", fontFamily: "'DM Sans', sans-serif" }}>
            <span style={{ fontSize: 12.5, fontWeight: 700 }}>👁 Preview — what {previewUser.name} ({previewUser.role}) sees</span>
            <button onClick={() => setPreviewUserId(null)} style={{ background: "rgba(255,255,255,0.18)", border: "1px solid rgba(255,255,255,0.5)", color: "#fff", borderRadius: 8, padding: "5px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Exit preview</button>
          </div>
          <EmployeeView user={previewUser} entries={entries} appointments={previewAppointments} ghlConfig={ghlConfig} onClock={async () => {}} onGrantConsent={() => {}} locating={false} onLogout={() => setPreviewUserId(null)} onUpdateEntry={() => {}} />
        </div>
      )}
      {showAddEntry && (
        <AddEntryModal
          allUsers={allUsers}
          currentUser={user}
          onSave={handleCreateManualEntry}
          onClose={() => setShowAddEntry(false)}
        />
      )}
      {trailEntry && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, fontFamily: "'DM Sans', sans-serif" }}>
          <div style={{ background: "#fff", borderRadius: 20, padding: 20, maxWidth: 880, width: "100%", maxHeight: "90vh", overflow: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div>
                <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 20, fontWeight: 700, color: "#0F172A" }}>{trailEntry.userName}</div>
                <div style={{ color: "#64748B", fontSize: 13 }}>
                  {new Date(trailEntry.clockIn).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })}
                  {" · "}
                  {new Date(trailEntry.clockIn).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  {trailEntry.clockOut ? ` – ${new Date(trailEntry.clockOut).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : " · still clocked in"}
                </div>
              </div>
              <button onClick={() => setTrailEntry(null)} style={{ background: "none", border: "1px solid #E5E7EB", color: "#64748B", borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontSize: 12, fontFamily: "inherit" }}>✕</button>
            </div>
            <TrailMap db={db} entry={trailEntry} height={480} />
          </div>
        </div>
      )}
    </>
  );
}